/**
 * HYPERLOCAL — web loop entry.
 *
 * Tier-0 non-negotiables this file is responsible for:
 *   1. Map renders on localhost without a black screen.
 *   2. Hyderabad loads by default — URL hash cannot override.
 *   3. 60 verdict pins render on top of the basemap.
 *   4. Tap a pin OR tap anywhere near a seeded area -> verdict card.
 *   5. No regressions: if a basemap fails, we fall through, we don't die.
 *
 * Basemap cascade (simplified — no more vector-source mismatch risks):
 *   1. Carto Dark raster (a/b/c subdomains, no key, proven)
 *   2. OpenStreetMap standard raster (ultimate fallback; always up)
 *
 * Every step groups console output under `HYPERLOCAL:boot` so devtools shows
 * exactly where a failure happens.
 */

import maplibregl, { StyleSpecification } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
// Design tokens (docs/DESIGN_LANGUAGE.md) — loaded before any UI module
// mounts so every injected stylesheet can rely on the custom properties.
import "./styles/tokens.css";
// Display face — actually self-hosted (the era of declaring fonts that never
// load is over). Scoped use: headline/wordmark/area names/score numerals.
import "@fontsource/space-grotesk/700.css";
import { HYD_VERDICTS, nearestVerdict, type Verdict } from "./data/verdicts";
import {
  MEL_VERDICTS,
  MEL_CENTER,
  nearestVerdictMel,
} from "./data/verdicts-mel";
// New cities (BLR + SYD) added 2026-05-03. Same Verdict shape as
// HYD/MEL — minimal viable starter sets (~20 suburbs each) that the
// SPA boots into when ?city=blr or ?city=syd is on the URL.
import {
  BLR_VERDICTS,
  BLR_CENTER,
  nearestVerdictBlr,
} from "./data/verdicts-blr";
import {
  SYD_VERDICTS,
  SYD_CENTER,
  nearestVerdictSyd,
} from "./data/verdicts-syd";
// School-density signal — derived from existing SCHOOL_RANKS only. We
// stamp the bucketed level onto each verdict feature so a paint
// expression can render a subtle density halo without needing a
// runtime callback. Verdict colour and confidenceScore are NEVER
// touched by this — see src/data/school-density.ts header.
import { getSchoolDensity } from "./data/school-density";
// Safety signal — categorical (safe / moderate / risky) derived from
// the existing incident feed. Init is fire-and-forget at boot for the
// active city; verdict card waits on `whenSafetyReady` before showing
// the safety bullet. See src/data/safety-signal.ts header.
import { initSafetySignal, whenSafetyReady } from "./data/safety-signal";
// Tradeoff signal — combines verdict + density + safety into a single
// "your assumption may not hold" indicator. Pure derivation, no new
// data, no scoring. See src/data/tradeoff.ts header.
import { tradeoffFeatureProps } from "./data/tradeoff";
import {
  showVerdict,
  showNoVerdict,
  dismissVerdict,
} from "./ui/verdict-card";
import { dismissCompareCard } from "./ui/compare-card";
import {
  getCompareState,
  enterWaitA,
  selectA as selectCompareA,
  selectB as selectCompareB,
  cancel as cancelCompare,
  subscribeCompare,
} from "./ui/compare-state";
import { setActiveDataset, getActiveDataset } from "./ui/active-dataset";
import { installAreaSnapshot } from "./ui/area-snapshot";
// School chart module (~50KB with school-facts) is loaded on first pill
// click — it never renders at boot, so it has no business in the boot chunk.
import type { SchoolCity } from "./data/school-ranks";
// SCHOOL_RANKS no longer consumed here — removed with the overlay fit path.
import { installSuburbSearch } from "./ui/suburb-search";
// School View map overlay removed — the toggle wasn't delivering enough
// visual value to justify its footprint. School rankings are still
// accessible via the School chart pill (which lives in the left rail
// only under Priority = School).
import {
  getMode as getLensMode,
  getModeLabel as getLensModeLabel,
  subscribe as subscribeLens,
  buildPersonaPillRow,
  resetToExploration,
} from "./ui/decision-lens";
import {
  getFullMapView,
  toggleFullMapView,
  subscribeFullMapView,
} from "./ui/full-map-view";
import {
  setBudgetCity,
  buildModeRow,
  buildTypeRow,
  getBudgetBand,
  getBudgetBandLabel,
  subscribeBudgetBand,
  getUserBudgetRange,
  subscribeUserBudgetRange,
} from "./ui/budget-band";
import { buildBudgetRangeBar } from "./ui/budget-range-bar";
import type { CityCode } from "./ui/budget-format";
import { getPurchaseMode, subscribePurchaseMode } from "./ui/purchase-mode";
// fact-coherence + suburb-intel-panel are loaded in the deferred phase
// (see installDeferredPhase inside the map.load handler).
import { priorityFit, selectTopMatches } from "./ui/priority-fit";
import {
  getPriority,
  subscribePriority,
  buildPriorityRow,
  getPriorityLabel,
} from "./ui/constraint-priority";
import {
  installShortlistPanel,
  subscribeShortlist,
  getShortlistIds,
} from "./ui/shortlist-panel";
// ---- Deferred-phase modules -----------------------------------------------
// Everything decorative or secondary (landmarks, events, incident bubbles,
// 3D texture passes, evaluator, layer toggles, …) is dynamically imported in
// installDeferredPhase() AFTER the map has painted and the loading screen is
// gone. This keeps ~500KB of module code out of the boot chunk — the single
// biggest cause of the old ~10s black screen. Type-only imports below are
// erased at compile time and pull no runtime code.
import type { installNearMePanel } from "./ui/near-me-panel";
import type { installSchoolsLayer } from "./ui/schools-layer";
import type { installIncidentBubbles } from "./ui/incident-bubbles";
import { installMapControls } from "./ui/map-controls";
import { installChromeShell } from "./ui/chrome-shell";

type C = [number, number];

const HYD: C = [78.4867, 17.385];
const INITIAL_ZOOM = 11.2;

// --- City router -----------------------------------------------------------
// Strict duplication — no multi-city abstraction, no registry, no runtime
// switch. We read ?city= once at boot and pick one of two static configs.

type CityId = "hyd" | "mel" | "blr" | "syd";

interface CityConfig {
  id: CityId;
  label: string;
  center: C;
  zoom: number;
  verdicts: Verdict[];
  nearFn: (lng: number, lat: number, maxKm?: number) => Verdict | null;
}

const CITIES: Record<CityId, CityConfig> = {
  hyd: {
    id: "hyd",
    label: "Hyderabad",
    center: HYD,
    zoom: INITIAL_ZOOM,
    verdicts: HYD_VERDICTS,
    nearFn: nearestVerdict,
  },
  mel: {
    id: "mel",
    label: "Melbourne",
    center: MEL_CENTER,
    zoom: 11.2,
    verdicts: MEL_VERDICTS,
    nearFn: nearestVerdictMel,
  },
  blr: {
    id: "blr",
    label: "Bengaluru",
    center: BLR_CENTER,
    zoom: 11.2,
    verdicts: BLR_VERDICTS,
    nearFn: nearestVerdictBlr,
  },
  syd: {
    id: "syd",
    label: "Sydney",
    center: SYD_CENTER,
    zoom: 11.2,
    verdicts: SYD_VERDICTS,
    nearFn: nearestVerdictSyd,
  },
};

/**
 * Suggested starter areas — a small, hand-picked list per city used ONLY by
 * the first-load attention-guidance halo. Not a filter, not a search index,
 * not part of compare or verdict logic. Pure visual guidance to help a new
 * user pick a first tap when the map shows 100+ pins.
 *
 * Picks are recognisable, geographically spread, and span verdict levels so
 * the halo doesn't double as a "best places" claim.
 */
const SUGGESTED_IDS: Record<CityId, string[]> = {
  hyd: [
    "gachibowli",
    "banjara-hills",
    "jubilee-hills",
    "madhapur",
    "kondapur",
  ],
  mel: [
    "mel-cbd",
    "mel-carlton",
    "mel-richmond",
    "mel-st-kilda",
    "mel-brighton",
  ],
  blr: [
    "blr-indiranagar",
    "blr-koramangala",
    "blr-whitefield",
    "blr-jayanagar",
    "blr-hsr-layout",
  ],
  syd: [
    "syd-cbd",
    "syd-bondi",
    "syd-surry-hills",
    "syd-manly",
    "syd-chatswood",
  ],
};

/** Shown once per browser session — cleared after first tap or after 12 s. */
const SUGGESTED_FLAG = "hyperlocal.suggestedSeen";

function readCityParam(): CityId {
  try {
    const raw = new URLSearchParams(window.location.search)
      .get("city")
      ?.toLowerCase();
    if (raw === "mel" || raw === "melbourne") return "mel";
    if (raw === "hyd" || raw === "hyderabad") return "hyd";
    if (raw === "blr" || raw === "bengaluru" || raw === "bangalore") return "blr";
    if (raw === "syd" || raw === "sydney") return "syd";
  } catch {
    // fall through
  }
  // Sydney is the pilot the whole strategy bets on (locked 2026-06-07:
  // "Sydney = existing pilot, deploy + soak now") — a bare /app/ visit
  // lands on the city where the truth engine is live, not a fallback.
  return "syd";
}

/**
 * Read `?focus=<id>` (alias: `?suburb=<id>`) from the URL. Returns the
 * verdict matching the id, else null. Accepts both bare ids ("balwyn",
 * "gachibowli") and the city-prefixed forms ("mel-balwyn",
 * "hyd-gachibowli") — the SEO landing pages link with bare ids, but the
 * underlying verdict.id may carry the prefix (Melbourne does, Hyderabad
 * doesn't).
 *
 * Used at app boot to support deep-links from the SEO suburb pages
 * ("Open Balwyn on the map →" → /app/?city=mel&focus=balwyn). `?suburb=`
 * is the documented public form (/app/?city=…&suburb=…) — both work so
 * existing links never break.
 */
function readFocusParam(verdicts: readonly Verdict[]): Verdict | null {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = (params.get("focus") ?? params.get("suburb"))?.toLowerCase();
    if (!raw) return null;
    // Direct match first.
    for (const v of verdicts) if (v.id === raw) return v;
    // Try with city prefix — covers all 4 cities. The bare landing-page
    // URLs (Open Bondi on map →) carry the unprefixed slug; verdict ids
    // may or may not carry the city prefix depending on city.
    for (const v of verdicts) {
      if (v.id === `mel-${raw}` || v.id === `hyd-${raw}` || v.id === `blr-${raw}` || v.id === `syd-${raw}`) return v;
    }
    // Try without the prefix (in case the link carried it).
    const stripped = raw.replace(/^(mel|hyd|blr|syd)-/, "");
    for (const v of verdicts) if (v.id === stripped) return v;
    return null;
  } catch {
    return null;
  }
}

// Primary (2026-04-26 Apple-Maps-DARK simulator pass — second iteration):
//   Carto Dark Matter VECTOR. Same OpenMapTiles schema as Voyager (so the
//   `building` source-layer with `render_height` still feeds our 3D
//   fill-extrusion), but a dark #0e0e0e background that matches the rest
//   of the product chrome (verdict pins, top-picks panel, suburb dots
//   were all designed for a dark canvas). The cream-paper Voyager look
//   was tested in user-feedback round 2026-04-26 and rejected because
//   the existing dark UI elements floated awkwardly on a light basemap.
//   Free, no API key. Vector tiles served from carto.streets/v1.
const CARTO_DARK_VECTOR_URL =
  "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";
// Secondary fallback: Carto Dark Matter raster. Existing implementation.
const CARTO_DARK_TILES = [
  "https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
  "https://b.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
  "https://c.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png",
];
// Ultimate fallback: OSM standard. Plain but always up.
const OSM_TILES = [
  "https://a.tile.openstreetmap.org/{z}/{x}/{y}.png",
  "https://b.tile.openstreetmap.org/{z}/{x}/{y}.png",
  "https://c.tile.openstreetmap.org/{z}/{x}/{y}.png",
];

// ---- Probe ----------------------------------------------------------------

async function tileOk(tileUrl: string, timeoutMs = 3500): Promise<boolean> {
  // Probe a real tile (z/x/y) instead of the template root — some CDNs
  // reject HEAD on the template but serve tiles fine. We do a GET with
  // `no-cors` so an opaque success still clears the probe.
  const probed = tileUrl
    .replace("{z}", "5")
    .replace("{x}", "22")
    .replace("{y}", "13");
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const r = await fetch(probed, {
      method: "GET",
      mode: "no-cors",
      signal: ctrl.signal,
      cache: "no-store",
    });
    clearTimeout(t);
    // Opaque responses in no-cors mode return status 0 but are "successful".
    return r.type === "opaque" || r.ok;
  } catch {
    clearTimeout(t);
    return false;
  }
}

// ---- Styles ---------------------------------------------------------------

function createRasterStyle(
  id: string,
  tiles: string[],
  attribution: string
): StyleSpecification {
  return {
    version: 8,
    // The verdict-label layer renders `text-field`, which MapLibre requires
    // a `glyphs` URL for even when the basemap itself is raster-only. Using
    // the public Carto Maki font pack keeps text legible without pulling in
    // a new font asset or a whole vector basemap.
    glyphs: "https://fonts.openmaptiles.org/{fontstack}/{range}.pbf",
    sources: {
      [id]: {
        type: "raster",
        tiles,
        tileSize: 256,
        attribution,
      },
    },
    layers: [
      { id: "bg", type: "background", paint: { "background-color": "#0F0F0F" } },
      { id: `${id}-base`, type: "raster", source: id, paint: { "raster-opacity": 1 } },
    ],
  } as StyleSpecification;
}

/**
 * Apple-Maps DARK "simulator" style v2 (2026-04-26 second iteration).
 *
 * Loads Carto Dark Matter's public GL style (near-black background +
 * desaturated streets, OpenMapTiles schema — same `building` source-layer
 * with `render_height` that the cream Voyager variant used, so 3D
 * extrusions still work) and rewrites a handful of layers to push it
 * closer to Apple Maps' DARK mode aesthetic:
 *
 *   • Background pushed to a true #0a0a0a so the verdict pins, top-picks
 *     panel, suburb dots and FAB (all designed for a dark canvas) read
 *     with proper contrast. Dark Matter ships with #0e0e0e which was
 *     close but slightly washed out.
 *   • Parks / landcover desaturated to deep mossy greens so green spaces
 *     are still distinguishable from the street grid without glowing.
 *   • Water sunk to a navy-graphite #0d1820 — Apple's dark-mode water is
 *     noticeably bluer than the surrounding chrome.
 *   • Road casings preserved (Dark Matter ships with light-grey roads on
 *     near-black, the right hierarchy for our use).
 *   • A `building-3d` fill-extrusion layer is appended at runtime in
 *     `attach3DBuildings(map)` once the style finishes loading; doing it
 *     here would race with the network fetch.
 *
 * Returns a style URL string (not a Style object) — MapLibre fetches it
 * itself, which is the cheapest path: if Carto serves us slightly different
 * JSON tomorrow, our code doesn't break, only the runtime overrides need
 * to handle missing layer ids gracefully (which they do — see
 * `applyAppleMapsTint`).
 */
function createBasemapStyleURL(): string {
  return CARTO_DARK_VECTOR_URL;
}

/**
 * Apply Apple-Maps DARK-mode tint on top of Dark Matter's loaded style.
 *
 * Runs once after `map.on('load')`. Uses `setPaintProperty` so we don't
 * have to fork the entire style file. Every override is wrapped in a
 * try/catch — if Carto renames a layer, we degrade to "still looks like
 * stock Dark Matter" rather than throwing and breaking the whole map.
 */
function applyAppleMapsTint(map: maplibregl.Map): void {
  const safeSet = (
    layerId: string,
    prop: string,
    value: unknown
  ): void => {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (map.getLayer(layerId)) (map as any).setPaintProperty(layerId, prop, value);
    } catch {
      /* layer renamed upstream — degrade gracefully */
    }
  };
  // Near-black canvas — Apple Maps dark mode is true black, not slate.
  safeSet("background", "background-color", "#0a0a0a");
  // Deep mossy greens — visible against #0a0a0a but not eye-grabbing.
  // Carto Dark Matter ships a single "landcover" layer that already covers
  // wood + grass + recreation_ground. Setting the *_grass / *_wood ids is a
  // no-op here (they don't exist in this style) but kept for forward-compat
  // with future Carto versions that might split them. The real work happens
  // on `landcover` and the two park layers below.
  safeSet("landcover_grass", "fill-color", "#1a2316");
  safeSet("landcover_wood", "fill-color", "#16201f");
  safeSet("park_fill", "fill-color", "#1c2a18");
  // Bump the actual layers Carto Dark Matter renders. These were #0e0e0e
  // (canvas) so parks were invisible. Mossy greens at the same lightness as
  // canvas-+-tint are readable but never garish.
  safeSet("landcover", "fill-color", "#162117");
  safeSet("park_national_park", "fill-color", "#1d2e1c");
  safeSet("park_nature_reserve", "fill-color", "#1a2818");

  // ----- Apple-Maps-style depth-tiered water -------------------------------
  // OpenMapTiles `water` source-layer carries a `class` attribute even though
  // the stock Carto Dark Matter style flattens every polygon to a single
  // `#2C353C` slate. Apple Maps reads water depth visually by giving lakes a
  // slightly brighter, bluer fill than ocean (so a freshwater lake doesn't
  // disappear into the surrounding sea on a coast view), and rivers a
  // muted middle. We replicate that hierarchy with a `match` expression on
  // class so a single layer paints every water polygon at the right depth:
  //
  //   ocean / sea  → deep navy-graphite (the "outside the city" water)
  //   bay / strait → just-shy of ocean (mild differentiation on coastlines)
  //   lake / pond  → richer cyan-navy   (Apple's freshwater "pop")
  //   reservoir    → richer cyan-navy   (man-made lake — Hussain Sagar feels
  //                                       like a lake to a Hyderabad buyer)
  //   river        → muted lake (slightly less blue, more graphite)
  //   swimming_pool→ brightest pool blue (rare, usually only at z>=17)
  //   dock         → harbour grey (functional, not picturesque)
  //   default      → existing navy-graphite (unchanged behaviour for any
  //                                          class we didn't enumerate)
  //
  // Why a `match` instead of a stack of filtered fill layers: one layer is
  // cheaper to paint (single GL draw call) and keeps the layer list clean.
  // The trade-off is that we can't apply different opacities per class — but
  // Apple's lake/ocean differentiation is purely a fill-color shift, so this
  // expression is the right tool.
  safeSet("water", "fill-color", [
    "match",
    ["coalesce", ["get", "class"], "default"],
    ["ocean", "sea"], "#0a1622",
    ["bay", "strait"], "#0c1a28",
    ["lake", "pond", "reservoir"], "#1a3a52",
    ["river", "stream", "canal"], "#163349",
    ["swimming_pool"], "#2a5878",
    ["dock"], "#0e1e2c",
    /* default (intermittent ponds, swamp, generic) */ "#0d1820",
  ] as unknown as string);
  // The Carto-shipped `water_shadow` is a translated copy that fakes a soft
  // drop-shadow under coastlines. It's currently `transparent` so it does
  // nothing on dark-mode. Tint it to a slightly warmer near-black so the
  // shadow reads as a faint halo ON LAND just outside lakes/oceans —
  // that's the Apple-Maps "land-meets-water" subtle outer glow.
  safeSet("water_shadow", "fill-color", "rgba(36, 56, 76, 0.35)");
  safeSet("water_intermittent", "fill-color", "#142028");
  // Waterways (rivers, streams, canals) — Carto ships `rgba(63,90,109,1)`
  // which reads as dull blue-grey. Bump to the same hue as our river polygon
  // fill so a riverline visually continues into a river polygon at z-out.
  safeSet("waterway", "line-color", "#3a6480");
  // Slightly thicker stroke at high zoom so the Yarra / Musi reads as a
  // proper waterway rather than a hairline.
  safeSet("waterway", "line-width", {
    stops: [[8, 0.6], [11, 1.2], [13, 1.8], [15, 2.6], [17, 4.0]],
  } as unknown as number);

  // ---- SimCity-vibe freeways & trunk roads --------------------------------
  // Per 2026-04-26 user feedback: "colour the Free ways and Outer ring road
  // in a subtle way also in sim city vibe replicating the actual road."
  //
  // OpenMapTiles transportation classes: `motorway` ≈ freeways /expressways
  // (Hyderabad's Outer Ring Road is tagged motorway), `trunk` ≈ major arterials.
  // Carto Dark Matter renders both in the same desaturated grey as residential
  // streets, which makes the road hierarchy invisible against #0a0a0a.
  //
  // We dial them up to a warm amber that reads as "the highway you take to get
  // somewhere" without being garish. Sun-warmed asphalt — SimCity's freeways
  // have this exact desaturated-orange treatment. Casing (outer stroke) is
  // darker brown so the highway has a visible edge against the canvas; fill
  // (inner ribbon) is the brighter amber.
  //
  // Done last (after the parks/water re-tint) so the road colours are a clear
  // top-of-stack override; if Carto reorders layers tomorrow nothing breaks
  // because each safeSet is independent and try/caught.
  const ROAD_MOTORWAY_CASE = "#3a2a14"; // deep warm brown
  const ROAD_MOTORWAY_FILL = "#a37236"; // SimCity warm amber
  const ROAD_TRUNK_CASE = "#2e2210";    // slightly darker brown
  const ROAD_TRUNK_FILL = "#866028";    // slightly darker amber
  // Surface roads (no bridge/tunnel) — the bulk of the visible motorway/trunk.
  safeSet("road_mot_case_noramp", "line-color", ROAD_MOTORWAY_CASE);
  safeSet("road_mot_fill_noramp", "line-color", ROAD_MOTORWAY_FILL);
  safeSet("road_trunk_case_noramp", "line-color", ROAD_TRUNK_CASE);
  safeSet("road_trunk_fill_noramp", "line-color", ROAD_TRUNK_FILL);
  // Ramps (on/off-ramps) — same palette so they connect visually.
  safeSet("road_mot_case_ramp", "line-color", ROAD_MOTORWAY_CASE);
  safeSet("road_mot_fill_ramp", "line-color", ROAD_MOTORWAY_FILL);
  safeSet("road_trunk_case_ramp", "line-color", ROAD_TRUNK_CASE);
  safeSet("road_trunk_fill_ramp", "line-color", ROAD_TRUNK_FILL);
  // Bridges & tunnels — kept on the same warm tone so the highway reads as
  // continuous when it crosses water (Hussain Sagar) or dives underground.
  safeSet("bridge_mot_case", "line-color", ROAD_MOTORWAY_CASE);
  safeSet("bridge_mot_fill", "line-color", ROAD_MOTORWAY_FILL);
  safeSet("bridge_trunk_case", "line-color", ROAD_TRUNK_CASE);
  safeSet("bridge_trunk_fill", "line-color", ROAD_TRUNK_FILL);
  safeSet("tunnel_mot_case", "line-color", ROAD_MOTORWAY_CASE);
  safeSet("tunnel_mot_fill", "line-color", ROAD_MOTORWAY_FILL);
  safeSet("tunnel_trunk_case", "line-color", ROAD_TRUNK_CASE);
  safeSet("tunnel_trunk_fill", "line-color", ROAD_TRUNK_FILL);
}

/**
 * Apple-Maps + SimCity layered refinement for water, parks, and landuse.
 *
 * Four visual passes that read together as "this is a real city, not a
 * monochrome graphic":
 *
 *   1. SimCity-style landuse zoning.
 *      SimCity colour-codes residential / commercial / industrial / etc.
 *      so the player can read the city at a glance. Carto Dark Matter
 *      paints all landuse pitch-black except a half-transparent "darker"
 *      tint on residential. We add ONE extra fill layer per zone class
 *      (a single layer with a `match` expression on `class`), tinting
 *      each zone with a near-canvas hue — warm yellow-brown for homes,
 *      cool blue for commercial, ochre for industrial, magenta for
 *      retail, green for schools/universities, red for hospitals, drab
 *      for military. They're INTENTIONALLY low-contrast (~6% lightness
 *      delta from canvas) so the map still reads as monochrome until you
 *      zoom in past 12 — at which point a real-estate buyer can SEE
 *      "this whole strip is industrial" the same way a SimCity player
 *      sees zoning.
 *
 *   2. Apple + SimCity layered LAKES / RIVER POLYGONS.
 *      Each freshwater BODY (anything wide enough to come through as a
 *      polygon) gets a 4-layer treatment:
 *        a. Apple inner cyan glow (blurred, low-opacity, inset)        — "sunlit water"
 *        b. SimCity inner shallows band (crisp, lighter blue)          — "the bright shore"
 *        c. Apple outer warm halo (blurred, just outside the polygon)  — "ambient bounce"
 *        d. SimCity outer shoreline casing (crisp, dark teal, on top)  — "the defining edge"
 *      Hierarchy via `match` on class: reservoirs/lakes get the boldest
 *      treatment, ponds the subtlest, rivers the medium-flowing band.
 *      Mirrors the road `case`+`fill` palette structure (motorway > trunk).
 *
 *   3. Apple + SimCity layered PARKS / FORESTS.
 *      Same 4-layer treatment as water but in green-space:
 *        a. Apple inner grass-green glow (blurred, sun-on-leaves)       — "sunlit lawn"
 *        b. SimCity inner meadow band (crisp, vivid green)              — "mown perimeter path"
 *        c. Apple outer warm halo                                       — "where grass meets path"
 *        d. SimCity outer dark-forest casing                            — "park boundary"
 *      Hierarchy: national_park / nature_reserve get the boldest, urban
 *      parks medium, generic landcover (forest/grass) just the casing
 *      pass to avoid visual noise from many small patches.
 *
 *   4. Apple + SimCity layered RIVERS / STREAMS / CANALS (linestrings).
 *      The lake pass (above) only catches water that's wide enough to
 *      come through as a polygon. Narrow watercourses — urban creeks,
 *      the upper Yarra, the Musi at low flow, drainage canals — come
 *      through the SEPARATE `waterway` source-layer as LINESTRINGS, so
 *      they need their own treatment. We mirror the road `case`+`fill`
 *      pattern directly:
 *        a. Apple atmospheric glow  (wide, blurred, low-alpha blue)     — "in the landscape"
 *        b. SimCity casing          (wider darker outer line)           — "the watercourse edge"
 *        c. SimCity fill            (narrower brighter inner line)      — "the water surface"
 *      Hierarchy: river > canal > stream > drain/ditch. Colours match
 *      the polygon water shallows palette so a river-line meeting a
 *      river-polygon at a width transition reads as ONE continuous
 *      waterway. The Carto-shipped flat `waterway` layer is hidden
 *      (opacity 0) so this stack is what you see.
 *
 * All passes fail silently if the underlying source-layer doesn't ship
 * (raster fallback case) — we just skip and the map continues to work
 * with the base tints.
 */
function attachWaterAndLanduseRefinement(map: maplibregl.Map): void {
  try {
    // Find an anchor: insert these refinement layers BEFORE the first symbol
    // (label) layer so labels stay legible on top. If there are no symbol
    // layers (raster fallback), we skip entirely.
    const layers = map.getStyle().layers ?? [];
    let firstSymbolId: string | undefined;
    for (const l of layers) {
      if (l.type === "symbol") { firstSymbolId = l.id; break; }
    }
    if (!map.getSource("carto")) return;

    // ---- 1. SimCity zoning tint -----------------------------------------
    // Insert BEFORE the `water` layer so water polygons cut cleanly through
    // landuse, not on top of it. (Otherwise a lake on residential land would
    // get a residential tint laid over the water — wrong.)
    if (!map.getLayer("hyperlocal-landuse-zoning") && map.getLayer("water")) {
      map.addLayer(
        {
          id: "hyperlocal-landuse-zoning",
          type: "fill",
          source: "carto",
          "source-layer": "landuse",
          minzoom: 11,
          paint: {
            // Low-saturation hues at near-canvas lightness. Buyer-friendly:
            // a glance tells them "this neighbourhood is mostly residential"
            // (warm) vs "industrial" (ochre) vs "retail strip" (magenta).
            "fill-color": [
              "match",
              ["coalesce", ["get", "class"], "default"],
              ["residential"], "#1c1a14",      // warm lived-in
              ["commercial"], "#101822",        // commercial blue
              ["industrial"], "#1d180e",        // industrial ochre
              ["retail"], "#1a1015",            // retail magenta
              ["school", "university", "college", "kindergarten"], "#141a14", // education green
              ["hospital"], "#1c1414",          // medical red
              ["military"], "#181a14",          // drab military
              ["theme_park", "zoo", "playground"], "#1a1c14", // recreation
              /* default */ "rgba(0,0,0,0)",
            ],
            // Fade in over zoom: invisible until 11.5, full at 13.5.
            // Below 11 the city is already too small for zoning to help.
            "fill-opacity": [
              "interpolate", ["linear"], ["zoom"],
              11, 0,
              11.5, 0.55,
              14, 0.85,
              16, 1.0,
            ],
            "fill-antialias": true,
          },
        },
        "water"
      );
    }

    // ---- 2. Apple-Maps lake shoreline glow ------------------------------
    // A soft inner halo on the LAND-FACING edge of lake/pond/reservoir/river
    // polygons. We use line-offset to push the line just inside the polygon
    // (so it reads as the water's own glow, not a black outline on land),
    // and line-blur to make it feel optical rather than cartographic.
    if (!map.getLayer("hyperlocal-lake-shoreline-glow") && map.getLayer("water")) {
      map.addLayer(
        {
          id: "hyperlocal-lake-shoreline-glow",
          type: "line",
          source: "carto",
          "source-layer": "water",
          minzoom: 9,
          // Only freshwater bodies — ocean shorelines are coastlines (huge,
          // would over-glow the whole map) and Carto has its own coastline
          // treatment. Lakes/rivers/reservoirs are the ones Apple Maps glows.
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["match",
              ["coalesce", ["get", "class"], "default"],
              ["lake", "pond", "reservoir", "river"], true,
              false,
            ],
          ],
          paint: {
            "line-color": "#5a9fc8",         // pale cyan-blue glow
            "line-blur": 1.5,                // soft optical edge
            // Width grows with zoom — at z<10 the glow is sub-pixel anyway.
            "line-width": [
              "interpolate", ["linear"], ["zoom"],
              9, 0.4,
              12, 0.9,
              14, 1.4,
              16, 2.0,
              18, 2.6,
            ],
            // Inset the glow slightly so it lands ON the water, not on land.
            // Negative offset means inward (toward the polygon interior).
            "line-offset": -0.6,
            "line-opacity": [
              "interpolate", ["linear"], ["zoom"],
              9, 0.0,
              10, 0.35,
              13, 0.55,
              15, 0.55,
              18, 0.45,
            ],
          },
        },
        firstSymbolId
      );
    }

    // ---- 2b. Lake outer rim — a faint warm halo just outside the water --
    // Apple's secondary trick: the shore HAS a slight warmth where the water
    // meets the path/grass. We mirror this with a second, wider, warmer line
    // OUTSIDE the polygon (positive offset). Subtle — about a quarter the
    // intensity of the inner glow — so the eye reads it as ambient bounce
    // light rather than a drawn outline.
    if (!map.getLayer("hyperlocal-lake-shoreline-rim") && map.getLayer("water")) {
      map.addLayer(
        {
          id: "hyperlocal-lake-shoreline-rim",
          type: "line",
          source: "carto",
          "source-layer": "water",
          minzoom: 11,
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["match",
              ["coalesce", ["get", "class"], "default"],
              ["lake", "pond", "reservoir", "river"], true,
              false,
            ],
          ],
          paint: {
            "line-color": "#3c4a3a",         // muted warm grey-green
            "line-blur": 2.2,
            "line-width": [
              "interpolate", ["linear"], ["zoom"],
              11, 0.6,
              13, 1.4,
              16, 2.6,
            ],
            "line-offset": 1.2,              // outside the polygon
            "line-opacity": 0.18,
          },
        },
        firstSymbolId
      );
    }

    // ---- 3. SimCity-vibe lake hierarchy (parallel to road treatment) ----
    // Per user 2026-04-26: "do similar work on lakes like you have done for
    // roads in simcity inspiration."
    //
    // The road SimCity treatment uses a strict CASE (outer dark border) +
    // FILL (inner brighter ribbon) pattern, hierarchically scaled per class
    // (motorway brighter than trunk). We mirror that exact structure for
    // water bodies — a clearly defined SHORELINE CASING + a SHALLOWS BAND
    // just inside, with classes ranked:
    //
    //   reservoir/lake (major)  → boldest treatment   (Hussain Sagar, Albert Park)
    //   pond           (minor)  → subtler             (urban duck-ponds)
    //   river                   → flowing band        (Yarra, Musi)
    //
    // The Apple-Maps soft glow + warm halo (layers 2 + 2b above) stays in
    // place — it's the painterly atmospheric pass. THIS pass is the
    // structural pass that gives the eye a hierarchy at a glance, exactly
    // like SimCity teaches the player to read freeway > arterial > street
    // by colour-weight alone.
    //
    // Layer order (back→front):
    //   water fill (Carto)
    //     → hyperlocal-lake-shoreline-glow (Apple inner cyan, blurred)
    //     → hyperlocal-lake-shallows-band  (NEW — SimCity inner lighter ring)
    //     → hyperlocal-lake-shoreline-rim  (Apple outer warm halo)
    //     → hyperlocal-lake-shoreline-case (NEW — SimCity crisp outer rim)
    //   labels (symbols)
    //
    // The "case" goes ON TOP because, like roads, it's the defining edge —
    // anything beneath should not bleed past the casing.

    // The shallows band — a lighter inner ring just inside the polygon edge.
    // Negative offset puts it ON the water (inward). NO blur — this is the
    // structural pass; we want a defined band, not an optical halo.
    // Width scales with class so reservoirs get a fatter shallows than
    // ponds, just like motorways get a fatter ribbon than trunks.
    if (!map.getLayer("hyperlocal-lake-shallows-band") && map.getLayer("water")) {
      map.addLayer(
        {
          id: "hyperlocal-lake-shallows-band",
          type: "line",
          source: "carto",
          "source-layer": "water",
          minzoom: 10,
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["match",
              ["coalesce", ["get", "class"], "default"],
              ["lake", "pond", "reservoir", "river"], true,
              false,
            ],
          ],
          paint: {
            // Lighter SimCity-blue ring per class — the "shallows" colour.
            // Reservoirs/lakes get the brightest (most prominent), rivers
            // a slightly cooler tone, ponds a touch muted.
            "line-color": [
              "match",
              ["coalesce", ["get", "class"], "default"],
              ["reservoir", "lake"], "#3d7fa5",  // major — bright SimCity shallows
              ["pond"], "#346d8e",                // minor — softer
              ["river"], "#3878a0",               // flowing — between the two
              "#356e90",                          // default fallback
            ] as unknown as string,
            // No blur — this is the SimCity structural band. Crisp on purpose.
            "line-blur": 0,
            // Width hierarchy per class — reservoirs get the thickest band
            // (parallel to motorway > trunk in road treatment).
            "line-width": [
              "interpolate", ["linear"], ["zoom"],
              10, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 0.6,
                ["pond"], 0.3,
                ["river"], 0.4,
                0.3,
              ],
              13, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 1.6,
                ["pond"], 0.8,
                ["river"], 1.1,
                0.9,
              ],
              16, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 3.0,
                ["pond"], 1.4,
                ["river"], 2.0,
                1.6,
              ],
              18, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 4.0,
                ["pond"], 1.8,
                ["river"], 2.6,
                2.0,
              ],
            ] as unknown as number,
            // Inset just enough that the band lives ON the water and reads
            // as the lake's own bright shore-edge, not as an outline drawn
            // on top. About 1.5x the Apple-glow inset so the layers stack
            // visibly: deep fill → cyan glow (-0.6) → shallows band (-2.0)
            // → calm warm rim outside (+1.2) → SimCity casing on top.
            "line-offset": -2.0,
            "line-opacity": [
              "interpolate", ["linear"], ["zoom"],
              10, 0.0,
              11, 0.45,
              13, 0.7,
              16, 0.78,
              18, 0.7,
            ],
          },
        },
        firstSymbolId
      );
    }

    // The shoreline casing — a crisp dark outer rim, the water-equivalent of
    // a road's `case` layer. Sits ON TOP of all the other water layers so
    // the lake has a clear, defined edge against landuse. This is what makes
    // the SimCity hierarchy snap into focus: a fat dark rim around major
    // lakes vs a thin one around ponds is the same visual trick SimCity uses
    // to differentiate freeway-vs-arterial-vs-street.
    // (Park layers added below — the following block is the lake casing.)
    if (!map.getLayer("hyperlocal-lake-shoreline-case") && map.getLayer("water")) {
      map.addLayer(
        {
          id: "hyperlocal-lake-shoreline-case",
          type: "line",
          source: "carto",
          "source-layer": "water",
          minzoom: 9,
          filter: [
            "all",
            ["==", ["geometry-type"], "Polygon"],
            ["match",
              ["coalesce", ["get", "class"], "default"],
              ["lake", "pond", "reservoir", "river"], true,
              false,
            ],
          ],
          paint: {
            // Deep teal casing per class — slightly different shades so a
            // reservoir reads heavier than a pond at the same zoom. Mirrors
            // the road CASE colour palette (`#3a2a14` motorway vs `#2e2210`
            // trunk) — same delta in blue-space.
            "line-color": [
              "match",
              ["coalesce", ["get", "class"], "default"],
              ["reservoir", "lake"], "#16384e",  // boldest (motorway-equivalent)
              ["pond"], "#142836",                // subtlest (street-equivalent)
              ["river"], "#152d3a",               // flowing-medium (trunk-equivalent)
              "#143246",
            ] as unknown as string,
            "line-blur": 0,
            // Width hierarchy — same scaling philosophy as the band but
            // a touch narrower so the casing reads as a defining EDGE,
            // not as a second band.
            "line-width": [
              "interpolate", ["linear"], ["zoom"],
              9, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 0.4,
                ["pond"], 0.2,
                ["river"], 0.3,
                0.25,
              ],
              13, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 1.1,
                ["pond"], 0.55,
                ["river"], 0.8,
                0.7,
              ],
              16, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 2.0,
                ["pond"], 0.9,
                ["river"], 1.3,
                1.1,
              ],
              18, [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["reservoir", "lake"], 2.6,
                ["pond"], 1.1,
                ["river"], 1.6,
                1.4,
              ],
            ] as unknown as number,
            // Sit ON the polygon edge (offset 0). This is the defining
            // line — neither inside nor outside. Same convention roads
            // use for their case layer.
            "line-offset": 0,
            // Slightly translucent so the underlying glow + band still
            // contribute. Full opacity would over-define the edge and
            // kill the soft Apple atmospheric pass beneath.
            "line-opacity": [
              "interpolate", ["linear"], ["zoom"],
              9, 0.55,
              12, 0.78,
              15, 0.92,
              18, 1.0,
            ],
          },
        },
        firstSymbolId
      );
    }

    // ---- 4. Apple-Maps + SimCity parks (mirror of the lake treatment) ---
    // Per user 2026-04-26: "How do we get that the SimCity and apple map
    // inspired colour shade and design on Parks and lakes and rivers."
    //
    // Lakes/rivers got a 4-layer stack (Apple inner glow, Apple outer rim,
    // SimCity inner band, SimCity outer casing). Parks deserve the same
    // treatment so the city reads as a layered ecosystem — water and green
    // space are the two big atmospheric players. Without it, parks are
    // flat fills sitting on the canvas with no visual weight; the eye
    // skips past them. With the layered pass, you can SEE Royal Park or
    // KBR National Park as protected territory at a glance.
    //
    // Source-layer:
    //   OpenMapTiles `park` carries the protected-areas (national parks,
    //   nature reserves, regular parks). `landcover` carries wood / grass
    //   / forest. We treat them together — both are "green space" — but
    //   the park source-layer gets the boldest hierarchy because that's
    //   what people search for ("near a park").
    //
    // Hierarchy (parallel to motorway > trunk > street and reservoir > pond):
    //   national_park / nature_reserve  → boldest    (KBR, Royal Park)
    //   protected_area                   → bold      (Botanic Gardens)
    //   default park (no class)          → medium    (suburb pocket parks)
    //
    // Colours:
    //   Apple inner glow:  #5fa860  pale grass green (sun-on-leaves)
    //   Apple outer rim:   #2a3a26  warm grey-green (path-meets-grass)
    //   SimCity meadow band (lighter inner ring): tiered greens — vivid
    //   SimCity edge casing (crisp outer rim):    tiered dark forest greens
    //
    // Layer order (back→front): park fill → glow → meadow-band → rim → case → labels.

    // 4a. Apple inner soft glow — the sunlit-leaf halo on the park's inner edge.
    // Mirror of hyperlocal-lake-shoreline-glow. Inset, blurred, low-opacity green.
    if (!map.getLayer("hyperlocal-park-glow")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-park-glow",
            type: "line",
            source: "carto",
            "source-layer": "park",
            minzoom: 9,
            filter: ["==", ["geometry-type"], "Polygon"],
            paint: {
              "line-color": "#5fa860",        // pale warm grass green
              "line-blur": 1.5,
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                9, 0.4,
                12, 0.9,
                14, 1.4,
                16, 2.0,
                18, 2.6,
              ],
              "line-offset": -0.6,             // inward — the glow lives on the grass
              "line-opacity": [
                "interpolate", ["linear"], ["zoom"],
                9, 0.0,
                10, 0.30,
                13, 0.50,
                15, 0.50,
                18, 0.40,
              ],
            },
          },
          firstSymbolId
        );
      } catch {
        /* park source-layer absent on this style — skip */
      }
    }

    // 4b. SimCity meadow band — a lighter green inner ring (the "mown perimeter
    // path" effect SimCity parks have). Mirror of hyperlocal-lake-shallows-band.
    // Crisp (no blur), hierarchically wider for protected areas.
    if (!map.getLayer("hyperlocal-park-meadow-band")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-park-meadow-band",
            type: "line",
            source: "carto",
            "source-layer": "park",
            minzoom: 10,
            filter: ["==", ["geometry-type"], "Polygon"],
            paint: {
              // Tiered SimCity park-greens — national parks brightest, urban
              // parks medium. Same delta-in-green-space as the lake shallows
              // tier in delta-in-blue-space.
              "line-color": [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["national_park", "nature_reserve"], "#52a046",  // boldest meadow
                ["protected_area"], "#3f8a3a",                    // bold
                "#3a7d36",                                        // default park
              ] as unknown as string,
              "line-blur": 0,
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                10, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 0.6,
                  ["protected_area"], 0.4,
                  0.3,
                ],
                13, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 1.6,
                  ["protected_area"], 1.1,
                  0.8,
                ],
                16, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 3.0,
                  ["protected_area"], 2.0,
                  1.4,
                ],
                18, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 4.0,
                  ["protected_area"], 2.6,
                  1.8,
                ],
              ] as unknown as number,
              "line-offset": -2.0,             // ON the grass, just inside the edge
              "line-opacity": [
                "interpolate", ["linear"], ["zoom"],
                10, 0.0,
                11, 0.40,
                13, 0.62,
                16, 0.72,
                18, 0.65,
              ],
            },
          },
          firstSymbolId
        );
      } catch {
        /* park source-layer absent — skip */
      }
    }

    // 4c. Apple outer warm rim — bounce light where grass meets path/asphalt.
    // Mirror of hyperlocal-lake-shoreline-rim. Just outside the polygon, blurred,
    // very low opacity.
    if (!map.getLayer("hyperlocal-park-rim")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-park-rim",
            type: "line",
            source: "carto",
            "source-layer": "park",
            minzoom: 11,
            filter: ["==", ["geometry-type"], "Polygon"],
            paint: {
              "line-color": "#3a4a36",         // muted warm grey-green
              "line-blur": 2.2,
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                11, 0.6,
                13, 1.4,
                16, 2.6,
              ],
              "line-offset": 1.2,              // outside — the bounce
              "line-opacity": 0.16,
            },
          },
          firstSymbolId
        );
      } catch {
        /* skip */
      }
    }

    // 4d. SimCity edge casing — the crisp dark forest-green outline that
    // defines the park as a unit. Mirror of hyperlocal-lake-shoreline-case. Sits
    // ON TOP of the other park layers so the boundary snaps into focus,
    // exactly the way a road's `case` layer is the defining edge.
    if (!map.getLayer("hyperlocal-park-edge-case")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-park-edge-case",
            type: "line",
            source: "carto",
            "source-layer": "park",
            minzoom: 9,
            filter: ["==", ["geometry-type"], "Polygon"],
            paint: {
              // Tiered dark forest greens — same hierarchy convention as the
              // band layer, but darker and more saturated so it reads as
              // an edge, not a band.
              "line-color": [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["national_park", "nature_reserve"], "#0e1f0c",  // deepest forest
                ["protected_area"], "#142718",                    // mid forest
                "#1a2118",                                        // default park edge
              ] as unknown as string,
              "line-blur": 0,
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                9, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 0.4,
                  ["protected_area"], 0.3,
                  0.2,
                ],
                13, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 1.1,
                  ["protected_area"], 0.8,
                  0.55,
                ],
                16, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 2.0,
                  ["protected_area"], 1.3,
                  0.9,
                ],
                18, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["national_park", "nature_reserve"], 2.6,
                  ["protected_area"], 1.6,
                  1.1,
                ],
              ] as unknown as number,
              "line-offset": 0,                 // exactly on the polygon edge
              "line-opacity": [
                "interpolate", ["linear"], ["zoom"],
                9, 0.50,
                12, 0.72,
                15, 0.88,
                18, 0.95,
              ],
            },
          },
          firstSymbolId
        );
      } catch {
        /* skip */
      }
    }

    // 4e. Landcover (wood / grass / forest) — same Apple/SimCity treatment but
    // applied to the BROADER green tile (forests, lawns, urban tree-cover)
    // that don't have a `park` polygon record. This covers Royal Botanic
    // Gardens-adjacent urban forest, the gum-tree patches in Hyderabad's
    // outer suburbs, etc. We use ONE casing-only layer here (no full 4-layer
    // stack) because landcover patches are smaller and more numerous —
    // a 4-layer pass would add visual noise. Just the SimCity casing is
    // enough to give them definition.
    if (!map.getLayer("hyperlocal-landcover-edge")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-landcover-edge",
            type: "line",
            source: "carto",
            "source-layer": "landcover",
            minzoom: 12,
            filter: [
              "all",
              ["==", ["geometry-type"], "Polygon"],
              ["match",
                ["coalesce", ["get", "class"], "default"],
                ["wood", "grass", "scrub", "forest"], true,
                false,
              ],
            ],
            paint: {
              "line-color": [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["wood", "forest"], "#0f1c10",     // forest darkest
                ["grass"], "#162217",               // grass medium
                ["scrub"], "#1a2218",               // scrub subtle
                "#161e16",
              ] as unknown as string,
              "line-blur": 0,
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                12, 0.25,
                14, 0.55,
                16, 0.9,
                18, 1.2,
              ],
              "line-offset": 0,
              "line-opacity": [
                "interpolate", ["linear"], ["zoom"],
                12, 0.40,
                15, 0.70,
                18, 0.85,
              ],
            },
          },
          firstSymbolId
        );
      } catch {
        /* landcover absent — skip */
      }
    }

    // ---- 5. Apple-Maps + SimCity rivers / streams / canals (waterway lines) ---
    // Per user 2026-04-26: "I mean can you get the river stream right".
    //
    // The lake/river POLYGON treatment in section 3 only catches water that's
    // wide enough for OpenMapTiles to render as a polygon. The narrow stuff
    // — urban creeks, the upper Yarra above Warrandyte, the Musi at low
    // flow, drainage canals through Tarneit — comes through the SEPARATE
    // `waterway` source-layer as LINESTRINGS. Those got nothing more than
    // a flat #3a6480 line in applyAppleMapsTint, which is why streams and
    // small rivers read as a "drawn-on hairline" rather than as part of
    // the same water identity as the lakes.
    //
    // Recipe: we mirror the road `case`+`fill` pattern directly (it's the
    // same shape — a hierarchical line with a wider darker outer + narrower
    // brighter inner). Plus an Apple-Maps soft glow underneath for
    // atmospheric continuity with the lake stack.
    //
    // Hierarchy (parallel to motorway > trunk and reservoir > pond):
    //   river        → boldest  (Yarra, Musi, Brisbane River-equivalents)
    //   canal        → bold     (Melbourne stormwater channels, Hyderabad nalas)
    //   stream       → medium   (urban creeks)
    //   drain/ditch  → subtle   (utilitarian channels, almost invisible at low z)
    //
    // Colours (deliberately match the polygon water `shallows-band` palette so
    // a river-line meeting a river-polygon at a width transition reads as
    // ONE continuous waterway, not as two unrelated features):
    //   Apple atmospheric glow:  #2a4d68 — soft blue, blurred, very low alpha
    //   SimCity casing (outer):  #16384e (river) / #162f3e (canal) /
    //                            #152836 (stream) / #142025 (drain/ditch)
    //   SimCity fill (inner):    #3878a0 (river) / #3a72a0 (canal) /
    //                            #346585 (stream) / #2a5878 (drain/ditch)
    //
    // Layer order (back→front, all before firstSymbolId):
    //   carto base water/land → hyperlocal-waterway-glow → hyperlocal-waterway-case
    //   → hyperlocal-waterway-fill → labels.

    // 5a. Apple atmospheric glow — a wide, very low-alpha, blurred blue line
    // that bleeds beyond the actual stream/river edge. Same job as the lake
    // shoreline-glow: gives the watercourse the sense of being "in" the
    // landscape rather than drawn on top of it.
    if (!map.getLayer("hyperlocal-waterway-glow")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-waterway-glow",
            type: "line",
            source: "carto",
            "source-layer": "waterway",
            minzoom: 9,
            filter: ["==", ["geometry-type"], "LineString"],
            paint: {
              "line-color": "#2a4d68",          // soft mid-blue (atmospheric)
              "line-blur": 2.4,                  // halo, not a defined edge
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                9, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 2.6,
                  ["canal"], 2.0,
                  ["stream"], 1.4,
                  ["drain", "ditch"], 1.0,
                  1.6,
                ],
                13, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 5.0,
                  ["canal"], 3.6,
                  ["stream"], 2.4,
                  ["drain", "ditch"], 1.6,
                  2.8,
                ],
                16, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 8.0,
                  ["canal"], 5.6,
                  ["stream"], 3.4,
                  ["drain", "ditch"], 2.2,
                  4.0,
                ],
                18, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 11.0,
                  ["canal"], 7.0,
                  ["stream"], 4.4,
                  ["drain", "ditch"], 2.8,
                  5.0,
                ],
              ] as unknown as number,
              "line-opacity": [
                "interpolate", ["linear"], ["zoom"],
                9, 0.0,
                11, 0.18,
                14, 0.32,
                17, 0.40,
              ],
            },
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
          },
          firstSymbolId
        );
      } catch {
        /* waterway source-layer absent on this style — skip */
      }
    }

    // 5b. SimCity casing — wide darker outer line. Same role as a road's
    // `case` layer: defines the edge of the watercourse against surrounding
    // land. Hierarchical class-tier widths and tier-matched dark blues so a
    // river casing reads bolder than a stream casing reads bolder than a
    // ditch.
    if (!map.getLayer("hyperlocal-waterway-case")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-waterway-case",
            type: "line",
            source: "carto",
            "source-layer": "waterway",
            minzoom: 9,
            filter: ["==", ["geometry-type"], "LineString"],
            paint: {
              "line-color": [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["river"], "#16384e",          // boldest dark teal — matches lake-case
                ["canal"], "#162f3e",          // canal — slightly cooler
                ["stream"], "#152836",         // stream — subtler
                ["drain", "ditch"], "#142025", // utilitarian — darkest, near-canvas
                "#16384e",                     // default = river-equivalent
              ] as unknown as string,
              "line-blur": 0,
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                9, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 1.4,
                  ["canal"], 1.0,
                  ["stream"], 0.7,
                  ["drain", "ditch"], 0.5,
                  0.9,
                ],
                13, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 2.8,
                  ["canal"], 2.0,
                  ["stream"], 1.3,
                  ["drain", "ditch"], 0.9,
                  1.7,
                ],
                16, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 5.0,
                  ["canal"], 3.4,
                  ["stream"], 2.0,
                  ["drain", "ditch"], 1.4,
                  2.6,
                ],
                18, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 7.0,
                  ["canal"], 4.6,
                  ["stream"], 2.6,
                  ["drain", "ditch"], 1.7,
                  3.5,
                ],
              ] as unknown as number,
              "line-opacity": [
                "interpolate", ["linear"], ["zoom"],
                9, 0.55,
                12, 0.78,
                15, 0.92,
                18, 1.0,
              ],
            },
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
          },
          firstSymbolId
        );
      } catch {
        /* skip */
      }
    }

    // 5c. SimCity fill — narrower brighter inner line on TOP of the casing.
    // Same role as a road's `fill` layer: the visible "water surface" of
    // the watercourse, in the same blue family as the polygon shallows
    // band so a river-line continues into a river-polygon without a hue
    // jump at the width transition.
    if (!map.getLayer("hyperlocal-waterway-fill")) {
      try {
        map.addLayer(
          {
            id: "hyperlocal-waterway-fill",
            type: "line",
            source: "carto",
            "source-layer": "waterway",
            minzoom: 8,                          // visible from same zoom as the carto stock layer
            filter: ["==", ["geometry-type"], "LineString"],
            paint: {
              "line-color": [
                "match",
                ["coalesce", ["get", "class"], "default"],
                ["river"], "#3878a0",          // matches lake-shallows river
                ["canal"], "#3a72a0",          // canal — slightly more saturated
                ["stream"], "#346585",         // stream — slightly muted
                ["drain", "ditch"], "#2a5878", // utilitarian — most muted
                "#3878a0",                     // default = river-equivalent
              ] as unknown as string,
              "line-blur": 0,
              // Fill width is ~55-65% of the casing width, so the casing
              // visibly sticks out as the dark rim (same proportion roads use).
              "line-width": [
                "interpolate", ["linear"], ["zoom"],
                8, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 0.6,
                  ["canal"], 0.4,
                  ["stream"], 0.3,
                  ["drain", "ditch"], 0.2,
                  0.4,
                ],
                13, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 1.6,
                  ["canal"], 1.1,
                  ["stream"], 0.7,
                  ["drain", "ditch"], 0.5,
                  0.95,
                ],
                16, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 3.2,
                  ["canal"], 2.0,
                  ["stream"], 1.2,
                  ["drain", "ditch"], 0.8,
                  1.6,
                ],
                18, [
                  "match",
                  ["coalesce", ["get", "class"], "default"],
                  ["river"], 4.6,
                  ["canal"], 2.8,
                  ["stream"], 1.6,
                  ["drain", "ditch"], 1.0,
                  2.2,
                ],
              ] as unknown as number,
              "line-opacity": [
                "interpolate", ["linear"], ["zoom"],
                8, 0.55,
                11, 0.80,
                14, 0.92,
                18, 1.0,
              ],
            },
            layout: {
              "line-cap": "round",
              "line-join": "round",
            },
          },
          firstSymbolId
        );
      } catch {
        /* skip */
      }
    }

    // 5d. Hide the Carto-shipped `waterway` layer — our 3-layer stack
    // supersedes it. Without this, the original flat #3a6480 line would
    // render alongside our case+fill and you'd see TWO blue lines at
    // different widths, both for the same river. Setting opacity to 0
    // (rather than removing the layer outright) is robust to dev hot-reloads
    // where layer ordering isn't guaranteed.
    try {
      if (map.getLayer("waterway")) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (map as any).setPaintProperty("waterway", "line-opacity", 0);
      }
    } catch {
      /* layer renamed upstream — degrade gracefully (our layers carry the load) */
    }
  } catch (err) {
    // Same lint policy as the other Carto-overlay helpers: warn, never throw.
    console.warn("[HYPERLOCAL] water/landuse refinement failed:", err);
  }
}

/**
 * Append a 3D fill-extrusion layer for OSM buildings.
 *
 * Voyager's vector source exposes a `building` source-layer with a
 * `render_height` numeric field. We extrude every building from
 * `render_min_height` (0 for ground-level) to `render_height` and shade
 * the walls with a height-driven gradient — taller buildings get a
 * darker base and lighter top, which approximates the way Apple Maps
 * gives skyscrapers visible "weight" against shorter neighbours.
 *
 * Pitch must be > 0 for the extrusion to actually look 3D; the caller
 * sets `pitch: 45` when this style succeeds. At pitch 0 the extrusion
 * still draws but it just looks like flat polygons (still useful as a
 * vector outline).
 *
 * Wrapped in a feature-detect: if the loaded style ever stops shipping
 * the `building` source-layer (or the `carto` source is renamed), we
 * silently skip rather than throw.
 */
function attach3DBuildings(map: maplibregl.Map): void {
  try {
    if (map.getLayer("building-3d")) return;
    if (!map.getSource("carto")) return;
    map.addLayer(
      {
        id: "building-3d",
        type: "fill-extrusion",
        source: "carto",
        "source-layer": "building",
        minzoom: 13,
        filter: ["!=", ["get", "hide_3d"], true],
        paint: {
          // Height-driven dark-grey gradient. Sun-top-left convention is
          // implemented by MapLibre's natural face-shading on extrusions
          // (the engine darkens north/east faces relative to south/west).
          // Tall buildings settle a touch lighter so the city skyline has
          // visible "weight" against the #0a0a0a canvas without glowing.
          "fill-extrusion-color": [
            "interpolate",
            ["linear"],
            ["coalesce", ["get", "render_height"], 5],
            0, "#1f1f1f",  // very short building — barely above canvas
            10, "#262626",
            30, "#303030",
            80, "#3c3c3c",
            200, "#4a4a4a", // skyscraper — pale graphite top
          ],
          "fill-extrusion-height": [
            "interpolate",
            ["linear"],
            ["zoom"],
            13, 0,
            13.5, ["coalesce", ["get", "render_height"], 5],
          ],
          "fill-extrusion-base": [
            "coalesce",
            ["get", "render_min_height"],
            0,
          ],
          "fill-extrusion-opacity": 0.92,
          // Vertical gradient softens the harsh top-edge boundary.
          "fill-extrusion-vertical-gradient": true,
        },
      },
      // Insert UNDER any text/symbol layer so labels stay legible.
      // Find first symbol layer and insert before it; if none, append.
      (() => {
        const layers = map.getStyle().layers ?? [];
        for (const l of layers) {
          if (l.type === "symbol") return l.id;
        }
        return undefined;
      })()
    );
  } catch (err) {
    // Warning-level console use is permitted in this file (see lint
    // config). The previous eslint-disable-next-line directive became
    // redundant after the policy change and was failing CI under the
    // --report-unused-disable-directives flag.
    console.warn("[HYPERLOCAL] 3D buildings attach failed:", err);
  }
}

/**
 * SimCity-style "top 1% / landmark" tall-building overlay.
 *
 * Per user feedback 2026-04-26: "try colour coding top 1% building (Could
 * be landmarks or tallest buildings) in Sim city style on the map."
 *
 * Approach:
 *   We add a SECOND fill-extrusion layer on top of `building-3d`, filtered
 *   to only render buildings with `render_height >= 50m`. The original
 *   threshold was 70m (~20 storeys, conceptually "top 1%"), but live
 *   sampling against Carto's vector tiles in Hitec City showed real OSM
 *   data tops out at ~92m max with p90 ≈ 48m, p99 ≈ 92m. A 70m floor
 *   lit nothing; 50m hits roughly the tallest 10% of structures, which
 *   in both Hyderabad and Melbourne picks out the actual landmark
 *   highrises (Hitec City towers, CBD towers, Eureka, Rialto) without
 *   lighting up every 4-storey block.
 *
 *   Color is a saturated SimCity gold (#ffb84d → #ffd97a) — warm and
 *   obviously "this is a landmark" against the dark-grey skyline. The
 *   gradient runs taller-buildings = more golden so we get a real visual
 *   crescendo on the city's tallest spires.
 *
 *   We DON'T duplicate the geometry's height — we just paint over it with
 *   a slightly higher opacity, so the SimCity gold is unambiguously the
 *   "selected" tall-buildings layer above the dark-grey base.
 *
 * Wrapped in a feature-detect: if `building-3d` didn't attach (style
 * upstream changed), we silently skip rather than throw.
 */
function attachLandmarkBuildings(map: maplibregl.Map): void {
  try {
    if (map.getLayer("building-3d-landmark")) return;
    if (!map.getLayer("building-3d")) return; // depends on base layer existing
    if (!map.getSource("carto")) return;
    map.addLayer(
      {
        id: "building-3d-landmark",
        type: "fill-extrusion",
        source: "carto",
        "source-layer": "building",
        minzoom: 13,
        // Anything 50m+ is in the city's top ~10% by height — call it
        // a landmark. Threshold dropped from 70m after live sampling
        // showed Carto's OSM data caps at 92m max (p90 ≈ 48m, p99 ≈ 92m)
        // — a 70m floor lit nothing.
        filter: [
          "all",
          ["!=", ["get", "hide_3d"], true],
          [">=", ["coalesce", ["get", "render_height"], 0], 50],
        ],
        paint: {
          // SimCity warm-gold gradient. The 50m floor matches the filter
          // and we ramp up so an 80m+ building is the brightest point in
          // the skyline. Stops are non-linear so a 50m landmark is clearly
          // golden but the rare 90m+ tower stands out as the brightest
          // honey on the map.
          "fill-extrusion-color": [
            "interpolate",
            ["linear"],
            ["coalesce", ["get", "render_height"], 50],
            50, "#d99840",   // landmark floor — warm amber
            70, "#ffb84d",   // mid-tier landmark — saturated gold
            100, "#ffc868",
            150, "#ffd97a",
            220, "#ffe49a",  // tallest tier — bright honey
          ],
          "fill-extrusion-height": [
            "interpolate",
            ["linear"],
            ["zoom"],
            13, 0,
            13.5, ["coalesce", ["get", "render_height"], 50],
          ],
          "fill-extrusion-base": [
            "coalesce",
            ["get", "render_min_height"],
            0,
          ],
          // Slightly more opaque than the base layer so the gold reads
          // unambiguously as "this is a landmark", but still blends with
          // the camera-shading on faces.
          "fill-extrusion-opacity": 0.96,
          "fill-extrusion-vertical-gradient": true,
        },
      },
      // Insert just above `building-3d` (so it overpaints the base) but
      // still under any symbol/text layer. Find the first symbol layer.
      (() => {
        const layers = map.getStyle().layers ?? [];
        for (const l of layers) {
          if (l.type === "symbol") return l.id;
        }
        return undefined;
      })()
    );
  } catch (err) {
    // See note on the matching eslint directive removal above.
    console.warn("[HYPERLOCAL] landmark buildings attach failed:", err);
  }
}

/**
 * Zoom-driven pitch easing — 2D for decision-making, 3D for exploration.
 *
 * Per user feedback 2026-04-26: "only make the map 3D once it is zoomed in
 * close to buildings or else have 2D map which will be easier for decision
 * making."
 *
 * The default city zoom (11.2) drops users into a 2D top-down view that
 * makes scanning suburbs and verdict pins straightforward — no perspective
 * skew warping the spatial relationship between two adjacent suburbs.
 * As they zoom IN past 14 (street level), pitch lifts smoothly so 3D
 * buildings come into view; by zoom 16 the camera is at full Apple-Maps
 * tilt (50° desktop / 35° mobile) and the city skyline reads in 3D.
 *
 * We cap pitch updates to once per ~120ms (rough animation-frame budget)
 * so a continuous wheel-zoom doesn't fire setPitch on every event — that
 * was visibly stuttery on a 60fps display when tested earlier.
 *
 * Why a JS handler instead of `pitch: ["interpolate", ["zoom"], …]`:
 *   MapLibre supports zoom-interpolation on `paint` properties but NOT
 *   on the camera (pitch/bearing). Camera state is imperative.
 *
 * @param map           the maplibre instance
 * @param maxPitch      target pitch at zoom >= MAX_ZOOM (50 desktop / 35 mobile)
 * @param threshold     zoom below which pitch is locked at 0 (decision view)
 */
function installPitchByZoom(
  map: maplibregl.Map,
  maxPitch: number,
  threshold = 14,
  fullPitchAt = 16
): void {
  // Smoothstep so the pitch lift doesn't pop at the threshold — the eye
  // tolerates a gradual ramp better than a hard cut.
  const easeAtZoom = (z: number): number => {
    if (z <= threshold) return 0;
    if (z >= fullPitchAt) return maxPitch;
    const t = (z - threshold) / (fullPitchAt - threshold);
    // Smoothstep: 3t² - 2t³
    const s = t * t * (3 - 2 * t);
    return maxPitch * s;
  };
  let last = 0;
  const update = (force = false): void => {
    const now = performance.now();
    if (!force && now - last < 120) return;
    last = now;
    const target = easeAtZoom(map.getZoom());
    // Only nudge if we're more than 1° off — prevents micro-stutter and
    // also lets the user override pitch manually (two-finger drag) without
    // us snapping it back every zoom event.
    const current = map.getPitch();
    if (Math.abs(current - target) <= 1) return;
    if (force) {
      // After motion settles (moveend) and any in-flight programmatic
      // animation has resolved, gently easeTo the target pitch over 220ms.
      // This is the catchup pass — flyTo/fitBounds may have left pitch in
      // an inconsistent state, and `force=true` here ensures the zoom-based
      // pitch authority always wins after motion stops.
      //
      // We avoid easeTo during in-flight programmatic animations by only
      // firing this branch on `moveend`, which is itself emitted *after*
      // the prior animation completes — so isEasing() should be false here.
      if (!map.isEasing()) {
        map.easeTo({
          pitch: target,
          duration: 220,
          easing: (t) => t * t * (3 - 2 * t),
        });
      } else {
        // Defensive — if for some reason an animation is still running
        // (e.g. consecutive flyTo calls firing moveend mid-flight),
        // jumpTo without animating so we don't fight the running easing.
        map.setPitch(target);
      }
    } else {
      // During live zoom (wheel/pinch), use setPitch — instantaneous, fires
      // no easing, so it can't cancel the user's wheel-zoom animation. Per
      // 2026-04-26 user feedback ("Once it got to the last zoom in point
      // map is getting stuck"): the prior easeTo({pitch}) launched a
      // 220ms animation on every zoom event, which fought the wheel zoom's
      // own internal easing — on slow hardware the queue of competing
      // animations could stall the renderer. setPitch is synchronous and
      // the per-tick deltas are small (smoothstep curve) so the result
      // still reads as a smooth pitch ramp without animation conflict.
      map.setPitch(target);
    }
  };
  map.on("zoom", () => update(false));
  // After ANY camera movement settles (flyTo, fitBounds, drag-end, wheel-end)
  // run the pitch update once more, forcing past the rate-limit. This is the
  // catchup pass that smoothly eases pitch into place after programmatic
  // animations that didn't specify their own pitch target.
  map.on("moveend", () => update(true));
  // Also fire once on attach so the initial pitch matches the initial zoom.
  update(true);
}

// ---- Status banner --------------------------------------------------------

/**
 * Status banner — intentionally stubbed to a no-op. The visible tag
 * ("Hyderabad · 60 verdicts · Carto Dark (raster)") covered map real
 * estate near the home/back chip and competed with the city toggle for
 * attention. Users want a clean map canvas; the dataset size / basemap
 * name is not information they ever need to read. Boot messages still
 * flow through console.log, so devtools can still see the progression.
 *
 * Kept as a function (not removed) so the existing call sites compile
 * without edits. If a status UI is re-introduced later, put it back
 * here — don't sprinkle DOM code through the cascade.
 */
function status(msg: string): void {
  // eslint-disable-next-line no-console
  console.log("[HYPERLOCAL:boot]", msg);
  // Defensive: if a banner was created in an earlier session/hot-reload
  // pass it lingers in the DOM. Remove it so the map is fully exposed.
  const stale = document.getElementById("status-banner");
  if (stale) stale.remove();
}

// ---- FPS badge — DELETED 2026-06-11. A debug counter reading "FPS: 2" was
// shipping to real users and its rAF loop ticked forever (battery cost on
// mobile, trust cost everywhere). Profile with devtools, not product UI.

// ---- First-visit hint — DELETED 2026-06-11. "Tap any glowing spot to see
// its verdict" asked the user to hunt for the answer. The area snapshot
// (src/ui/area-snapshot.ts) puts the answer on screen instead.

// ---- Compare-2-Areas pill -------------------------------------------------
// Shared compare state lives in `./ui/compare-state`. The pill only renders
// the current state; transitions are driven by the shared module so Top
// Picks cards and verdict popups can drive compare from their own surfaces.
// Pin taps still intercept when mode is waitA/waitB (see installVerdictLayer).

function updateCompareUI() {
  const pill = document.getElementById("hyperlocal-compare-pill");
  const txt = document.getElementById("hyperlocal-compare-pill-txt");
  if (!pill || !txt) return;
  const { mode, a } = getCompareState();
  // The chrome-shell.ts CSS owns the visual states via `.es-armed` —
  // idle is a quiet neutral chip, armed (waitA / waitB) earns amber.
  // Inline styles below are kept as a fallback for surfaces that
  // don't load the chrome-shell stylesheet, but are overridden when
  // the pill lives inside `.es-bottom`.
  if (mode === "off") {
    pill.classList.remove("es-armed");
    pill.style.background = "rgba(255,211,126,0.14)";
    pill.style.borderColor = "rgba(255,211,126,0.55)";
    pill.style.color = "#FFD37E";
    pill.style.boxShadow =
      "0 4px 14px rgba(0,0,0,0.35),0 0 0 1px rgba(255,211,126,0.08) inset";
    txt.textContent = "Compare";
  } else if (mode === "waitA") {
    pill.classList.add("es-armed");
    pill.style.background = "rgba(255,211,126,0.26)";
    pill.style.borderColor = "rgba(255,211,126,0.85)";
    pill.style.color = "#FFE6B0";
    pill.style.boxShadow =
      "0 6px 18px rgba(0,0,0,0.45),0 0 0 1px rgba(255,211,126,0.22) inset";
    txt.textContent = "Pick Area A · cancel";
  } else {
    pill.classList.add("es-armed");
    pill.style.background = "rgba(255,211,126,0.32)";
    pill.style.borderColor = "rgba(255,211,126,0.95)";
    pill.style.color = "#FFE6B0";
    pill.style.boxShadow =
      "0 6px 18px rgba(0,0,0,0.5),0 0 0 1px rgba(255,211,126,0.28) inset";
    const aName = a?.name ?? "Area A";
    txt.textContent = `A selected: ${aName} · Choose B · cancel`;
  }
}

/**
 * Pill-driven cancel. Resets shared state (which triggers updateCompareUI
 * via the subscription) and shows the one-shot retry hint.
 */
function exitCompare() {
  cancelCompare();
  flashCompareRetry();
}

/**
 * Frictionless retry: after a compare card closes, nudge the pill to
 * "Compare another area →" for a few seconds. Clicking it re-enters compare.
 * No new UI element, no animation — just a label swap on the existing pill.
 */
let flashCompareRetryTimer: ReturnType<typeof setTimeout> | null = null;
function flashCompareRetry() {
  const txt = document.getElementById("hyperlocal-compare-pill-txt");
  if (!txt) return;
  txt.textContent = "Compare another →";
  if (flashCompareRetryTimer) clearTimeout(flashCompareRetryTimer);
  flashCompareRetryTimer = setTimeout(() => {
    if (getCompareState().mode !== "off") return;
    const t = document.getElementById("hyperlocal-compare-pill-txt");
    if (t) t.textContent = "Compare";
    flashCompareRetryTimer = null;
  }, 6000);
}

function installComparePill() {
  if (document.getElementById("hyperlocal-compare-pill")) return;
  const pill = document.createElement("button");
  pill.id = "hyperlocal-compare-pill";
  pill.setAttribute("type", "button");
  pill.setAttribute("aria-label", "Compare two areas");
  pill.title = "Compare two areas";
  // Amber tint, amber border, drop-shadow, 600 weight — keeps the pill
  // visually distinct from the muted nav buttons it sits beside in the
  // bottom bar. `cursor:pointer`, `transition`, hover handlers make
  // clickability obvious.
  pill.style.cssText = `background:rgba(255,211,126,0.14);
    color:#FFD37E;padding:9px 14px 9px 12px;border-radius:999px;
    font:600 12.5px/1 system-ui;letter-spacing:0.02em;
    border:1px solid rgba(255,211,126,0.55);
    box-shadow:0 4px 14px rgba(0,0,0,0.35),0 0 0 1px rgba(255,211,126,0.08) inset;
    cursor:pointer;display:inline-flex;align-items:center;gap:8px;
    transition:background 140ms ease,border-color 140ms ease,transform 120ms ease,box-shadow 140ms ease;`;
  // Compare icon: two small squares + connecting arrows. Inline SVG so it
  // inherits currentColor and doesn't depend on any icon font.
  const icon = document.createElement("span");
  icon.setAttribute("aria-hidden", "true");
  icon.style.cssText =
    "display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;";
  icon.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3L4 7l4 4"/><path d="M4 7h12"/><path d="M16 21l4-4-4-4"/><path d="M20 17H8"/></svg>';
  const txt = document.createElement("span");
  txt.id = "hyperlocal-compare-pill-txt";
  txt.textContent = "Compare";
  pill.appendChild(icon);
  pill.appendChild(txt);
  // Hover / active — boost fill + lift, so clickability is unmistakable on
  // first pass without a loud color change.
  pill.addEventListener("mouseenter", () => {
    if (getCompareState().mode === "off") {
      pill.style.background = "rgba(255,211,126,0.22)";
      pill.style.borderColor = "rgba(255,211,126,0.75)";
      pill.style.boxShadow =
        "0 6px 18px rgba(0,0,0,0.45),0 0 0 1px rgba(255,211,126,0.14) inset";
    }
  });
  pill.addEventListener("mouseleave", () => {
    // `updateCompareUI` owns the per-mode style. Re-apply it rather than
    // hard-coding the resting state here so active modes don't flicker.
    updateCompareUI();
  });
  pill.addEventListener("mousedown", () => {
    pill.style.transform = "translateY(1px)";
  });
  pill.addEventListener("mouseup", () => {
    pill.style.transform = "translateY(0)";
  });
  pill.addEventListener("click", () => {
    if (getCompareState().mode === "off") {
      // Enter compare mode. Hide any open verdict card so the user knows the
      // next pin tap means "Area A".
      dismissVerdict();
      dismissCompareCard();
      enterWaitA();
      console.log("[HYPERLOCAL] compare: enter, waiting for Area A");
    } else {
      console.log("[HYPERLOCAL] compare: cancel from pill");
      exitCompare();
      dismissCompareCard();
    }
  });
  // Keep the pill in sync with transitions driven elsewhere (shortlist
  // cards, verdict popup, map pins).
  subscribeCompare(() => updateCompareUI());
  // Mount inside the Decide page's compare slot if it's already in the
  // DOM; otherwise poll briefly until the surface renders. Falls back to
  // body so the pill is never missing if the surface fails to mount.
  const mount = () => {
    const slot = document.querySelector(
      '#hyperlocal-filter-page [data-slot="compare"]',
    );
    if (slot) {
      slot.appendChild(pill);
      return true;
    }
    return false;
  };
  if (!mount()) {
    let tries = 0;
    const t = setInterval(() => {
      if (mount() || ++tries > 40) {
        clearInterval(t);
        if (!pill.isConnected) document.body.appendChild(pill);
      }
    }, 50);
  }
}

/* -------------------------------------------------------------------------- */
/*  School View toggle pill — opt-in overlay for the school-marker layer.     */
/*  Visible only when Priority === "school". Off by default; clicking         */
/*  toggles the overlay + the floating legend. Keeps the base map clean.      */
/* -------------------------------------------------------------------------- */

// installSchoolOverlayPill removed — see top-of-file note.

/* -------------------------------------------------------------------------- */
/*  School Chart pill — slot 3 in the left control rail.                      */
/*  Opens the full ranked school list modal. Visible only under               */
/*  Priority === "school"; slot stays reserved regardless so the rail         */
/*  layout doesn't reflow.                                                    */
/* -------------------------------------------------------------------------- */

function installSchoolChartPill(cityId: CityId, cityLabel: string, verdicts: readonly Verdict[], map: maplibregl.Map) {
  if (document.getElementById("hyperlocal-schoolchart-pill")) return;
  const pill = document.createElement("button");
  pill.id = "hyperlocal-schoolchart-pill";
  pill.setAttribute("type", "button");
  const label = `${cityLabel} school rankings`;
  pill.setAttribute("aria-label", `Open the ${label} chart`);
  pill.title = `Open the ${label} chart`;
  // Lives inside the Decide page's "What matters most?" section (mounted
  // into [data-slot=school] below) and only renders under Priority=school.
  pill.style.cssText = `
    background:rgba(159,207,224,0.12);backdrop-filter:blur(10px);
    color:#9FCFE0;padding:8px 12px;border-radius:999px;
    font:600 12px/1 system-ui;letter-spacing:0.02em;
    border:1px solid rgba(159,207,224,0.30);
    box-shadow:0 4px 14px rgba(0,0,0,0.35);
    cursor:pointer;display:none;align-items:center;gap:7px;
    margin-top:8px;
    transition:background 140ms ease,border-color 140ms ease;`;
  // Bar-chart glyph — visually maps to "chart / ranking list".
  const icon = document.createElement("span");
  icon.setAttribute("aria-hidden", "true");
  icon.style.cssText =
    "display:inline-flex;align-items:center;justify-content:center;width:16px;height:16px;";
  icon.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/></svg>';
  const txt = document.createElement("span");
  // Desktop gets the full "Hyderabad school rankings" label; mobile
  // swaps to the shorter "School rankings" via the media-query rule
  // below — the HYD/MEL toggle already tells the user which city.
  // We wire two text nodes (desktop + mobile) and CSS hides the one
  // that doesn't belong, so there's no JS-based resize listener to
  // break if the viewport rotates.
  const txtDesktop = document.createElement("span");
  txtDesktop.className = "sc-label-desktop";
  txtDesktop.textContent = label;
  const txtMobile = document.createElement("span");
  txtMobile.className = "sc-label-mobile";
  txtMobile.textContent = "School rankings";
  txt.appendChild(txtDesktop);
  txt.appendChild(txtMobile);
  pill.appendChild(icon);
  pill.appendChild(txt);
  // Stamp the toggling rules once — desktop-only by default; flip
  // visibility on mobile viewports. Idempotent.
  if (!document.getElementById("hyperlocal-schoolchart-pill-labels")) {
    const s = document.createElement("style");
    s.id = "hyperlocal-schoolchart-pill-labels";
    s.textContent = `
      #hyperlocal-schoolchart-pill .sc-label-mobile { display: none; }
      @media (max-width: 720px), (max-height: 500px) {
        #hyperlocal-schoolchart-pill .sc-label-desktop { display: none; }
        #hyperlocal-schoolchart-pill .sc-label-mobile { display: inline; }
      }
    `;
    document.head.appendChild(s);
  }

  function render() {
    pill.style.display = getPriority() === "school" ? "inline-flex" : "none";
  }

  pill.addEventListener("mouseenter", () => {
    pill.style.background = "rgba(159,207,224,0.22)";
  });
  pill.addEventListener("mouseleave", () => {
    pill.style.background = "rgba(159,207,224,0.12)";
  });
  pill.addEventListener("click", async () => {
    // `getActiveDataset` was populated when the city loaded; resolves
    // suburb-id → suburb-name for the chart's rightmost column.
    const dataset = getActiveDataset();
    const nameById = new Map<string, string>();
    if (dataset) for (const v of dataset) nameById.set(v.id, v.name);
    const verdictById = new Map<string, Verdict>();
    for (const v of verdicts) verdictById.set(v.id, v);
    // Chart module loads on first click (and is cached after) — it's the
    // only consumer of the ~50KB school-chart/school-facts pair.
    const { showSchoolChart } = await import("./ui/school-chart");
    showSchoolChart(
      cityId as SchoolCity,
      cityLabel,
      (id) => nameById.get(id) ?? null,
      // Row click → fly to the suburb and open its verdict popup. Chart
      // is dismissed by school-chart.ts itself before this fires.
      (suburbId) => {
        const v = verdictById.get(suburbId);
        if (!v) return;
        flyToSuburb(map, v.coord);
        showVerdict(v);
      }
    );
  });

  subscribePriority(render);
  render();

  // Mount into the Decide page's school slot (the surface installs just
  // before this in the boot order; poll briefly to be safe, body fallback
  // so the pill is never lost).
  const mount = () => {
    const slot = document.querySelector(
      '#hyperlocal-filter-page [data-slot="school"]',
    );
    if (slot) {
      slot.appendChild(pill);
      return true;
    }
    return false;
  };
  if (!mount()) {
    let tries = 0;
    const t = setInterval(() => {
      if (mount() || ++tries > 40) {
        clearInterval(t);
        if (!pill.isConnected) document.body.appendChild(pill);
      }
    }, 50);
  }
}

/* -------------------------------------------------------------------------- */
/*  Auto-framing — camera follows the highlighted points.                     */
/*                                                                            */
/*  Two triggers:                                                             */
/*    1. Shortlist ids change (priority/band/lens/mode/type moved the picks). */
/*       Fit the map to the picks. Skipped when: set is empty, full-map is    */
/*       active, or school overlay is on (overlay has its own frame path).    */
/*    2. School View turns on. Fit to the school markers that will actually   */
/*       render — schools in currently shortlisted suburbs. City-specific via */
/*       the verdict dataset (Hyderabad has no ranks → zero coords → no-op).  */
/*                                                                            */
/*  Asymmetric padding — left-heavy so the control rail + Top Picks panel     */
/*  never cover the framed points.                                            */
/* -------------------------------------------------------------------------- */

// Responsive auto-fit padding.
//
// Desktop: the shortlist panel docks on the LEFT at 320px + 16px gap, so we
// reserve ~360px of left-side padding to keep fit-bounds from placing pins
// underneath it.
//
// Mobile (<=720px): the shortlist panel becomes a bottom-sheet, so the left
// reservation would waste half the viewport. Symmetric, generous bottom
// padding keeps pins visible above the docked card + sheet handle.
function getAutoFitPadding() {
  const w = typeof window !== "undefined" ? window.innerWidth : 1280;
  if (w <= 720) {
    return { top: 90, right: 24, bottom: 280, left: 24 };
  }
  return { top: 80, right: 70, bottom: 60, left: 360 };
}
const AUTO_FIT_MAX_ZOOM = 12.5;
const AUTO_FIT_SINGLE_ZOOM = 12;
const AUTO_FIT_DURATION = 900;

/**
 * Camera grammar (design language §7): ONE select move, ONE framing move.
 * Every "go to this suburb" gesture — pin tap, near-tap, school row,
 * snapshot expand, deep link — flies with the same 800ms feel; framing
 * moves (fit-to-picks / full city) use the 900ms AUTO_FIT_DURATION. Six
 * bespoke speeds for the same gesture family was noise, not character.
 */
const SELECT_MOVE_MS = 800;
function flyToSuburb(
  map: maplibregl.Map,
  center: [number, number],
  minZoom = 13
): void {
  map.flyTo({
    center,
    zoom: Math.max(map.getZoom(), minZoom),
    duration: SELECT_MOVE_MS,
    essential: true,
  });
}

// Full-city framing (Full Map button) deliberately allows a lower zoom
// than the shortlist-fit above — a city-wide frame for MEL spans ~100 km
// east-west, so capping at 12.5 would clip the outer growth belts.
// 10.5 comfortably frames both HYD's tighter footprint and MEL's broader
// metro without overshooting into world-view.
const FULL_CITY_FIT_MAX_ZOOM = 10.5;
const FULL_CITY_FIT_DURATION = 900; // framing move — same feel as AUTO_FIT_DURATION

function installAutoFraming(
  map: maplibregl.Map,
  verdicts: readonly Verdict[],
  cityCenter: [number, number],
  cityZoom: number
): void {
  const verdictById = new Map<string, Verdict>();
  for (const v of verdicts) verdictById.set(v.id, v);

  // Every major user action that reframes the map or changes "what
  // matters" should yield the visual priority to that action — i.e.
  // close any stale verdict popup.
  subscribePriority(() => dismissVerdict());
  subscribeLens(() => dismissVerdict());
  subscribeFullMapView(() => dismissVerdict());

  function fitToCoords(coords: Array<[number, number]>): void {
    if (coords.length === 0) return;
    if (coords.length === 1) {
      map.flyTo({
        center: coords[0],
        zoom: Math.max(map.getZoom(), AUTO_FIT_SINGLE_ZOOM),
        padding: getAutoFitPadding() as any,
        duration: AUTO_FIT_DURATION,
      });
      return;
    }
    const [fLng, fLat] = coords[0];
    let minLng = fLng, maxLng = fLng, minLat = fLat, maxLat = fLat;
    for (const [lng, lat] of coords) {
      if (lng < minLng) minLng = lng;
      if (lng > maxLng) maxLng = lng;
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
    }
    map.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      {
        padding: getAutoFitPadding(),
        maxZoom: AUTO_FIT_MAX_ZOOM,
        duration: AUTO_FIT_DURATION,
      }
    );
  }

  // Pure helper: fit to current shortlist picks. Shared by the shortlist
  // subscription AND the full-map-view exit path.
  function fitToShortlistIds(ids: ReadonlySet<string>): void {
    const coords: Array<[number, number]> = [];
    for (const id of ids) {
      const v = verdictById.get(id);
      if (v) coords.push(v.coord);
    }
    if (coords.length === 0) return;
    fitToCoords(coords);
  }

  // 1. Shortlist → fit. Skipped in Exploration mode — the user hasn't
  //    committed to a decision frame, so the map should not reframe.
  //    Also skipped while Full-map view is active.
  let lastShortlistKey = "";
  subscribeShortlist((ids) => {
    if (getLensMode() === "none") return;
    if (getFullMapView()) return; // user is looking at the full city
    const key = Array.from(ids).sort().join(",");
    if (key === lastShortlistKey) return;
    lastShortlistKey = key;
    if (ids.size === 0) return;
    // Any stale popup belongs to the OLD picks; reframing the map makes
    // it read as blocking content. Dismiss before moving.
    dismissVerdict();
    fitToShortlistIds(ids);
  });

  // 1b. Full-map view toggle → camera behaviour.
  //   entering (true)  → fit to EVERY suburb point in the active city
  //                      dataset, so the frame guarantees all pins are
  //                      visible. Asymmetric padding reserves room for
  //                      the left-side shortlist panel (still visible
  //                      with a "Full map" note) and the top control
  //                      rail (Compare / School-chart pills).
  //   leaving  (false) → re-fit to the current shortlist picks.
  subscribeFullMapView((on) => {
    if (on) {
      // Collect every suburb coord from the active city's dataset. No
      // filtering, no priority, no persona — the whole city in frame.
      const allCityCoords: Array<[number, number]> = [];
      for (const v of verdicts) allCityCoords.push(v.coord);
      if (allCityCoords.length === 0) {
        // Defensive fallback — empty dataset shouldn't happen but we
        // don't want a dead button if it does.
        map.flyTo({
          center: cityCenter,
          zoom: cityZoom,
          duration: FULL_CITY_FIT_DURATION,
          essential: true,
        });
        return;
      }
      if (allCityCoords.length === 1) {
        map.flyTo({
          center: allCityCoords[0],
          zoom: Math.min(cityZoom, FULL_CITY_FIT_MAX_ZOOM),
          duration: FULL_CITY_FIT_DURATION,
          essential: true,
        });
        return;
      }
      const [fLng, fLat] = allCityCoords[0];
      let minLng = fLng, maxLng = fLng, minLat = fLat, maxLat = fLat;
      for (const [lng, lat] of allCityCoords) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
      }
      map.fitBounds(
        [
          [minLng, minLat],
          [maxLng, maxLat],
        ],
        {
          padding: getAutoFitPadding(),
          maxZoom: FULL_CITY_FIT_MAX_ZOOM,
          duration: FULL_CITY_FIT_DURATION,
        }
      );
      return;
    }
    // Returning to "Picks for you" — re-fit to the user's picks using
    // their current filters. Reset the dedup key so the next shortlist
    // broadcast also forces a fit in edge cases.
    lastShortlistKey = "";
    fitToShortlistIds(getShortlistIds());
  });

  // 2. Explicit "filters applied" commit. Fires when the user taps "See
  //    the map" on the mobile filter page. Forces a fit to the current
  //    shortlist even if filters didn't change — so opening the filter
  //    page and tapping Apply without mutating anything still reframes
  //    the camera to the picks. If the shortlist is empty (Exploration
  //    mode or no matches), falls back to the full-city frame so the
  //    user sees context rather than an off-screen default center.
  window.addEventListener("hyperlocal:filters-applied", () => {
    const ids = getShortlistIds();
    lastShortlistKey = ""; // drop dedup so a duplicate shortlist still fits
    if (ids.size > 0) {
      fitToShortlistIds(ids);
      return;
    }
    // Empty shortlist: fit to ALL city pins so the user at least sees
    // the map in context instead of a blank default zoom/center.
    const allCityCoords: Array<[number, number]> = [];
    for (const v of verdicts) allCityCoords.push(v.coord);
    if (allCityCoords.length === 0) return;
    fitToCoords(allCityCoords);
  });

  // Idle auto-fit REMOVED (design language §2: "the camera belongs to the
  // user"). The old behaviour seized the camera 5 seconds after the user
  // stopped interacting — barely longer than reading one verdict line — and
  // no set of guard rules makes autonomous camera motion feel right on a
  // map someone deliberately parked. The explicit refit triggers above
  // (shortlist change, filters applied, full-map toggle) all remain.
}

// ---- Verdict layer --------------------------------------------------------

function installVerdictLayer(
  map: maplibregl.Map,
  verdicts: Verdict[],
  nearFn: (lng: number, lat: number, maxKm?: number) => Verdict | null,
  cityLabel: string,
  cityId: CityId
): void {
  // One log line per layer install so you can see what dataset is wired up.
  console.log(
    "[HYPERLOCAL] installing layer for city:",
    cityId,
    "(", cityLabel, ") dataset size:",
    verdicts.length
  );
  /**
   * Pin features carry the fixed verdict (safety/quality truth) plus a
   * separate `isTopMatch` / `fitColor` pair driven by the active priority.
   * Two-axis design:
   *
   *   verdict     → drives `circle-color` on the dot + base glow.
   *                 ALWAYS green/amber/red. Never altered by user preference.
   *                 This is the core safety/quality signal — users must not
   *                 be able to "make a Red suburb look Green" by switching
   *                 priority.
   *   isTopMatch  → true for at most 5 suburbs per priority (the highest-
   *                 scoring fits). The halo layer ONLY renders when this
   *                 flag is true, so the map's answer to "where should I
   *                 look first?" is visually unambiguous. Everything else
   *                 stays clean — no halo, no noise, no implied approval.
   *   fitColor    → halo tint (teal for commute, green for school, coral
   *                 for lifestyle). Only consumed on top-match pins.
   *
   * Result: under Commute priority, ≤5 suburbs glow with a sharp teal ring.
   * A Red-safety suburb with a top-5 commute score still glows teal, and
   * the base red pin sits inside that ring — "good commute fit, but risky
   * overall" reads instantly without copy.
   */
  const buildFeatures = (priority: ReturnType<typeof getPriority>) => {
    const topIds = selectTopMatches(verdicts, priority);
    const shortlistIdSet = getShortlistIds();
    if (priority !== "none") {
      console.log(
        "[HYPERLOCAL] priority-fit top matches for",
        priority,
        "·",
        [...topIds].join(", ") || "(none qualified)"
      );
    }
    if (shortlistIdSet.size > 0) {
      console.log(
        "[HYPERLOCAL] shortlist IDs (map emphasis):",
        [...shortlistIdSet].join(", ")
      );
    }
    return verdicts.map((v: Verdict) => {
      const fit = priorityFit(v, priority);
      const isTop = topIds.has(v.id);
      const isShortlisted = shortlistIdSet.has(v.id);
      // Density derives purely from SCHOOL_RANKS row count for this
      // suburbId. Computed once per build (cheap — one Map.get) so the
      // density halo paint expression can read `["get","schoolDensity"]`.
      const density = getSchoolDensity(v.id);
      // Tradeoff signal — derived once from verdict + density + safety.
      // Returns `{ hasTradeoff: false, tradeoffCase: "" }` until the
      // safety feed is ready, so a refresh after `whenSafetyReady`
      // resolves is what flips the ⚠ icon on. NEVER altered by priority
      // or shortlist — the tradeoff is a property of the suburb, not of
      // the user's filter.
      const tradeoff = tradeoffFeatureProps({ verdict: v, city: cityId });
      return {
        type: "Feature" as const,
        geometry: { type: "Point" as const, coordinates: v.coord },
        properties: {
          id: v.id,
          name: v.name,
          verdict: v.verdict,
          fitTier: fit.tier,
          fitScore: fit.score,
          fitColor: fit.haloColor,
          fitLabel: fit.label,
          isTopMatch: isTop,
          isShortlisted,
          // True when *any* shortlist is active; the dot/glow use this to
          // decide whether to dim non-shortlisted pins. We write it as a
          // per-feature prop (same value on every feature) because MapLibre
          // paint expressions can't easily reference "number of features",
          // but they can freely reference feature properties.
          anyShortlist: shortlistIdSet.size > 0,
          // True when a priority filter (school / commute / lifestyle / etc.)
          // is engaged — drives the same dim mechanic as anyShortlist so
          // non-top-match pins fade out, focusing the user's eye on the
          // top picks per the user's 2026-05-08 request: "when suburb
          // filters are selected, the top picks of the filter [stay] as it
          // is and dim every other which doesn't fall in the filter."
          anyPriorityActive: priority !== "none",
          // School-density signal — "high" / "medium" / "low" — drives
          // the density halo's radius/opacity expressions below. Pure
          // descriptive metadata; verdict colour expression does not
          // read this.
          schoolDensity: density.level,
          schoolDensityCount: density.count,
          // Tradeoff flag — drives the ⚠ symbol layer's filter. Single
          // boolean per feature; `tradeoffCase` carries the *which* so
          // future tooltip/click logic can branch without re-deriving.
          hasTradeoff: tradeoff.hasTradeoff,
          tradeoffCase: tradeoff.tradeoffCase,
        },
      };
    });
  };

  map.addSource("hyperlocal-verdicts", {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: buildFeatures(getPriority()),
    },
  } as any);

  // VERDICT PIN PALETTE — Apple HIG system colours. Vivid enough to pop
  // on the dark basemap, refined enough to feel designed (vs neon).
  // ~140°/35°/4° hue separation survives the most common colour-blindness
  // profiles; the orange-leaning amber keeps green/amber distinguishable.
  const colorExpr: any = [
    "match",
    ["get", "verdict"],
    "green", "#15803D",
    "amber", "#B45309",
    "red",   "#B91C1C",
    "#888888",
  ];

  // PRIORITY-FIT RING / HALO — REMOVED.
  //
  // History: earlier revisions drew a soft blurry priority "halo" AND a
  // crisp priority "ring" around each top-match pin, colour-keyed per
  // priority (teal / green / coral). Combined with the always-on verdict
  // glow beneath, top-match pins rendered as stacked concentric rings
  // that dominated the map visually and read as crosshairs/targets
  // instead of "these five are your best matches".
  //
  // Even collapsed to a single crisp ring, the teal/green/coral stroke
  // still fought the basemap's muted aesthetic and drew the eye away
  // from the verdict colour inside the dot — the map lost its quiet
  // "ground reality" feel.
  //
  // How users now discover top matches without the ring:
  //   1. The Picks FAB badge tells them exactly how many picks exist,
  //      and the Picks page lists them by name in rank order.
  //   2. Non-shortlisted pins fade to ~35 % opacity (see dotOpacity-
  //      Expr / glowOpacityExpr below). Picks stay full-bright — same
  //      signal, no extra geometry on the map.
  //   3. Tapping a pick card in the Picks page or a pin on the map
  //      opens the verdict card, which prints LENS · PRIORITY badges
  //      so users see WHY it matched.
  //
  // Net: the map is just coloured dots again, the way a map should be.

  // SHORTLIST FADE EXPRESSION
  // ---------------------------
  // When any shortlist is active (anyShortlist=true), non-shortlisted pins
  // dim to ~30% of their normal opacity so the user's eye locks onto the
  // picks. Shortlisted pins (and all pins when no shortlist is active) stay
  // at full opacity. Color is NEVER touched — only alpha — so a Red pin that
  // sits in a faded state is still unmistakably red, just quieter.
  // ALWAYS-VISIBLE CONTRACT (2026-05-07): the user's load-bearing
  // requirement is that every suburb pin remains visible at every
  // zoom AND in every shortlist state. Earlier the non-shortlisted
  // dim was 0.35 which read as "missing" against the busy SimCity
  // basemap. We now hold non-shortlisted at 0.85 so the dim is still
  // perceptible (eye locks onto the 0.98 picks) but the dot itself
  // never disappears.
  // Match condition: a pin is "matched" if ALL active filters consider
  // it in. A pin is OUT when EITHER (a) shortlist active and not in
  // shortlist, OR (b) priority active and not a top match. When out,
  // dim hard so the user's eye locks onto the kept set.
  // 0.28 chosen empirically: still visible (Red dots stay readable as
  // Red even when faded), but the gap between matched and dimmed reads
  // unmistakably as "these are the picks, those are context."
  // 2026-05-08: user wants non-picks DIM but still solid (not see-
  // through). Earlier 0.28 read as "transparent / missing"; flat 1.0
  // killed the picks-vs-context signal. 0.55 is the compromise — well
  // above the ~0.4 perceptual transparency threshold so the dot still
  // reads as a solid coloured pin, but visibly faded against picks at
  // 1.0 so the eye still locks onto the matched set when a shortlist
  // or priority filter is on. With no filter active, the case below
  // resolves to 1.0 for every pin.
  // 2026-05-09: user's explicit ask is "dim every other which doesn't
  // fall in the filter" so the top picks visually own the screen. The
  // earlier 1.0-flat policy made non-picks indistinguishable from picks
  // (the user's "even dim suburbs don't look dim" complaint). Now:
  //   • No filter active → every dot at 1.0 (the designed-map baseline)
  //   • Filter active and dot is OUT (not shortlisted, not top-match) →
  //     drop to 0.45 — visibly dim against picks at 1.0, but the dot is
  //     still solid enough to read as "context" not "missing"
  // 0.45 sits well above the ~0.4 perceptual transparency threshold so
  // a dimmed dot still reads as a coloured pin, not a ghost. Combined
  // with the now-zero school-density halo and rgba-zero stroke on
  // non-picks, the picks-vs-context gap is unmistakable.
  const dotOpacityExpr: any = [
    "case",
    [
      "any",
      ["all", ["get", "anyShortlist"], ["!", ["get", "isShortlisted"]]],
      ["all", ["get", "anyPriorityActive"], ["!", ["get", "isTopMatch"]]],
    ],
    0.45,
    1.0,
  ];
  // The glow's shortlist-fade is folded into the zoom-aware opacity ramp
  // below (see glowZoomOpacity) — the standalone expression is no longer
  // needed.

  // Outer soft glow — amplified so pins stay obvious on a busy basemap.
  // This is the verdict-driven glow that sits *above* the priority halo,
  // so safety colour is always the dominant ring even when fit is strong.
  //
  // ALWAYS-VISIBLE CONTRACT (2026-05-07): suburb verdicts must be
  // perceptible at every zoom level. Earlier curves tapered the glow
  // toward invisibility at city-overview to avoid 60+ pins fusing into
  // a candy-bowl, but a user opening the map without zooming in saw
  // no Red/Green/Amber signal at all. We now hold the glow at
  // perceptible opacity (≥0.18) and visible radius (≥6) at every zoom
  // stop, accepting denser overlap at city-overview as the necessary
  // trade to keep the colour story always-on.
  //
  // SIMCITY BASEMAP RECALIBRATION: kept the z>=11 bump (0.32 / 0.40)
  // so the halo dominates the saturated zoning + amber roads + textured
  // buildings + radar pulse overlay at decision zoom.
  //   z6  — radius 6,  opacity 0.18   (always-visible at city overview)
  //   z9  — radius 10, opacity 0.24
  //   z11 — radius 16, opacity 0.32   (decision-zoom; halo dominates)
  //   z14 — radius 30, opacity 0.40   (close-in; full presence)
  // The shortlist-fade gate is honoured at every zoom stop.
  // Glow honours the same OR-of-filters dim as the dot. Keep the glow
  // dim factor a touch softer than the dot (0.30 vs 0.28) — the glow
  // is already low-alpha, dropping it further makes it disappear.
  const glowZoomOpacity = (full: number): any => [
    "case",
    [
      "any",
      ["all", ["get", "anyShortlist"], ["!", ["get", "isShortlisted"]]],
      ["all", ["get", "anyPriorityActive"], ["!", ["get", "isTopMatch"]]],
    ],
    full * 0.30, // hard fade so the picks visibly dominate
    full,
  ];
  // Apple-Maps-aligned: this layer reads as a soft drop shadow under the
  // dot, not a saturated colored halo. Smaller radius, lower opacity, more
  // blur — the colored dot above it carries the signal; the shadow just
  // anchors it to the map.
  map.addLayer({
    id: "hyperlocal-verdict-glow",
    type: "circle",
    source: "hyperlocal-verdicts",
    paint: {
      "circle-radius": [
        "interpolate", ["linear"], ["zoom"],
        6, 4,
        9, 6,
        11, 9,
        14, 14,
      ],
      "circle-color": colorExpr,
      // Asymmetric: picks get a punchy halo (the helper passes the value
      // straight through for matched), non-picks get the value scaled
      // by 0.30 — that gap is what tells the eye "these are your picks"
      // when every dot is solid at 1.0.
      "circle-opacity": [
        "interpolate", ["linear"], ["zoom"],
        6, glowZoomOpacity(0.30),
        9, glowZoomOpacity(0.36),
        11, glowZoomOpacity(0.50),
        14, glowZoomOpacity(0.60),
      ],
      "circle-blur": 1.2,
    },
  });

  // SCHOOL-DENSITY HALO
  // -------------------
  // A subtle desaturated ring whose RADIUS encodes how many schools we
  // have on file for this suburb. Sits between the verdict glow (above)
  // and the solid dot (below) so that:
  //   • Verdict colour remains the loudest signal — this halo is grey-
  //     blue, never green/amber/red.
  //   • Stacked above the verdict glow it'd compete with safety colour;
  //     stacked below the dot it disappears at decision zoom. The
  //     between position is the only place it reads as "second ring".
  //
  // We deliberately encode density via radius+opacity, not colour, so:
  //   • Colour-blind users still get the size cue.
  //   • The pin never reads as "extra warning" — it stays an info halo.
  //
  // Low density (0–1 schools) → halo NOT drawn (opacity 0). The dataset
  // is curated so absence often means "no data here yet" rather than
  // "no schools" — drawing a halo for those would imply a confidence we
  // don't have. The verdict card surfaces the coverage caveat in copy.
  // ZOOMED-OUT SPACING: school-density is a decision-detail signal — at
  // city-overview zoom (z<=10.5) it just adds a second blue-grey ring
  // behind every dot, doubling the visual weight per pin and crushing
  // any breathing room between neighbours. We now hold it at zero radius
  // / zero opacity below z10.5 and ramp it in over a 1.5-zoom window so
  // it appears smoothly as the user pulls in past city overview.
  const densityHaloRadius: any = [
    "interpolate", ["linear"], ["zoom"],
    10.5, 0,
    11, [
      "match", ["get", "schoolDensity"],
      "high", 5,
      "medium", 4,
      0,
    ],
    14, [
      "match", ["get", "schoolDensity"],
      "high", 11,
      "medium", 9,
      0,
    ],
  ];
  // 2026-05-08: USER REQUESTED REMOVAL of this halo. Even at the lowest
  // dim opacity (0.06) it stayed visible enough that "dimmed" pins still
  // read as bright, defeating the purpose of the filter-driven dim. We
  // hold the layer in place (so the verdict-layer-order stack and the
  // moveLayer calls don't need to be touched) but hard-zero the opacity.
  // If we ever want to re-introduce school density as a visual signal,
  // restore the previous interpolate expression here.
  const densityHaloOpacity: any = 0;
  map.addLayer({
    id: "hyperlocal-verdict-school-halo",
    type: "circle",
    source: "hyperlocal-verdicts",
    paint: {
      "circle-radius": densityHaloRadius,
      // Cool muted blue-grey — distinct from verdict palette and from
      // the warm shortlist amber stroke. Deliberately desaturated so it
      // reads as "info" not "alert".
      "circle-color": "#9FB7C8",
      "circle-opacity": densityHaloOpacity,
      "circle-blur": 0.55,
      // Crucially: NO stroke. A stroked ring would compete with the
      // shortlist amber stroke on the dot above it.
      "circle-stroke-width": 0,
    },
  });

  // Solid dot — visible at Hyderabad zoom (11) without zooming in.
  //
  // Shortlisted pins get a SUBTLE amber ring (stroke) + marginally
  // larger radius so they read as "yours" without dominating the map.
  // The non-shortlisted dim (dotOpacityExpr) is the primary signal; the
  // stroke is a nudge — small enough that zooming out past z10 the
  // ring visually disappears into the dot, large enough at decision-
  // zoom (z11–14) to pick out at a glance.
  // ALWAYS-VISIBLE STROKE (2026-05-07): the prior dark-graphite stroke
  // (#0F0F0F) disappeared into the SimCity navy background, leaving the
  // dot edges fuzzy and the pin reading as a smudge against busy roads.
  // We now use a near-white cream stroke for non-picks (high-contrast
  // outline against ANY backdrop — dark canvas, bright orange road,
  // saturated zoning), and the warm amber #FFD37E for picks.
  // STROKE COLOR — only picks (shortlist OR top-match for the active
  // priority) get an amber accent ring. The previous always-on white
  // rim was acting as a uniform halo that made dimmed pins still read
  // as bright (the user's 2026-05-08 complaint). Now: matched pins
  // glow amber, dimmed pins have NO stroke at all (rgba(0,0,0,0)).
  const isMatched: any = [
    "any",
    ["get", "isShortlisted"],
    ["all", ["get", "anyPriorityActive"], ["get", "isTopMatch"]],
  ];
  // Per-verdict stroke colour. The cream/amber #FFD37E reads as a clear
  // "pick" ring against green and red dots, but on AMBER dots the cream
  // and the dot share the same warm-yellow hue family — the ring blends
  // into the dot and matched amber pins look identical to non-matched
  // ones. Amber-verdict matches therefore get a pure-white stroke for
  // maximum luminance contrast against the warm dot; green and red keep
  // the cream stroke that ties the highlight to the warm-amber pick
  // language used elsewhere in the UI.
  const shortlistStrokeColor: any = [
    "case",
    isMatched,
    [
      "match", ["get", "verdict"],
      "amber", "#FFFFFF",
      "#FFD37E",
    ],
    "rgba(0,0,0,0)", // dimmed pins: no stroke
  ];
  // STROKE WIDTH — picks (shortlist OR top-match for active priority)
  // get a thicker amber ring; dimmed pins get a hairline (will be
  // invisible because the stroke color is rgba(0,0,0,0)). The picks'
  // ring widens with zoom so the highlight reads at decision zoom.
  // Apple-style stroke: thin, deliberate. Picks ~1.4× wider so the
  // highlight is unmistakable on the saturated SimCity backdrop.
  const shortlistStrokeWidth: any = [
    "interpolate", ["linear"], ["zoom"],
    6, ["case", isMatched, 1.2, 0.6],
    9, ["case", isMatched, 2.0, 0.7],
    11, ["case", isMatched, 2.5, 0.9],
    14, ["case", isMatched, 3.0, 1.0],
  ];
  // ALWAYS-VISIBLE CONTRACT (2026-05-07): the user requires verdict
  // dots to remain visible at every zoom level. Earlier curves shrank
  // dots to ~2-3 px at city-overview to avoid the 60+ HYD pins fusing
  // into a candy-bowl, but the trade-off was that opening the map and
  // not zooming in showed no Red/Green/Amber signal. We now hold the
  // non-shortlist dot at ≥5 px from z6 upward; clumping at z<=8 is
  // accepted as the necessary cost of always-on colour.
  //
  // SIMCITY BASEMAP RECALIBRATION: decision-zoom radii (z11/z14) keep
  // the earlier bump so verdict colour beats the textured-building
  // pattern + saturated zoning + bright amber roads at z11+ territory.
  // The shortlist delta is preserved at every stop so picks always
  // read as ~1.2-1.5× larger than non-picks.
  // RADIUS — matched pins (shortlist OR top-match for the active
  // priority filter) read ~1.3× larger than peers so size + amber
  // stroke compound into a clear "this is your pick" signal. Both
  // signals reuse the `isMatched` flag so the highlight tracks
  // whatever filter context is active.
  // 2026-05-08: with all dots now solid (no opacity dim for non-picks),
  // size + stroke + glow have to carry pick-vs-context differentiation
  // alone. Pushed pick radii to ~1.7x non-picks at decision zooms so
  // the matched set is unmistakable when a priority filter is on.
  const shortlistRadius: any = [
    "interpolate", ["linear"], ["zoom"],
    6, ["case", isMatched, 5, 3],
    8, ["case", isMatched, 9, 5],
    9, ["case", isMatched, 11, 6],
    11, ["case", isMatched, 14, 8],
    14, ["case", isMatched, 17, 10],
  ];
  map.addLayer({
    id: "hyperlocal-verdict-dot",
    type: "circle",
    source: "hyperlocal-verdicts",
    paint: {
      "circle-radius": shortlistRadius,
      "circle-color": colorExpr,
      "circle-stroke-color": shortlistStrokeColor,
      "circle-stroke-width": shortlistStrokeWidth,
      "circle-opacity": dotOpacityExpr,
    },
  });

  map.addLayer({
    id: "hyperlocal-verdict-label",
    type: "symbol",
    source: "hyperlocal-verdicts",
    minzoom: 12,
    layout: {
      "text-field": ["get", "name"],
      "text-font": ["Open Sans Regular"],
      // Smaller-then-grow lets labels appear without crowding the moment
      // they cross the minzoom threshold, then bloom to full size by z14.
      // Apple-style label: tighter type, closer to the dot now that the
      // dot is half the size. Same minzoom 12 gate so labels still wait
      // until decision zoom to appear.
      "text-size": ["interpolate", ["linear"], ["zoom"], 12, 9, 14, 10.5],
      "text-offset": [0, 0.9],
      "text-anchor": "top",
      // Collision-avoidance: 4 px padding around each label + text-optional
      // means dense neighbourhoods (Banjara/Jubilee, Carlton/Fitzroy) drop
      // overlapping labels gracefully instead of stacking on top of each
      // other. The pin underneath stays — only the *label* is optional.
      "text-padding": 4,
      "text-optional": true,
      "text-allow-overlap": false,
    },
    paint: {
      "text-color": "#F1F1F1",
      "text-halo-color": "#0F0F0F",
      "text-halo-width": 1.4,
    },
  });

  // TRADEOFF ⚠ INDICATOR
  // ---------------------
  // A small warning glyph in the top-right corner of any verdict pin
  // whose verdict + density + safety contradict each other (see
  // src/data/tradeoff.ts for the three cases). Deliberately understated:
  //
  //   • Single ⚠ glyph at text-size 11 — barely larger than the verdict
  //     name label so it never out-shouts the pin's colour.
  //   • Warm amber (#FFB347) — same hue as the shortlist stroke, which
  //     keeps the map's secondary palette to two tokens (amber + cool
  //     grey-blue density halo). NOT red — red would imply "danger" and
  //     overlap with the verdict-red pins; the tradeoff is "your
  //     assumption may not hold", not "do not enter".
  //   • Top-right offset (+0.85, -0.85 ems) — sits clear of the dot, the
  //     density halo, AND the bottom-anchored name label.
  //   • Filtered to features where `hasTradeoff === true`. When safety
  //     hasn't loaded yet, ALL features carry hasTradeoff=false (see
  //     tradeoffFeatureProps) so the layer is invisible until init
  //     resolves and the source is refreshed.
  //   • Allow-overlap is true so adjacent pins don't drop the icon
  //     under collision; with at most 3 cases in ~60 pins the worst-
  //     case visible count is single-digit.
  map.addLayer({
    id: "hyperlocal-verdict-tradeoff",
    type: "symbol",
    source: "hyperlocal-verdicts",
    // ZOOMED-OUT SPACING: \u26A0 glyphs at city-overview pile on top of the
    // already-clumped dots and read as random crosses scattered across
    // the canvas. Tradeoff is a decision-zoom signal \u2014 gate the layer
    // to z>=11 so it only appears once the user is studying individual
    // suburbs.
    minzoom: 11,
    filter: ["==", ["get", "hasTradeoff"], true],
    layout: {
      "text-field": "\u26A0",
      "text-font": ["Open Sans Regular"],
      // Smaller glyph, tighter offset \u2014 the dot is half the size it used
      // to be, so the warning needs to live correspondingly closer.
      "text-size": 9,
      "text-offset": [0.6, -0.6],
      "text-anchor": "center",
      "text-allow-overlap": true,
      "text-ignore-placement": true,
    },
    paint: {
      "text-color": "#FFB347",
      "text-halo-color": "#0F0F0F",
      "text-halo-width": 1.4,
      // Mirror the dot's shortlist-fade so a tradeoff icon on a faded
      // (non-shortlisted) pin doesn't pop while the pin itself is
      // dimmed. Same gate as `dotOpacityExpr`.
      "text-opacity": [
        "case",
        ["all", ["get", "anyShortlist"], ["!", ["get", "isShortlisted"]]],
        0.4,
        1.0,
      ],
    },
  });

  // ALWAYS-ON-TOP ENFORCEMENT (2026-05-07):
  // Other modules (sim-buildings, traffic-streaks, landmark-billboards,
  // radar-pulse, chrome-shell) install layers AFTER this one and can
  // visually overlap or occlude verdict pins. We re-stack verdict
  // layers to the top — but ONLY when they're not already on top, to
  // avoid an infinite styledata loop (moveLayer itself fires styledata).
  const VERDICT_LAYER_ORDER = [
    "hyperlocal-verdict-glow",
    "hyperlocal-verdict-school-halo",
    "hyperlocal-verdict-dot",
    "hyperlocal-verdict-label",
    "hyperlocal-verdict-tradeoff",
  ];
  let promoteScheduled = false;
  const promoteVerdictLayersToTop = () => {
    if (promoteScheduled) return;
    const layers = map.getStyle().layers;
    if (!layers || !layers.length) return;
    // Idempotency check: if the verdict layers are already the last 5
    // entries in their canonical order, skip the moveLayer pass.
    const tail = layers.slice(-VERDICT_LAYER_ORDER.length).map((l: any) => l.id);
    let alreadyOnTop = true;
    for (let i = 0; i < VERDICT_LAYER_ORDER.length; i++) {
      if (tail[i] !== VERDICT_LAYER_ORDER[i]) { alreadyOnTop = false; break; }
    }
    if (alreadyOnTop) return;
    promoteScheduled = true;
    // Defer to the next tick so the styledata event that triggered us
    // finishes propagating before we mutate the style again.
    setTimeout(() => {
      try {
        for (const id of VERDICT_LAYER_ORDER) {
          try { if (map.getLayer(id)) map.moveLayer(id); } catch { /* removed */ }
        }
      } finally {
        promoteScheduled = false;
      }
    }, 0);
  };
  promoteVerdictLayersToTop();
  map.on("styledata", promoteVerdictLayersToTop);

  // PIN-COLOUR IMMUTABILITY TRIPWIRE
  // ---------------------------------
  // Suburb-pin colour is a hard contract: green / amber / red driven by the
  // hand-curated `verdict` field, and NEVER varies by time, priority,
  // shortlist state, fresh incident counts, event load, or anything else.
  // Users learn the colour map at first glance and rely on it as a stable
  // anchor — silently shifting it (e.g. red → amber after a quiet week)
  // would destroy that trust. Opacity may dim, halos may add/remove, but
  // the pin colour itself is locked.
  //
  // This wrapper traps any future code path that tries to mutate
  // `circle-color` on the verdict layers via setPaintProperty. It logs
  // loudly and swallows the call so the static `colorExpr` keeps winning.
  // Other paint properties (opacity, radius, stroke) pass through normally.
  const VERDICT_COLOUR_LOCKED_LAYERS = new Set([
    "hyperlocal-verdict-dot",
    "hyperlocal-verdict-glow",
  ]);
  const VERDICT_COLOUR_LOCKED_PROPS = new Set([
    "circle-color",
  ]);
  const _origSetPaint = map.setPaintProperty.bind(map);
  (map as any).setPaintProperty = (layerId: string, prop: string, value: unknown, opts?: any) => {
    if (
      VERDICT_COLOUR_LOCKED_LAYERS.has(layerId) &&
      VERDICT_COLOUR_LOCKED_PROPS.has(prop)
    ) {
      console.error(
        `[HYPERLOCAL] BLOCKED attempt to mutate ${prop} on ${layerId} — verdict pin colour is contractually locked to the static green/amber/red expression. If you genuinely need to vary visual emphasis on these layers, use opacity, radius, stroke, or a separate overlay layer.`
      );
      return map;
    }
    return _origSetPaint(layerId, prop, value as any, opts);
  };

  // Live priority-fit refresh. When the user flips the Priority pill the
  // halo must recompute per-suburb. Base `verdict` property is NEVER touched
  // by this subscription — only the fit-derived properties change, so the
  // core safety colour of every pin is preserved across priority flips.
  const refreshFitOnPriorityChange = subscribePriority((p) => {
    const src = map.getSource("hyperlocal-verdicts") as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!src) return;
    src.setData({
      type: "FeatureCollection",
      features: buildFeatures(p) as any,
    } as any);
  });

  // Live shortlist refresh. When lens/priority/budget change, the shortlist
  // panel recomputes its IDs and broadcasts; we rebuild features so that
  // `isShortlisted` / `anyShortlist` flip per-feature and the fade expression
  // re-evaluates. `verdict` and `fitColor` are unchanged by this path.
  const refreshOnShortlistChange = subscribeShortlist(() => {
    const src = map.getSource("hyperlocal-verdicts") as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!src) return;
    src.setData({
      type: "FeatureCollection",
      features: buildFeatures(getPriority()) as any,
    } as any);
  });

  // Safety-ready refresh. Initial buildFeatures() ran while the incident
  // feed was still loading, so every feature got `hasTradeoff: false`
  // (the safe default — see tradeoffFeatureProps when safety.ready is
  // false). Once the feed resolves, re-stamp so any pin whose tradeoff
  // case fires can render its ⚠ glyph. Single fire-and-forget refresh —
  // no subscription needed because safety counts are immutable per city.
  whenSafetyReady(cityId).then(() => {
    const src = map.getSource("hyperlocal-verdicts") as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!src) return;
    src.setData({
      type: "FeatureCollection",
      features: buildFeatures(getPriority()) as any,
    } as any);
  }).catch(() => {
    // Safety feed failed — leave the map in its no-tradeoff state.
    // The verdict card surfaces "(checking…)" copy in this branch so
    // the user knows the absence of a signal isn't a positive signal.
  });

  // Release subscriptions if the map is ever torn down (future-proofing; the
  // current boot code keeps one map for the page lifetime, so these handlers
  // are held but never invoked).
  map.on("remove", () => {
    refreshFitOnPriorityChange();
    refreshOnShortlistChange();
  });

  map.on("mouseenter", "hyperlocal-verdict-dot", () => {
    map.getCanvas().style.cursor = "pointer";
  });
  map.on("mouseleave", "hyperlocal-verdict-dot", () => {
    map.getCanvas().style.cursor = "";
  });

  // Direct pin tap.
  map.on("click", "hyperlocal-verdict-dot", (e) => {
    const f = e.features && e.features[0];
    if (!f) return;
    const id = f.properties?.id as string | undefined;
    const v = verdicts.find((x) => x.id === id);
    if (!v) return;

    // Compare-mode intercept. The shared compare-state module drives the
    // transition + notifies the pill/shortlist/popup subscribers.
    const cs = getCompareState();
    if (cs.mode === "waitA") {
      selectCompareA(v);
      console.log("[HYPERLOCAL] compare: A selected:", v.id, "(city:", cityLabel, ")");
      flyToSuburb(map, v.coord, 12);
      return;
    }
    if (cs.mode === "waitB") {
      if (cs.a && cs.a.id === v.id) {
        console.log("[HYPERLOCAL] compare: B same as A, ignoring");
        return;
      }
      console.log(
        "[HYPERLOCAL] compare: B selected:",
        v.id,
        "—> opening compare card for",
        cs.a?.id,
        "vs",
        v.id
      );
      selectCompareB(v, verdicts);
      return;
    }

    // Default flow: single-tap verdict card.
    console.log("[HYPERLOCAL] pin tap:", v.id, "city:", cityId, "dataset:", cityLabel);
    showVerdict(v);
    flyToSuburb(map, v.coord);
  });

  // Empty-space tap -> nearest-within-3km verdict, else "no verdict yet" toast.
  map.on("click", (e) => {
    const hits = map.queryRenderedFeatures(e.point, {
      layers: ["hyperlocal-verdict-dot"],
    });
    if (hits.length > 0) return; // pin handler already fired
    // Signal marks are GPU features now (incident-bubbles), not DOM markers
    // that swallow the click — so this handler must stand down when the tap
    // lands on one, or every signal tap would ALSO open the nearest verdict.
    // Namespace contract: every signal layer id starts with
    // "hyperlocal-signals-" (see SIGNAL_LAYER_IDS in ui/incident-bubbles.ts;
    // not imported here so the deferred chunk stays out of the boot bundle).
    // The 12px box mirrors the signal layer's own finger-sized hit test;
    // the ambient area halo only claims taps at scanning zooms.
    const signalLayers = (map.getStyle()?.layers ?? [])
      .map((l) => l.id)
      .filter((id) => id.startsWith("hyperlocal-signals-"));
    if (signalLayers.length > 0) {
      try {
        const sig = map.queryRenderedFeatures(
          [
            [e.point.x - 12, e.point.y - 12],
            [e.point.x + 12, e.point.y + 12],
          ],
          { layers: signalLayers }
        );
        const claimed = sig.some(
          (f) =>
            f.layer.id !== "hyperlocal-signals-area-halo" ||
            map.getZoom() <= 13.5
        );
        if (claimed) return; // the signal layer owns this tap
      } catch {
        /* style mid-change — fall through to the verdict tap */
      }
    }
    const v = nearFn(e.lngLat.lng, e.lngLat.lat, 3);

    // Compare-mode intercept on near-tap. Same semantics as pin tap so the
    // user can sloppy-tap near a glowing pin.
    const nearCs = getCompareState();
    if (nearCs.mode === "waitA" && v) {
      selectCompareA(v);
      console.log("[HYPERLOCAL] compare (near-tap): A selected:", v.id, "(city:", cityLabel, ")");
      flyToSuburb(map, v.coord, 12);
      return;
    }
    if (nearCs.mode === "waitB" && v) {
      if (nearCs.a && nearCs.a.id === v.id) {
        console.log("[HYPERLOCAL] compare (near-tap): B same as A, ignoring");
        return;
      }
      console.log(
        "[HYPERLOCAL] compare (near-tap): B selected:",
        v.id,
        "—> opening compare card for",
        nearCs.a?.id,
        "vs",
        v.id
      );
      selectCompareB(v, verdicts);
      return;
    }

    if (v) {
      console.log(
        "[HYPERLOCAL] nearest-snap:",
        v.id,
        "city:", cityId,
        "dataset:", cityLabel
      );
      showVerdict(v);
    } else {
      console.log(
        "[HYPERLOCAL] no verdict within 3km of",
        e.lngLat,
        "city:", cityId,
        "dataset:", cityLabel
      );
      showNoVerdict(cityLabel);
    }
  });
}

// ---- First-tap guidance halo ---------------------------------------------
// Non-interactive visual layer only. Draws a soft amber halo + crisp ring on
// 3–5 hand-picked starter areas per city so a first-time user's eye has a
// clear place to land. Auto-dismisses on first map tap or after 12 s and is
// remembered via sessionStorage so subsequent reloads stay clean.
//
// CRITICAL: this function does NOT touch any click handler, layer other than
// its own, or any verdict / compare state. It is a purely decorative overlay
// that removes itself cleanly.

function installSuggestedHighlight(
  map: maplibregl.Map,
  verdicts: Verdict[],
  cityId: CityId
): void {
  if (sessionStorage.getItem(SUGGESTED_FLAG)) {
    console.log("[HYPERLOCAL] suggested highlight: skipped (seen this session)");
    return;
  }
  const want = new Set(SUGGESTED_IDS[cityId] || []);
  const features = verdicts
    .filter((v) => want.has(v.id))
    .map((v) => ({
      type: "Feature" as const,
      geometry: { type: "Point" as const, coordinates: v.coord },
      properties: { id: v.id, name: v.name },
    }));
  if (features.length === 0) {
    console.log(
      "[HYPERLOCAL] suggested highlight: no matching IDs for city",
      cityId
    );
    return;
  }
  console.log(
    "[HYPERLOCAL] suggested highlight: city",
    cityId,
    "· featuring",
    features.length,
    "areas"
  );

  map.addSource("hyperlocal-suggested", {
    type: "geojson",
    data: { type: "FeatureCollection", features },
  } as any);

  // Wide translucent amber halo — the "glow" the eye picks up from a
  // distance. Sits above the verdict glow but below any text labels.
  map.addLayer({
    id: "hyperlocal-suggested-halo",
    type: "circle",
    source: "hyperlocal-suggested",
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 11, 14, 23],
      "circle-color": "#FFD37E",
      "circle-opacity": 0.18,
      "circle-blur": 0.8,
    },
  });

  // Crisp inner ring — gives the halo a defined edge, so suggested pins feel
  // "called out" rather than "fuzzy". Transparent fill so the dot beneath
  // stays fully visible and clickable.
  map.addLayer({
    id: "hyperlocal-suggested-ring",
    type: "circle",
    source: "hyperlocal-suggested",
    paint: {
      "circle-radius": ["interpolate", ["linear"], ["zoom"], 9, 6.5, 14, 11],
      "circle-color": "rgba(0,0,0,0)",
      "circle-stroke-color": "#FFD37E",
      "circle-stroke-width": 1.6,
      "circle-stroke-opacity": 0.85,
    },
  });

  // ---- Hint banner — REMOVED 2026-05-03 ----
  // Per user feedback: the "5 areas to start in <city>" banner anchored
  // bottom-centre was obstructing the map without earning its pixels.
  // The pulsing halo + ring on each suggested pin (hyperlocal-suggested-
  // halo / hyperlocal-suggested-ring layers above) already give the visual
  // call-out; the banner copy was redundant. Bubble layers stay so the
  // first-tap guidance still pulses on the map itself; only the banner
  // DOM + its inline <style> are gone. Original copy preserved in git
  // history if a smaller/inline chip is wanted later.

  // Halo + ring are now static — fixed radius/opacity in the paint blocks
  // above, no RAF pulse, no first-tap or timeout dismissal. The session
  // flag (`SUGGESTED_FLAG`) is still consulted at function entry so future
  // code paths can opt out, but nothing in this module sets it any more.
}

// ---- Boot -----------------------------------------------------------------

/**
 * Inject a single stylesheet that fixes mobile-specific layout issues for
 * controls that were built inline. Touches only the "edge" issues that don't
 * warrant rewriting a component wholesale:
 *   - Status banner: ellipsis + tighter max-width
 *   - FPS badge: hidden (noisy on small screens)
 *   - First-visit hint: re-anchored below the status banner
 *   - Hover-only chip highlights: swap to :active/focus-visible on touch
 */
function installMobileStyles(): void {
  if (document.getElementById("hyperlocal-mobile-mq")) return;
  const s = document.createElement("style");
  s.id = "hyperlocal-mobile-mq";
  // Mobile UX philosophy (v2): the map IS the product — everything else is
  // subordinate. Default mobile state hides every filter panel and leaves
  // only two things on screen: the map, and a single ⚙ button top-right that
  // expands an inline filter drawer. The drawer slides down under the city
  // toggle and contains Who is this for / Priority / Mode / Type as tiny
  // inline dropdown-styled controls. When closed (the default), the map is
  // completely unobstructed.
  //
  // Compare + School chart pills become icon-only (⇄ / 📊) and dock to
  // bottom-left so they don't collide with the drawer.
  //
  // Attribution collapses to the MapLibre (i) affordance only — the full
  // "© CARTO · © OpenStreetMap" text is hidden; users tap (i) to see it.
  s.textContent = `
    @media (max-width: 720px), (max-height: 500px) {
      /* -------- Hide dev noise + anything a non-technical user can't read --------
         The status banner ("HYD · 60") reads as jargon — "60" is dataset size,
         not a user-facing metric. The city toggle already tells users where
         they are, so the banner is pure chrome that takes space away from the
         map. Hide it on mobile. */
      #hyperlocal-fps-badge,
      #status-banner { display: none !important; }

      /* -------- City toggle: small top-right --------
         Pre-Apr 2026 we squeezed the toggle into right:92px to leave room
         for the "Filters" button on the same y-row. After renaming to
         "Suburb filter" the right-side button got wider and overlapped the
         toggle (truncated label visible in screenshot). Fix: stack them
         vertically — toggle at top:12, filter at top:54 (rule below). */
      #hyperlocal-city-toggle {
        top: 12px !important;
        right: 12px !important;
        gap: 2px !important;
        padding: 3px !important;
      }
      #hyperlocal-city-toggle button {
        padding: 5px 8px !important;
        font-size: 10.5px !important;
        font-weight: 700 !important;
        letter-spacing: 0.04em !important;
      }
      /* (The old 2-city label hack — font-size:0 + ::before "HYD"/"MEL"
         content stamped by child index — was deleted 2026-06-13: it
         mislabelled the 4-city toggle on phones. installCityToggle already
         renders compact 3-letter labels on mobile.)
         (The boot-time lens/priority/budget rails and their drawer-rebirth
         rules are gone too — the Decide page renders its controls directly
         from the stores. docs/ONE_STORY.md.) */

      /* -------- Search bar: shares its row with the Suburb-filter pill --------
         The pill sits at top:54, right:12 (rule above) and renders ~115-130px
         wide on mobile (icon + "Suburb filter" label, capped by font 12px).
         If the search bar reaches the right edge, its rounded corner climbs
         OVER the filter pill (verdict screenshots showed both glued together
         with the filter sitting on top of the search). Reserving 140px on the
         right gives the filter a clear lane (115 wide + 12 right inset + ~13
         breathing gap). At 320px viewports the search still gets ~168px of
         input width, comfortable for typing a suburb name.

         At LANDSCAPE phone (e.g. 812x375 — height triggers this mobile
         block via the (max-height:500px) clause), the previous
         "width:auto; max-width:calc(100vw - 152px)" allowed the bar to
         stretch to ~660px, dominating the top of the map AND covering
         2+ landmarks that happened to fall in that strip. Capping at
         320px gives the search bar a sensible upper bound matching
         the desktop inline style, while keeping the same expansion
         behaviour on narrow phones (Galaxy Fold 280 -> 128px input
         width, comfortable for typing). The 320 cap was chosen to
         match the desktop inline min(320px, calc(100vw - 380px)) —
         same visual ceiling. */
      #hyperlocal-suburb-search {
        top: 48px !important;
        left: 12px !important;
        right: auto !important;
        transform: none !important;
        width: min(320px, calc(100vw - 152px)) !important;
        max-width: 320px !important;
      }
      #hyperlocal-suburb-search input {
        padding: 7px 12px !important;
        font-size: 12px !important;
      }

      /* -------- Attribution: collapse to just the (i) --------
         MapLibre renders the compact attribution as an open details element
         with the sources inside .maplibregl-ctrl-attrib-inner. When the open
         attribute is absent (we remove it via JS after map load on mobile)
         the inner div is hidden by the browser default details styling,
         leaving only the summary which renders as the (i) circle. */
      .maplibregl-ctrl-bottom-right .maplibregl-ctrl-attrib {
        background: transparent !important;
        padding: 0 !important;
        margin: 0 8px 8px 0 !important;
      }
      .maplibregl-ctrl-attrib-button {
        width: 28px !important;
        height: 28px !important;
        background-color: rgba(22,22,22,0.85) !important;
        border-radius: 50% !important;
        backdrop-filter: blur(6px);
        border: 1px solid rgba(255,255,255,0.08);
      }
      /* Belt-and-braces: hide the inner text unless <details open> */
      details.maplibregl-ctrl-attrib:not([open]) .maplibregl-ctrl-attrib-inner {
        display: none !important;
      }
      /* When user taps (i), details[open]=true and inner shows as a small
         popover pill that stays out of the way. */
      details.maplibregl-ctrl-attrib[open] .maplibregl-ctrl-attrib-inner {
        background: rgba(22,22,22,0.92);
        border-radius: 6px;
        padding: 5px 9px;
        font-size: 10px;
        color: #cfcfcf;
        margin-right: 34px;
        white-space: nowrap;
      }
    }

    /* Touch devices — no hover feedback. */
    @media (hover: none) {
      button, [role="button"] {
        -webkit-tap-highlight-color: transparent;
        touch-action: manipulation;
      }
      button:active, [role="button"]:active {
        filter: brightness(1.12);
      }
    }

    /* When verdict card is open, hide the home chip + left icons + both
       mobile FABs so the bottom sheet doesn't stack with them. Users can
       close the card to reach those controls again. */
    @media (max-width: 720px), (max-height: 500px) {
      body:has(#hyperlocal-verdict-card) #home-chip,
      body:has(#hyperlocal-verdict-card) #hyperlocal-filter-page,
      body:has(#hyperlocal-verdict-card) #hyperlocal-decide-chip {
        display: none !important;
      }
    }

    /* -------- "Decide" chip (opens the Decide page) --------
       Lives inside the dock on every viewport (the chrome shell adopts it
       into the .es-filter-slot and restyles it to the dock chip language).
       The fixed positioning below is only the fallback when the shell
       fails to mount — the .es-bottom override wins once adopted. */
    #hyperlocal-filter-fab {
      display: inline-flex;
      position: fixed;
      bottom: 14px;
      right: 12px;
      z-index: 1310;
      height: 32px;
      padding: 0 12px 0 10px;
      border-radius: 999px;
      background: rgba(33,33,33,0.85);
      backdrop-filter: blur(10px);
      border: 1px solid rgba(255,214,128,0.30);
      color: #FFD37E;
      cursor: pointer;
      align-items: center;
      justify-content: center;
      gap: 6px;
      font: 600 12px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      letter-spacing: 0.02em;
      box-shadow: 0 4px 14px rgba(0,0,0,0.35);
      transition: background 140ms ease, border-color 140ms ease;
    }

    /* (★ Picks FAB + full-screen Picks page deleted 2026-06-13 — picks live
       inside the Decide page's "Your picks" section on every viewport.) */

    /* -------- FULL-SCREEN FILTER PAGE -------- */
    #hyperlocal-filter-page {
      display: none;
      position: fixed;
      inset: 0;
      z-index: 2500;
      background: rgba(12,12,14,0.98);
      backdrop-filter: blur(20px);
      flex-direction: column;
      overflow: hidden;
      -webkit-tap-highlight-color: transparent;
    }
    body[data-mobile-filters="open"] #hyperlocal-filter-page {
      display: flex;
    }

    /* Header: big back arrow (48x48 tap target), centered "Filters" title,
       and a Reset link right-aligned. Follows the pattern a 60-year-old
       recognizes from iOS/Android settings screens. */
    #hyperlocal-filter-page .fp-header {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: calc(env(safe-area-inset-top, 0px) + 10px) 12px 10px;
      border-bottom: 1px solid rgba(255,255,255,0.08);
      background: rgba(18,18,20,0.96);
    }
    #hyperlocal-filter-page .fp-back {
      width: 48px;
      height: 48px;
      background: transparent;
      border: 0;
      color: #f1f1f1;
      font-size: 24px;
      cursor: pointer;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 12px;
    }
    #hyperlocal-filter-page .fp-back:active {
      background: rgba(255,255,255,0.06);
    }
    #hyperlocal-filter-page .fp-title {
      flex: 1;
      min-width: 0;
      margin: 0;
      font: 700 17px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      color: #f1f1f1;
      letter-spacing: -0.01em;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    /* Number chip that sits inside each section heading — replaces the old
       double-label (eyebrow "1 · WHO" + heading "Who's moving in?"). Single
       line, single meaning. Compact 22px circle so each section header
       stays under 20px tall on mobile. */
    #hyperlocal-filter-page .fp-num {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 22px;
      height: 22px;
      border-radius: 50%;
      background: linear-gradient(135deg, rgba(255,214,128,0.22), rgba(255,184,92,0.14));
      color: #FFD37E;
      font: 700 11px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      margin-right: 10px;
      vertical-align: baseline;
      line-height: 1;
      flex-shrink: 0;
    }
    #hyperlocal-filter-page .fp-section h2 {
      display: flex;
      align-items: center;
      gap: 0;
    }
    #hyperlocal-filter-page .fp-reset {
      background: transparent;
      border: 0;
      color: #FFD37E;
      font: 600 14px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      cursor: pointer;
      padding: 10px 14px;
      border-radius: 10px;
    }
    #hyperlocal-filter-page .fp-reset:active {
      background: rgba(255,214,128,0.1);
    }

    /* Body: tight padding so the 5 sections + footer fit a phone viewport
       without scrolling on a 375×812 iPhone (and even a 667px iPhone SE is
       close). Each section is a compact block — big numbered circle, short
       heading, pill control. No per-section hints (the heading is self-
       explanatory); the optional fp-hint paragraph is hidden by default
       and only re-shown if a desktop popup (where vertical room is ample)
       chooses to reveal it via a class override. */
    #hyperlocal-filter-page .fp-body {
      flex: 1;
      overflow-y: auto;
      padding: 4px 16px 96px;
      -webkit-overflow-scrolling: touch;
    }
    #hyperlocal-filter-page .fp-section {
      padding: 10px 0 8px;
      border-bottom: 1px solid rgba(255,255,255,0.05);
    }
    #hyperlocal-filter-page .fp-section:last-of-type {
      border-bottom: 0;
    }
    #hyperlocal-filter-page .fp-section h2 {
      margin: 0 0 8px;
      font: 700 15px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      color: #f1f1f1;
      letter-spacing: -0.01em;
      line-height: 1.1;
    }
    /* Hints hidden by default on mobile (where every pixel counts). The
       heading + pill control is unambiguous on its own — "Buying or
       renting?" above a Buy/Rent row needs no tutorial. Desktop popup
       (min-width: 721px) re-shows them. */
    #hyperlocal-filter-page .fp-hint {
      display: none;
      margin: 0 0 10px;
      font: 13px/1.4 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      color: #8a8a8a;
    }

    /* Footer: sticky "See the map" button. Slimmer padding so the button's
       top edge sits tight against the last section and no wasted band of
       chrome eats into the section real estate. The button itself is the
       hero of the page — amber gradient + subtle glow + arrow = a clear
       "next" affordance that a 50-year-old first-time user reads in < 1s. */
    #hyperlocal-filter-page .fp-footer {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 10px 16px calc(10px + env(safe-area-inset-bottom, 0px));
      background: linear-gradient(to top, rgba(12,12,14,0.98) 55%, rgba(12,12,14,0.80) 100%);
      backdrop-filter: blur(20px);
      border-top: 1px solid rgba(255,255,255,0.06);
    }
    #hyperlocal-filter-page .fp-apply {
      width: 100%;
      height: 48px;
      background: linear-gradient(135deg, #FFE29A 0%, #FFD37E 50%, #FFB85C 100%);
      color: #1a1a1a;
      border: 0;
      border-radius: 12px;
      font: 700 15.5px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      letter-spacing: 0.02em;
      cursor: pointer;
      box-shadow:
        0 6px 18px rgba(255,184,92,0.32),
        inset 0 1px 0 rgba(255,255,255,0.30);
      position: relative;
      overflow: hidden;
      transition: transform 90ms ease, box-shadow 160ms ease;
    }
    #hyperlocal-filter-page .fp-apply::after {
      /* Right-pointing chevron built from two 8px borders — no SVG needed.
         Placed absolutely so it never shifts the button's text baseline. */
      content: "";
      position: absolute;
      right: 22px;
      top: 50%;
      width: 8px;
      height: 8px;
      border-right: 2px solid #1a1a1a;
      border-top: 2px solid #1a1a1a;
      transform: translateY(-50%) rotate(45deg);
      transition: right 160ms ease;
    }
    #hyperlocal-filter-page .fp-apply:hover::after { right: 18px; }
    #hyperlocal-filter-page .fp-apply:active {
      transform: translateY(1px);
      box-shadow:
        0 3px 10px rgba(255,184,92,0.30),
        inset 0 1px 0 rgba(255,255,255,0.24);
    }

    /* ---- Inside the filter page, restyle the injected controls for
       readability. Override the inline cssText of each control so they
       render as stacked, full-width, big-tap-target rows. ---- */
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] {
      display: block;
      width: 100%;
    }
    /* Control rows (radiogroups) become big stacked segmented controls.
       minmax(60px, 1fr): on a 375px viewport (343px inner), 4 pills with
       three 8px gaps fit as 4 × 79.75px columns — no wrap. On a 320px
       iPhone SE (288px inner) the same math gives 4 × 66px, still
       comfortably tappable. Wrapping a 4-pill row 3+1 would leave an
       orphan second row that reads as "broken" design; we'd rather
       have 60px pills than an uneven grid. */
    body[data-mobile-filters="open"] #hyperlocal-filter-page [role="radiogroup"] {
      display: grid !important;
      grid-template-columns: repeat(auto-fit, minmax(60px, 1fr));
      gap: 8px !important;
      padding: 0 !important;
      background: transparent !important;
      border: 0 !important;
      border-radius: 12px !important;
      width: 100% !important;
      max-width: 100% !important;
      justify-content: stretch !important;
      flex-wrap: nowrap !important;
    }
    body[data-mobile-filters="open"] #hyperlocal-filter-page [role="radiogroup"] button {
      min-height: 42px !important;
      padding: 9px 8px !important;
      font: 600 13.5px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif !important;
      background: rgba(255,255,255,0.05) !important;
      color: #e0e0e0 !important;
      border: 1px solid rgba(255,255,255,0.09) !important;
      border-radius: 10px !important;
      text-align: center !important;
      transition: background 140ms ease, transform 90ms ease !important;
    }
    body[data-mobile-filters="open"] #hyperlocal-filter-page [role="radiogroup"] button:active {
      transform: scale(0.97) !important;
    }
    body[data-mobile-filters="open"] #hyperlocal-filter-page [role="radiogroup"] button[aria-checked="true"] {
      background: #FFD37E !important;
      color: #1a1a1a !important;
      border-color: #FFD37E !important;
      font-weight: 700 !important;
    }

    /* Non-radiogroup wrappers inside slots (the budget range bar) stretch
       to the full row. */
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div:not([role]) {
      width: 100% !important;
      display: block !important;
    }

    /* ------------------------------------------------------------------
       Budget range bar — full-width inside the filter page slot, with
       bigger handles so a thumb on a 360-wide phone can grab them
       comfortably. Desktop styles live inline on the element; these
       rules ONLY apply while the mobile filter page is open.
       Selector boosted with [data-slot] > div[data-ezi="budget-range-bar"]
       so we tie specificity with the earlier [data-slot] > div:not([role])
       rule and win via source order (our rule appears later).
       ------------------------------------------------------------------ */
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div[data-ezi="budget-range-bar"] {
      display: flex !important;
      width: 100% !important;
      max-width: 100% !important;
      min-width: 0 !important;
      box-sizing: border-box !important;
      padding: 10px 14px 12px !important;
      gap: 8px !important;
      background: rgba(255,255,255,0.04) !important;
      border: 1px solid rgba(255,255,255,0.10) !important;
      border-radius: 12px !important;
    }
    /* Header row — readable typography, kept a touch smaller so the total
       bar stays compact enough for the 5-section no-scroll target. */
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div[data-ezi="budget-range-bar"] > div:first-child > span:first-child {
      font-size: 11px !important;
      color: #9a9a9a !important;
    }
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div[data-ezi="budget-range-bar"] > div:first-child > span:last-child {
      font-size: 15px !important;
    }
    /* Compact bar + mid-size handles. 38px bar / 26px handles still provides
       a 44px tap target once the invisible hit-zone span (inset:-14px)
       extends each handle's hit area — same thumb-friendly feel as before
       in half the vertical space. */
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div[data-ezi="budget-range-bar"] > div:nth-child(2) {
      height: 38px !important;
      padding: 0 14px !important;
    }
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div[data-ezi="budget-range-bar"] [role="slider"] {
      width: 24px !important;
      height: 24px !important;
      border-width: 3px !important;
    }
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div[data-ezi="budget-range-bar"] [role="slider"] > span {
      inset: -14px !important;
    }
    /* 6px rail keeps weight proportional to the 24px handles. */
    body[data-mobile-filters="open"] #hyperlocal-filter-page [data-slot] > div[data-ezi="budget-range-bar"] > div:nth-child(2) > div:first-child {
      height: 6px !important;
    }


    /* ============================================================
       DESKTOP (min-width: 721px, min-height: 501px)

       The Decide chip lives in the dock (chrome shell adopts it), and
       the Decide page renders as a bottom-centered popup that rises
       from the dock — the surface expands from its trigger. Controls
       render directly from the stores; no rails, no reparenting.
       ============================================================ */
    @media (min-width: 721px) and (min-height: 501px) {
      /* When the popup is open, tint the trigger chip so users see which
         button owns the open overlay — standard affordance. The selector
         out-specifies the .es-bottom dock chip rules. */
      body[data-mobile-filters="open"] #hyperlocal-filter-fab {
        background: var(--gold-a22, rgba(255,211,126,0.22)) !important;
        color: #FFE6B0 !important;
      }

      /* Re-shape the Decide page from full-screen to a bottom-centered
         popup card rising from the dock. The page keeps
         flex-direction:column (inherited from the base rule) so
         header/body/footer stack; justify-content:flex-end +
         padding-bottom anchors the card just above the dock. Each child
         carries a 440px max-width so the card reads as one column. */
      body[data-mobile-filters="open"] #hyperlocal-filter-page {
        align-items: center !important;
        justify-content: flex-end !important;
        padding: 24px 16px 80px !important;
        background: rgba(0,0,0,0.48) !important;
        backdrop-filter: blur(4px) !important;
        overflow: hidden !important;
      }

      /* All three direct children share the same 440px width so they
         read as one stacked card. We give each its own border segment
         and radius so the top/bottom rounded corners land correctly. */
      body[data-mobile-filters="open"] #hyperlocal-filter-page > * {
        width: 100% !important;
        max-width: 440px !important;
        box-sizing: border-box !important;
      }
      body[data-mobile-filters="open"] #hyperlocal-filter-page .fp-header {
        flex: 0 0 auto !important;
        border-top-left-radius: 16px !important;
        border-top-right-radius: 16px !important;
        border: 1px solid rgba(255,214,128,0.20) !important;
        border-bottom: 1px solid rgba(255,255,255,0.08) !important;
        background: rgba(22,22,24,0.98) !important;
        padding: 10px 12px !important;
      }
      body[data-mobile-filters="open"] #hyperlocal-filter-page .fp-body {
        flex: 0 1 auto !important;
        /* dvh — iOS URL bar collapse. 220px reserves room for the
         * filter page header + footer + top offset so the body never
         * runs past the sheet frame. */
        max-height: min(560px, calc(100vh - 220px)) !important;
        max-height: min(560px, calc(100dvh - 220px)) !important;
        overflow-y: auto !important;
        overscroll-behavior: contain !important;
        -webkit-overflow-scrolling: touch !important;
        background: rgba(18,18,20,0.98) !important;
        border-left: 1px solid rgba(255,214,128,0.20) !important;
        border-right: 1px solid rgba(255,214,128,0.20) !important;
        padding: 4px 18px 14px !important;
      }
      /* Footer on mobile is position:absolute pinned to the viewport
         bottom. Desktop needs it inline at the card's bottom so it
         sits directly under the body. */
      body[data-mobile-filters="open"] #hyperlocal-filter-page .fp-footer {
        flex: 0 0 auto !important;
        position: relative !important;
        bottom: auto !important;
        left: auto !important;
        right: auto !important;
        border-bottom-left-radius: 16px !important;
        border-bottom-right-radius: 16px !important;
        border: 1px solid rgba(255,214,128,0.20) !important;
        border-top: 1px solid rgba(255,255,255,0.06) !important;
        padding: 10px 12px !important;
        background: rgba(22,22,24,0.98) !important;
        box-shadow: 0 18px 48px rgba(0,0,0,0.55) !important;
      }

      /* Smaller header buttons on the desktop popup — the 48×48 mobile
         taps are oversized for a mouse target. */
      #hyperlocal-filter-page .fp-back {
        width: 36px !important;
        height: 36px !important;
        font-size: 20px !important;
      }
      #hyperlocal-filter-page .fp-title {
        font-size: 15px !important;
      }
      #hyperlocal-filter-page .fp-reset {
        padding: 7px 10px !important;
        font-size: 13px !important;
      }

      /* Hints stay hidden on desktop too — with bounded popup height and
         5 sections to cover, per-section hints push content below the
         fold and force a scroll. The numbered section headings
         ("1  Who's moving in?") are already unambiguous. */

      /* Apply button — slightly shorter on desktop since the card is
         bounded (mobile needs the full 48px thumb target). */
      #hyperlocal-filter-page .fp-apply {
        height: 44px !important;
        font-size: 14.5px !important;
      }

      /* Tighter section spacing on desktop so all 5 sit inside the
         bounded popup body without scrolling. */
      body[data-mobile-filters="open"] #hyperlocal-filter-page .fp-section {
        padding: 8px 0 6px !important;
      }
      body[data-mobile-filters="open"] #hyperlocal-filter-page .fp-section h2 {
        margin: 0 0 6px !important;
        font-size: 14px !important;
      }
    }

    /* -------- Picks panel inside the Decide page --------
       The shortlist panel is reparented into .fp-picks-slot while the page
       is open. Strip its floating-aside styling and let it flow as page
       content; outside the page it stays hidden (the Decide surface is its
       only home — docs/ONE_STORY.md). */
    #hyperlocal-shortlist-panel {
      display: none !important;
    }
    #hyperlocal-filter-page .fp-picks-slot #hyperlocal-shortlist-panel {
      display: block !important;
      position: static !important;
      width: 100% !important;
      max-width: 100% !important;
      max-height: none !important;
      inset: auto !important;
      background: transparent !important;
      backdrop-filter: none !important;
      border: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      padding: 4px 0 8px !important;
    }
    /* The panel's internal "Top Picks for You" title is redundant with the
       section heading — hide it inside the page. */
    #hyperlocal-filter-page .fp-picks-slot #hyperlocal-shortlist-panel > div:first-child {
      display: none !important;
    }

    /* Action sections (Compare / Your picks) — same heading scale as the
       numbered questions, with the slot content left-flowing. */
    #hyperlocal-filter-page .fp-section-action h2 {
      font-size: 14px;
    }
    #hyperlocal-filter-page .fp-picks-actions {
      margin-bottom: 6px;
    }

    /* -------- Decide status chip --------
       Bottom-center, riding just above the dock. Visible only while
       Decide-state ≠ exploration or the comparator is armed — the map is
       never silently filtered (docs/ONE_STORY.md). */
    #hyperlocal-decide-chip {
      display: none; /* JS flips to inline-flex when state is active */
      position: fixed;
      bottom: 72px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 1200;
      align-items: center;
      gap: 8px;
      max-width: min(520px, calc(100vw - 24px));
      padding: 7px 8px 7px 13px;
      background: var(--gold-a12, rgba(255,211,126,0.10));
      backdrop-filter: blur(10px);
      border: 1px solid var(--gold-a40, rgba(255,211,126,0.40));
      border-radius: 999px;
      color: #FFE6B0;
      font: 600 12px/1.2 system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      letter-spacing: 0.02em;
      box-shadow: 0 6px 18px rgba(0,0,0,0.40);
      cursor: pointer;
      white-space: nowrap;
    }
    #hyperlocal-decide-chip .dc-txt {
      overflow: hidden;
      text-overflow: ellipsis;
    }
    #hyperlocal-decide-chip .dc-x {
      flex: 0 0 auto;
      width: 22px;
      height: 22px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: rgba(255,255,255,0.08);
      border: 0;
      border-radius: 999px;
      color: #FFE6B0;
      font: 700 14px/1 system-ui;
      cursor: pointer;
      transition: background 140ms ease;
    }
    #hyperlocal-decide-chip .dc-x:hover {
      background: rgba(255,255,255,0.18);
    }
    @media (max-width: 720px), (max-height: 500px) {
      /* On phones the whole bottom edge is the snapshot (full-width,
         z 1300) stacked on the dock — a bottom-anchored chip hid BEHIND
         the snapshot. Move it to the free top-centre band (between the
         home chip and city toggle) and lift it above the snapshot so the
         "you are deciding" cue is always the visible truth. */
      #hyperlocal-decide-chip {
        top: 54px;
        bottom: auto;
        z-index: 1350;
        font-size: 11.5px;
        max-width: calc(100vw - 24px);
      }
    }
  `;
  document.head.appendChild(s);
}

/**
 * Install the ⚙ filter FAB + drawer for mobile. Idempotent.
 *
 * Default state is CLOSED — the map is unobstructed. When the user taps the
 * FAB, body[data-mobile-filters=open] is set, which causes the CSS above to:
 *   1. Show the drawer.
 *   2. Move the hidden filter panels (Who is this for / Priority / Mode /
 *      Type / Budget) INTO the drawer as flex children (via CSS positioning).
 *
 * We don't reparent DOM — each panel keeps its `position:fixed` ancestry in
 * the original body, and we simply override positioning to `position:static`
 * inside the drawer via a `body[data-mobile-filters=open]` selector. This
 * keeps their own event handlers and subscriptions intact.
 *
 * Tap outside drawer → closes. Tap FAB again → closes. Tap a pin → closes
 * (so the verdict card isn't covered by an open drawer).
 */
/**
 * The Decide surface — the ONE opt-in second act (docs/ONE_STORY.md).
 *
 * A single "Decide" chip in the dock opens this page: full-screen on
 * mobile, a bottom-anchored popup rising from the dock on desktop. It owns
 * every buyer-flavoured control with one name per concept:
 *
 *   1. Who's moving in?    — persona            (decision-lens store)
 *   2. Buying or renting?  — mode               (purchase-mode store)
 *   3. What type of home?  — type               (property-type store)
 *   4. Your budget         — budget range bar   (budget bucket/range stores)
 *   5. What matters most?  — priority           (constraint-priority store)
 *      + the School rankings entry (visible only when priority = school)
 *   ·  Compare two areas   — arms compare, then closes so the map can take
 *                            the two taps
 *   ·  Your picks          — the ranked shortlist panel, reparented in
 *                            while the page is open, plus the Full map view
 *                            toggle
 *
 * Controls render DIRECTLY from the stores into the page (built once at
 * install — subscriptions live for the session). The old pattern — three
 * fixed-position rails built at boot, permanently CSS-hidden, then
 * DOM-surgered in by positional child index — is deleted.
 *
 * DOM ids keep the legacy "filter" names (#hyperlocal-filter-fab/-page):
 * they are plumbing referenced by CSS in several modules. Every
 * user-facing word says Decide.
 */
function installDecideSurface(cityId: CityId): void {
  if (document.getElementById("hyperlocal-filter-fab")) return;

  // ---- "Decide" chip (adopted into the dock by the chrome shell) ----
  const fab = document.createElement("button");
  fab.id = "hyperlocal-filter-fab";
  fab.type = "button";
  fab.setAttribute(
    "aria-label",
    "Decide between areas — who's moving, budget, priorities, picks",
  );
  fab.title = "Decide between areas";
  fab.innerHTML =
    '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="m3 7 2 2 4-4"/><path d="m3 17 2 2 4-4"/><path d="M13 6h8"/><path d="M13 12h8"/><path d="M13 18h8"/></svg><span>Decide</span>';
  document.body.appendChild(fab);

  // ---- The Decide page ----------------------------------------------
  const page = document.createElement("div");
  page.id = "hyperlocal-filter-page";
  page.setAttribute("role", "dialog");
  page.setAttribute("aria-modal", "true");
  page.setAttribute("aria-label", "Decide between areas");
  page.innerHTML = `
    <header class="fp-header">
      <button class="fp-back" type="button" aria-label="Close Decide mode">←</button>
      <h1 class="fp-title">Decide between areas</h1>
      <button class="fp-reset" type="button">Reset</button>
    </header>
    <div class="fp-body">
      <section class="fp-section">
        <h2><span class="fp-num">1</span>Who's moving in?</h2>
        <p class="fp-hint">We'll personalise suggestions to what this group usually cares about.</p>
        <div class="fp-slot" data-slot="persona"></div>
      </section>
      <section class="fp-section">
        <h2><span class="fp-num">2</span>Buying or renting?</h2>
        <p class="fp-hint">Pick whichever applies right now — you can change it anytime.</p>
        <div class="fp-slot" data-slot="mode"></div>
      </section>
      <section class="fp-section">
        <h2><span class="fp-num">3</span>What type of home?</h2>
        <p class="fp-hint">Standalone house, apartment, or open to either.</p>
        <div class="fp-slot" data-slot="type"></div>
      </section>
      <section class="fp-section">
        <h2><span class="fp-num">4</span>Your budget</h2>
        <p class="fp-hint">Roughly how much can you spend? Sweep a range.</p>
        <div class="fp-slot" data-slot="budget"></div>
      </section>
      <section class="fp-section">
        <h2><span class="fp-num">5</span>What matters most?</h2>
        <p class="fp-hint">Pick ONE thing you can't compromise on. Top matches will glow on the map.</p>
        <div class="fp-slot" data-slot="priority"></div>
        <div class="fp-slot fp-school-slot" data-slot="school"></div>
      </section>
      <section class="fp-section fp-section-action">
        <h2>Compare two areas</h2>
        <p class="fp-hint">Arm the comparator, then tap two areas on the map.</p>
        <div class="fp-slot" data-slot="compare"></div>
      </section>
      <section class="fp-section fp-section-action">
        <h2>Your picks</h2>
        <p class="fp-hint">Ranked matches for your setup. Tap one to see it on the map.</p>
        <div class="fp-slot fp-picks-actions" data-slot="picks-actions"></div>
        <div class="fp-picks-slot" data-slot="picks"></div>
      </section>
    </div>
    <footer class="fp-footer">
      <button class="fp-apply" type="button">See the map</button>
    </footer>
  `;
  document.body.appendChild(page);

  // ---- Direct rendering from the stores -----------------------------
  // Built once; the stores broadcast every change so these rows stay in
  // sync with the status chip, the map emphasis and each other.
  const slot = (name: string) =>
    page.querySelector(`[data-slot="${name}"]`) as HTMLElement;
  slot("persona").appendChild(buildPersonaPillRow().el);
  slot("mode").appendChild(buildModeRow());
  slot("type").appendChild(buildTypeRow());
  slot("budget").appendChild(buildBudgetRangeBar(cityId as CityCode));
  slot("priority").appendChild(buildPriorityRow());

  // Full map ↔ picks view toggle — lives with the picks it affects.
  // Filters stay put; only the map emphasis releases.
  const viewBtn = document.createElement("button");
  viewBtn.type = "button";
  viewBtn.className = "fp-fullmap-toggle";
  const renderViewBtn = () => {
    const fullMap = getFullMapView();
    viewBtn.textContent = fullMap ? "Show picks" : "Full map";
    viewBtn.setAttribute(
      "aria-label",
      fullMap
        ? "Show picks — restore the shortlist emphasis on the map"
        : "Full map — release the shortlist emphasis and show the whole city",
    );
    viewBtn.style.cssText = `
      display:inline-flex;align-items:center;gap:6px;
      padding:6px 11px;border-radius:999px;
      font:600 11px/1 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;
      letter-spacing:0.02em;cursor:pointer;
      transition:background 140ms ease,border-color 140ms ease,color 140ms ease;
      ${
        fullMap
          ? "background:var(--gold-a22, rgba(255,211,126,0.22));border:1px solid var(--gold-a55, rgba(255,211,126,0.55));color:#FFE6B0;"
          : "background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.18);color:#DCDCDC;"
      }
    `;
  };
  renderViewBtn();
  viewBtn.addEventListener("click", () => toggleFullMapView());
  subscribeFullMapView(renderViewBtn);
  slot("picks-actions").appendChild(viewBtn);

  // ---- Picks panel reparenting ---------------------------------------
  // The shortlist panel renders inside the page while it's open and goes
  // home (hidden) on close. One persistent renderer, one visible home.
  let panelRestore: { parent: Element; next: Element | null } | null = null;

  function adoptPicksPanel(): void {
    const panel = document.getElementById("hyperlocal-shortlist-panel");
    const dest = slot("picks");
    if (!panel || !dest || panel.parentElement === dest) return;
    panelRestore = {
      parent: panel.parentElement || document.body,
      next: panel.nextElementSibling,
    };
    dest.appendChild(panel);
  }

  function restorePicksPanel(): void {
    const panel = document.getElementById("hyperlocal-shortlist-panel");
    if (panel && panelRestore) {
      const { parent, next } = panelRestore;
      if (next && next.parentElement === parent) {
        parent.insertBefore(panel, next);
      } else {
        parent.appendChild(panel);
      }
    }
    panelRestore = null;
  }

  // ---- Open / close ---------------------------------------------------
  function openPage(): void {
    document.body.setAttribute("data-mobile-filters", "open");
    adoptPicksPanel();
  }

  function closePage(): void {
    document.body.removeAttribute("data-mobile-filters");
    restorePicksPanel();
    // Remember that the user has completed setup for this session so we
    // don't auto-reopen the page on subsequent map loads.
    try {
      sessionStorage.setItem("hyperlocal.filterSetupDone", "1");
    } catch {
      /* storage may be blocked — that's OK, user can still use the app */
    }
  }

  fab.addEventListener("click", (e) => {
    e.stopPropagation();
    // Toggle — a second click on the chip closes an open page.
    if (document.body.getAttribute("data-mobile-filters") === "open") {
      closePage();
    } else {
      openPage();
    }
  });
  page.querySelector(".fp-back")?.addEventListener("click", (e) => {
    e.stopPropagation();
    closePage();
  });
  // Click-outside-card dismissal (desktop popup pattern). On mobile the
  // page IS the full viewport so this rarely triggers; on desktop the
  // page is an overlay and a click on the dim background closes.
  page.addEventListener("click", (e) => {
    if (e.target === page) closePage();
  });
  page.querySelector(".fp-apply")?.addEventListener("click", (e) => {
    e.stopPropagation();
    closePage();
    // Explicit "commit" signal — fires AFTER closePage so the map is now
    // visible. `installAutoFraming` listens and refits the viewport to
    // the current shortlist. Defer one frame so the page-removal has
    // flushed layout before the camera computes its fit.
    requestAnimationFrame(() => {
      window.dispatchEvent(new CustomEvent("hyperlocal:filters-applied"));
    });
  });
  page.querySelector(".fp-reset")?.addEventListener("click", (e) => {
    e.stopPropagation();
    // One shared reset (decision-lens.ts) — store setters fan out to every
    // rendered control, the status chip, and the map emphasis.
    resetToExploration(cityId as CityCode);
  });
  // Arming compare needs the MAP, not the page — when the compare pill
  // (mounted into the compare slot by installComparePill) flips state to
  // waitA, close the page so the next two taps land on areas. The pill's
  // own click handler runs first (registered earlier), so reading the
  // state after a frame sees the post-transition value.
  slot("compare").addEventListener("click", () => {
    requestAnimationFrame(() => {
      if (
        getCompareState().mode !== "off" &&
        document.body.getAttribute("data-mobile-filters") === "open"
      ) {
        closePage();
      }
    });
  });
  // Escape key closes.
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && document.body.getAttribute("data-mobile-filters") === "open") {
      closePage();
    }
  });

  // Any modal that belongs ABOVE the Decide page (a verdict card, a
  // compare card, the school-chart sheet) must reveal itself, not open
  // underneath the page. The picks list and school-chart entry live
  // INSIDE the page, so finalising a compare or tapping a chart row can
  // fire one of these while the page is still open — close the page the
  // moment such a modal mounts. One observer covers all three paths.
  const ABOVE_IDS = [
    "hyperlocal-verdict-card",
    "hyperlocal-compare-card",
    "hyperlocal-school-chart-backdrop",
  ];
  const aboveObserver = new MutationObserver(() => {
    if (document.body.getAttribute("data-mobile-filters") !== "open") return;
    if (ABOVE_IDS.some((id) => document.getElementById(id))) closePage();
  });
  aboveObserver.observe(document.body, { childList: true, subtree: true });
}

/**
 * The Decide status chip — the map is never silently filtered.
 *
 * Whenever Decide-state ≠ exploration (including state silently restored
 * from localStorage on a cold boot) a small chip above the dock names the
 * active setup — "Deciding: Family · Schools" — with a one-tap ✕ back to
 * Exploration. While the comparator is armed it switches to the compare
 * prompt and ✕ cancels the compare instead.
 */
function installDecideChip(cityId: CityId): void {
  if (document.getElementById("hyperlocal-decide-chip")) return;
  const chip = document.createElement("div");
  chip.id = "hyperlocal-decide-chip";
  chip.setAttribute("role", "status");

  const txt = document.createElement("span");
  txt.className = "dc-txt";
  const x = document.createElement("button");
  x.type = "button";
  x.className = "dc-x";
  x.textContent = "×";

  chip.appendChild(txt);
  chip.appendChild(x);
  document.body.appendChild(chip);

  const render = () => {
    const compare = getCompareState();
    if (compare.mode !== "off") {
      txt.textContent =
        compare.mode === "waitA"
          ? "Comparing — tap the first area"
          : `Comparing — ${compare.a?.name ?? "Area A"} picked, tap the second`;
      x.setAttribute("aria-label", "Cancel compare");
      chip.style.display = "inline-flex";
      return;
    }
    const parts: string[] = [];
    if (getLensMode() !== "none") parts.push(getLensModeLabel());
    if (getPriority() !== "none") parts.push(getPriorityLabel());
    if (getBudgetBand() !== "none") parts.push(getBudgetBandLabel());
    // A custom numeric sweep that doesn't snap to a named band still
    // filters the map (hasEnoughSignal checks userRange) — so it MUST
    // surface in the chip, or the map is silently filtered.
    else if (getUserBudgetRange() !== null) parts.push("Budget");
    if (getPurchaseMode() === "rent") parts.push("Renting");
    if (parts.length === 0) {
      chip.style.display = "none";
      return;
    }
    txt.textContent = `Deciding: ${parts.join(" · ")}`;
    x.setAttribute("aria-label", "Reset to exploration — clear all Decide filters");
    chip.style.display = "inline-flex";
  };

  x.addEventListener("click", (e) => {
    e.stopPropagation();
    if (getCompareState().mode !== "off") {
      cancelCompare();
      dismissCompareCard();
    } else {
      resetToExploration(cityId as CityCode);
    }
  });
  // Tapping the chip body (not ✕) opens the Decide page for adjustment.
  chip.addEventListener("click", () => {
    if (getCompareState().mode !== "off") return;
    if (document.body.getAttribute("data-mobile-filters") !== "open") {
      document.getElementById("hyperlocal-filter-fab")?.click();
    }
  });

  render();
  subscribeLens(render);
  subscribePriority(render);
  subscribeBudgetBand(render);
  subscribeUserBudgetRange(render);
  subscribePurchaseMode(render);
  subscribeCompare(render);
}


/**
 * Close every full-screen overlay that is currently covering the map
 * (the Decide page, the school-chart modal). Safe to call when none are
 * open — each branch no-ops. Used by pick-card click handlers so tapping
 * a pick reveals the map and opens the verdict card "solo".
 */
function closeMobileOverlays(): void {
  // Decide page — click the back button so its own close logic runs
  // (restores the picks panel, clears the body attribute).
  if (document.body.getAttribute("data-mobile-filters") === "open") {
    const back = document.querySelector(
      "#hyperlocal-filter-page .fp-back"
    ) as HTMLButtonElement | null;
    if (back) back.click();
  }
  // School-chart modal — clicking the close button keeps the
  // overlay-closing logic consistent across surfaces.
  const schoolBackdrop = document.getElementById("hyperlocal-school-chart-backdrop");
  if (schoolBackdrop) schoolBackdrop.remove();
}


async function boot() {
  console.group("[HYPERLOCAL:boot]");
  console.log("version", "3.1-two-cities");
  installMobileStyles();

  // Tag the body so any conditional auth-pill CSS still resolves correctly
  // for surfaces that DO mount the pill (e.g. /account). Setting the
  // attribute is cheap and harmless even though the city map no longer
  // mounts the pill itself.
  document.body.setAttribute("data-hyperlocal-page", "app");

  // Auth pill — REMOVED 2026-05-03 from city maps per user feedback:
  // sign-in had no remaining hook on the map. mountAuthPill etc. remain
  // exported from src/landing/auth.ts and are still mounted on the landing
  // page + /account. If a sign-in-required feature ships on the map later,
  // dynamic-import("./landing/auth") here — do NOT re-add the static import,
  // it drags ~36KB of auth/UI code into the boot chunk for a dead feature.

  const cityId = readCityParam();
  const city = CITIES[cityId];
  console.log("city", city.id, "(", city.label, ")");
  console.log("verdict count", city.verdicts.length);

  // 1) URL hash defence. A stale #8.82/40.9/-74.2 will drift the map off
  //    its city — wipe it before MapLibre can read it.
  if (window.location.hash && window.location.hash.length > 1) {
    console.log("cleared stale hash:", window.location.hash);
    history.replaceState(
      null,
      "",
      window.location.pathname + window.location.search
    );
  }

  status(`Hyperlocal loading… (${city.label})`);

  // 2) Basemap cascade (Apple-Maps DARK simulator pass, 2026-04-26 v2):
  //    a. Carto Dark Matter VECTOR — true-black palette + 3D building
  //       extrusions at runtime. This is the Apple-Maps-dark-mode look the
  //       user re-confirmed after the cream Voyager pass was rejected for
  //       clashing with the rest of the dark-themed product chrome.
  //    b. Carto Dark RASTER — proven flat dark fallback if vector unreachable.
  //    c. OpenStreetMap RASTER — ultimate fallback if everything else dies.
  //
  //    The probe is a GET on Dark Matter's style.json (the GL spec entry
  //    point). If we get a 200 we trust the vector tiles will load too;
  //    they're served from the same CDN.
  // Render-first boot (2026-06-06 - fix for the ~12s blank screen):
  // the previous boot AWAITED up to three sequential tile probes (3.5s
  // timeout each) BEFORE constructing the map - worst case ~10.5s of
  // blank viewport plus style load. Inverted: construct the map
  // immediately with the vector style (Carto uptime makes optimism the
  // right default) and arm a watchdog after construction that demotes to
  // Carto raster / OSM only when the style genuinely fails to load.
  const style: StyleSpecification | string = createBasemapStyleURL();
  let sourceLabel = "Carto Dark Matter (vector + 3D buildings)";
  let isVector = true;
  console.log("boot: render-first - vector style assumed; fallback watchdog arms after construction");
  console.log("basemap:", sourceLabel);
  status(`Map: ${sourceLabel}`);

  // 3) Create the map. Center + zoom come from the selected city.
  //
  // Touch-gesture handling is explicitly enabled so iOS Chrome and Android
  // Chrome get pinch-zoom + two-finger rotate without a default-vs-spec
  // ambiguity. `cooperativeGestures` is DELIBERATELY off — we're a
  // full-viewport map, not an embedded widget, so users expect the browser's
  // normal scroll/pinch to drive the map directly.
  const isTouch =
    typeof window !== "undefined" &&
    window.matchMedia("(hover: none)").matches;
  // Pitch is enabled when we got the vector style — 3D buildings need a
  // tilted camera to read as 3D. On raster fallbacks we keep pitch at 0
  // because raster tiles smear badly under projection.
  //
  // Per 2026-04-26 user feedback ("only make the map 3D once it is zoomed
  // in close to buildings or else have 2D map which will be easier for
  // decision making"): the INITIAL pitch is 0 even on vector. The default
  // city zoom (~11.2) drops users into a top-down 2D scanning view where
  // suburbs / verdict pins are easy to compare. As they zoom in past 14
  // the `installPitchByZoom` listener (called on map.load) eases pitch up
  // to the desktop/mobile target so 3D buildings come into view smoothly.
  //
  // We still publish the TARGET pitch (TARGET_PITCH_VECTOR) so the zoom
  // listener knows where to land at z16+. Mobile gets 35°, desktop 50°.
  const TARGET_PITCH_VECTOR = isTouch ? 35 : 50;
  const initialPitch = 0; // start in 2D regardless of basemap
  const initialBearing = isVector ? -17 : 0; // -17° is Apple Maps' canonical default tilt
  const map = new maplibregl.Map({
    container: "map",
    style,
    center: city.center,
    zoom: city.zoom,
    pitch: initialPitch,
    bearing: initialBearing,
    hash: false,
    maxPitch: isVector ? 65 : 0,
    // Per 2026-04-26 user feedback ("Once it got to the last zoom in point
    // map is getting stuck"): MapLibre's default maxZoom is 22. The Carto
    // vector source's natural maxzoom is 14, so z=22 is 256× overzoom.
    // Combined with the 50° pitch and the doubled fill-extrusion stack
    // (`building-3d` base + `building-3d-landmark` SimCity gold accent),
    // the GPU has to evaluate height/color expressions per feature against
    // thousands of stretched building polygons per frame. The render loop
    // stalls and the user perceives "stuck". Capping at 19 keeps individual
    // buildings readable, streets still distinguishable, and frame budget
    // comfortably under 16ms.
    maxZoom: 19,
    // Default attribution suppressed only so we can re-add it COMPACT and
    // dark-styled below — CARTO's terms and OSM's ODbL want user-facing
    // attribution, and a product whose brand is "the truth layer" does not
    // strip provenance from its own basemap.
    attributionControl: false,
    // Touch — explicit for determinism across iOS/Android Chrome + Safari.
    touchZoomRotate: true,
    // Vector + 3D buildings benefits from two-finger pitch on mobile so
    // users can dial the perspective to taste. On raster fallback we
    // leave it off (raster is already pitched at 0).
    touchPitch: isVector,
    dragRotate: !isTouch, // prevents accidental two-finger rotate on mobile
    pitchWithRotate: false,
  });
  // Some MapLibre builds require calling this *after* construction to ensure
  // two-finger rotate is on (desktop) / off (mobile).
  if (isTouch) {
    map.touchZoomRotate.enable();
    map.touchZoomRotate.disableRotation();
  }

  // Read-only console handle for diagnostics (layer/source inspection,
  // queryRenderedFeatures probes). Not a public API — UI modules must keep
  // receiving the map by argument, never reach for this.
  (window as unknown as Record<string, unknown>).HYPERLOCAL_MAP = map;

  // Fallback watchdog - the render-first counterpart of the old probe
  // cascade. Pre-load failures (style 4xx/5xx, CDN outage, offline)
  // demote vector -> Carto raster -> OSM via setStyle. Once 'load' has
  // fired we NEVER restyle: that would wipe the installed layers, and a
  // post-load tile hiccup is a transient, not a basemap failure.
  let basemapDegraded = false;
  const degradeBasemap = async (reason: string) => {
    if (basemapDegraded || map.loaded()) return;
    basemapDegraded = true;
    console.warn(`✗ vector basemap failed (${reason}) - probing raster fallbacks`);
    const cartoOk = await tileOk(CARTO_DARK_TILES[0], 2500);
    isVector = false;
    sourceLabel = cartoOk ? "Carto Dark (raster)" : "OpenStreetMap (raster)";
    const fallbackStyle = cartoOk
      ? createRasterStyle(
          "carto-dark",
          CARTO_DARK_TILES,
          '© <a href="https://carto.com/attributions">CARTO</a> · © OpenStreetMap'
        )
      : createRasterStyle("osm", OSM_TILES, "© OpenStreetMap contributors");
    console.warn(`falling back to ${sourceLabel}`);
    map.setPitch(0);
    map.setBearing(0);
    map.touchPitch.disable();
    map.setStyle(fallbackStyle);
    status(`Map: ${sourceLabel}`);
  };
  // The watchdog must test "did the STYLE arrive", not "is the whole map
  // rendered". `load` waits for every in-view tile, which at city zoom takes
  // longer than 5s on real connections — so the old timer demoted a healthy
  // vector map to raster for nearly every user (and the raster swap restarted
  // tile loading from zero: slower AND flat). `styledata` fires as soon as the
  // style document is parsed and applied; if it never fires, the style fetch
  // genuinely failed and the raster fallback is correct. Tile-CDN failures
  // after a healthy style are caught by the map error handler below.
  let styleArrived = false;
  map.once("styledata", () => {
    styleArrived = true;
  });
  const bootWatchdog = window.setTimeout(() => {
    if (styleArrived) return; // style is healthy — let tiles stream in peace
    void degradeBasemap("style not loaded within 5s");
  }, 5000);
  map.once("load", () => window.clearTimeout(bootWatchdog));

  // Compact provenance pill — collapsed to an ⓘ, dark-skinned to the
  // basemap. Kept out of the dock's way (bottom-right desktop; lifted above
  // the two-row dock on mobile via the override sheet below).
  map.addControl(
    new maplibregl.AttributionControl({ compact: true }),
    "bottom-right"
  );
  if (!document.getElementById("hyperlocal-attrib-styles")) {
    const attribCss = document.createElement("style");
    attribCss.id = "hyperlocal-attrib-styles";
    attribCss.textContent = `
      .maplibregl-ctrl-attrib {
        background: var(--surface-glass, rgba(20, 21, 24, 0.94)) !important;
        border: 1px solid var(--hairline, rgba(255,255,255,0.06));
        border-radius: var(--r-pill, 999px);
        color: var(--ink-3, #8A8680);
        font: 10.5px/1.4 var(--font-body, system-ui, sans-serif);
      }
      .maplibregl-ctrl-attrib a { color: var(--ink-2, #B8B4AC); }
      .maplibregl-ctrl-attrib.maplibregl-compact-show {
        padding: 2px 26px 2px 10px;
      }
      @media (max-width: 720px), (max-height: 500px) {
        .maplibregl-ctrl-bottom-right { bottom: 96px; }
      }
    `;
    document.head.appendChild(attribCss);
  }

  // The instant answer — a real area, its verdict, the top live signal and
  // the freshness heartbeat. Installed IMMEDIATELY after map construction
  // (not on map.load) so the answer is on screen even while tiles stream
  // on a slow connection. This replaces the old "Tap any glowing spot…"
  // hint: show, don't tell. Skipped when a ?focus= deep link will open the
  // full verdict card anyway.
  try {
    if (!readFocusParam(city.verdicts)) {
      installAreaSnapshot({
        cityId: city.id,
        cityLabel: city.label,
        verdicts: city.verdicts,
        nearFn: city.nearFn,
        suggestedIds: SUGGESTED_IDS[city.id],
        onExpand: (v) => {
          flyToSuburb(map, v.coord);
          showVerdict(v);
        },
      });
    }
  } catch (err) {
    console.warn("[HYPERLOCAL] area snapshot failed:", err);
  }
  map.on("error", (e: { error?: { message?: string } }) => {
    const msg = String(e?.error?.message ?? "");
    if (!map.loaded() && /style|source|tile|fetch|load/i.test(msg)) {
      void degradeBasemap(msg.slice(0, 80) || "map error before load");
    }
  });

  // One material (design language §2): the sim-basemap recolour used to land
  // in the deferred phase, seconds after first paint — the city visibly
  // changed colour under the user mid-read. The chunk is tiny, so fetch it
  // from construction time and apply it INSIDE the load handler, behind the
  // loader's 320ms exit fade. The user sees one composed city, once.
  const simBasemapEarly = import("./map/sim-basemap").catch((err) => {
    console.warn("[HYPERLOCAL] sim-basemap chunk failed to load:", err);
    return null;
  });

  // Map load success -> verdict layer + FPS + ready banner + first-visit hint.
  map.on("load", () => {
    console.log("✓ map load event");
    // The map has painted its first complete frame — reveal it NOW. Every
    // install below is incremental decoration on an already-usable map;
    // holding the loading screen hostage to ~25 component installs was a
    // large slice of the old ~10s black screen. The exit is a 320ms fade
    // (never a hard cut — see app/index.html .is-revealed) and clearing
    // data-booting lets the area snapshot settle back to its normal rung.
    const loadingEl = document.getElementById("loading-screen");
    if (loadingEl) {
      loadingEl.classList.add("is-revealed");
      window.setTimeout(() => loadingEl.remove(), 360);
    }
    document.body.removeAttribute("data-booting");

    // Apple-Maps-3D simulator pass (2026-04-26): tint Voyager toward Apple's
    // beige paper + saturated parks + pale-teal water palette, then attach
    // the 3D buildings extrusion layer. Both are no-ops on raster fallback,
    // so we don't need to gate by `isVector` — the inner functions check
    // for layer/source existence and degrade silently.
    try {
      applyAppleMapsTint(map);
    } catch (err) {
      console.warn("[HYPERLOCAL] Apple-Maps tint failed:", err);
    }
    // Apple-Maps lake shoreline glow + SimCity-style landuse zoning. Runs
    // after the base tint so the new layers sit on top of the recoloured
    // base (water class match expression already applied). Wrapped in its
    // own try/catch — if Carto changes their `landuse` source-layer schema
    // tomorrow this just no-ops without breaking the buildings extrusion.
    try {
      attachWaterAndLanduseRefinement(map);
      console.log("✓ water + landuse refinement attached (Apple-Maps lake shading + SimCity zoning)");
    } catch (err) {
      console.warn("[HYPERLOCAL] water/landuse refinement failed:", err);
    }
    // Water animation, city vibe, sim-basemap, textured buildings, traffic
    // streaks and landmark billboards moved to installDeferredPhase() — all
    // decorative passes on top of an already-correct basemap.
    try {
      attach3DBuildings(map);
      console.log("✓ 3D buildings layer attached");
    } catch (err) {
      console.warn("[HYPERLOCAL] 3D buildings attach failed:", err);
    }
    try {
      attachLandmarkBuildings(map);
      console.log("✓ landmark (top 1%) building accent layer attached");
    } catch (err) {
      console.warn("[HYPERLOCAL] landmark buildings attach failed:", err);
    }
    // Final paint pass — applied here (not deferred) so the basemap reaches
    // its finished material in one composed reveal. Runs LAST in the basemap
    // pipeline; nothing after it may re-paint these layers.
    void simBasemapEarly.then((mod) => {
      if (!mod) return;
      try {
        mod.applySimBasemap(map);
        console.log("✓ sim-basemap applied (saturated zoning + zone-tinted buildings + vibrant parks)");
      } catch (err) {
        console.warn("[HYPERLOCAL] sim-basemap apply failed:", err);
      }
    });
    // (city vibe, sim-basemap, textured buildings, traffic streaks,
    //  landmark billboards, incident cards → installDeferredPhase below.)
    // Chrome shell — top chips (weather/AQI), left rail (Local Pulse),
    // bottom nav (Map | centered red Search | Compare/Filter). Map button
    // fits the entire city. Pulse/Explore/Locate/Saved/Profile were
    // retired 2026-05-07 — they didn't pull weight on desktop.
    // Hides the legacy city-toggle / suburb-search / geo-pill / near-me
    // pill via CSS overrides — their click handlers stay wired and the
    // new shell forwards user actions to them. See src/ui/chrome-shell.ts.
    void installChromeShell(map, {
      cityId: city.id as "hyd" | "blr" | "mel" | "syd",
      cityLabel: city.label,
      points: city.verdicts.map((v) => v.coord as [number, number]),
    }).catch((err) => {
      console.warn("[HYPERLOCAL] chrome shell install failed:", err);
    });
    // Right-rail Suburb Intelligence panel removed 2026-05-07 — verdict
    // card now occupies that slot and serves as the canonical suburb
    // info surface.
    // Zoom-driven pitch easing: keep camera flat at city-overview zooms
    // for decision-making, lift to full Apple-Maps tilt as the user zooms
    // into street level (where 3D buildings start rendering).
    if (isVector) {
      try {
        installPitchByZoom(map, TARGET_PITCH_VECTOR);
        console.log("✓ pitch-by-zoom listener installed (target", TARGET_PITCH_VECTOR, "°)");
      } catch (err) {
        console.warn("[HYPERLOCAL] pitch-by-zoom install failed:", err);
      }
    }

    // Expose the active city's verdict list for decision surfaces that
    // need to finalise a compare without having the dataset threaded
    // through (e.g. the verdict popup's Compare action).
    setActiveDataset(city.verdicts);

    // Suburb boundary ring layer was removed — having two "selected"
    // indicators (ring + popup) conflicted visually. The verdict popup
    // is the single selection indicator now.

    // Kick off the safety-signal feed for the active city. Fire-and-
    // forget — verdict card waits on `whenSafetyReady` before showing
    // the safety bullet, and the verdict layer subscribes to the same
    // promise to re-stamp the ⚠ tradeoff icon once counts arrive.
    // Reads through the shared incidents-loader cache, so this is one
    // HTTP round-trip even when several modules consume the feed.
    try {
      void initSafetySignal({ city: city.id, verdicts: city.verdicts });
    } catch (err) {
      console.warn("[HYPERLOCAL] safety-signal init failed:", err);
    }

    try {
      installVerdictLayer(map, city.verdicts, city.nearFn, city.label, city.id);
      console.log(
        "✓ verdict layer installed (",
        city.verdicts.length,
        "pins,",
        city.label,
        ")"
      );
    } catch (err) {
      console.error("✗ verdict layer failed:", err);
    }

    // First-tap guidance. Pure decoration; safe to fail silently.
    try {
      installSuggestedHighlight(map, city.verdicts, city.id);
    } catch (err) {
      console.warn("[HYPERLOCAL] suggested highlight failed:", err);
    }

    // (Area snapshot installs right after map construction, NOT here —
    //  the answer must not wait for tile loading. See boot() below.)

    // School marker map layer removed — it wasn't delivering enough
    // visual value. Rankings are still accessible via the School chart.

    // Status banner removed from the UI — see `status()` for rationale.
    // We still call it once so the dataset + basemap info reaches the
    // console log trail, which makes boot debugging ("which city? which
    // basemap?") identical to before without painting anything.
    status(
      `${city.label} · ${city.verdicts.length} verdicts · ${sourceLabel}`
    );
    // Each install is wrapped in its own try/catch. The loading screen is
    // removed at the bottom of this handler — letting one install throw
    // would silently leave the user staring at a spinner forever (the
    // exact bug we hit during the 4-city expansion). Per-call try/catch
    // means one bad city-specific code path can fail loudly in the
    // console while every other panel still mounts.
    try { installCityToggle(city.id); }
    catch (err) { console.warn("[HYPERLOCAL] installCityToggle failed:", err); }
    // Bind the budget stores' storage to the active city. The old
    // budget-band rail install used to do this as a side effect; the rail
    // is gone (docs/ONE_STORY.md) but the binding must still happen before
    // anything reads budget state.
    try { setBudgetCity(city.id as CityCode); }
    catch (err) { console.warn("[HYPERLOCAL] setBudgetCity failed:", err); }
    // The ONE second act: the Decide surface (page + dock chip) renders
    // every buyer-flavoured control directly from the stores, and the
    // status chip keeps any restored Decide state visible — the map is
    // never silently filtered.
    try { installDecideSurface(city.id); }
    catch (err) { console.warn("[HYPERLOCAL] installDecideSurface failed:", err); }
    try { installDecideChip(city.id); }
    catch (err) { console.warn("[HYPERLOCAL] installDecideChip failed:", err); }
    // Compare pill + school-chart pill mount INTO the Decide page slots
    // (their install fns poll for [data-slot=compare] / [data-slot=school],
    // which now exist — the surface installed just above).
    try { installComparePill(); }
    catch (err) { console.warn("[HYPERLOCAL] installComparePill failed:", err); }
    try { installSchoolChartPill(city.id, city.label, city.verdicts, map); }
    catch (err) { console.warn("[HYPERLOCAL] installSchoolChartPill failed:", err); }
    try { installSuburbSearch(map, city.verdicts); }
    catch (err) { console.warn("[HYPERLOCAL] installSuburbSearch failed:", err); }
    // (MapLibre attribution control disabled at construction — no
    //  post-load collapse needed. See `attributionControl: false` above.)
    installAutoFraming(map, city.verdicts, city.center, city.zoom);

    // SEO deep-link: if /app/?focus=<suburb-id> is in the URL, fly to the
    // suburb and open its verdict card. Used by every "Open Balwyn on the
    // map →" CTA on the static SEO pages (175 suburb pages × 2-4 CTAs each).
    // Without this, those links would open the city map but not focus the
    // suburb — a gimmick we explicitly fixed in the answer-page rollout.
    try {
      const focusVerdict = readFocusParam(city.verdicts);
      if (focusVerdict) {
        // Wait one rAF tick so the auto-framing fit-bounds doesn't override
        // our zoom-in. Without this, fitBounds (called inside
        // installAutoFraming when the shortlist seeds) wins the camera fight.
        requestAnimationFrame(() => {
          flyToSuburb(map, focusVerdict.coord);
          showVerdict(focusVerdict);
        });
      }
    } catch (err) {
      console.warn("[HYPERLOCAL] focus deep-link failed:", err);
    }

    // Suburb-boundary ring is INTENTIONALLY not bridged to the verdict
    // popup — having two "selected" indicators (ring + popup) conflicted
    // visually. The popup is the single source of truth for selection.
    // Auto-shortlist panel — left-docked "Top Picks for You" list. Reads
    // lens × priority × budget and ranks the dataset to top 3–5. Wired to
    // the map: when the panel updates, non-shortlisted pins fade so the
    // picks pop. Clicking a card flies the map and opens the verdict card.
    try {
      installShortlistPanel({
        dataset: city.verdicts,
        city: city.id,
        onPick: (v) => {
          // Close any full-screen overlay that is covering the map
          // (mobile picks page, mobile filter page, school chart modal).
          // Without this the user taps a pick, the map flies behind a
          // covering page, and the verdict card opens UNDER that page —
          // so the "solo suburb view" they asked for never reads.
          closeMobileOverlays();
          // Let layout settle (page-removal triggers a reflow) before
          // moving the camera — ensures flyTo's padding calc uses the
          // now-visible viewport, not the still-covered one.
          requestAnimationFrame(() => {
            flyToSuburb(map, v.coord);
          });
        },
      });
    } catch (err) {
      console.warn("[HYPERLOCAL] shortlist panel failed:", err);
    }
    // installMapControls — Apple-Maps-style + / − / compass. Stays in the
    // critical phase: zoom buttons must exist the moment the map is
    // interactive, and the chrome shell's bottom bar adopts this element.
    try {
      installMapControls(map);
      console.log("✓ map controls (zoom + compass) installed");
    } catch (err) {
      console.warn("[HYPERLOCAL] map controls failed:", err);
    }

    // ----- Deferred phase --------------------------------------------------
    // Everything below this point is decoration or secondary panels on an
    // already-usable map: the user can pan, tap pins, read verdicts, search
    // and filter without any of it. Each module is dynamically imported so
    // none of this code is even downloaded until the map has painted, and
    // each install keeps its own try/catch so one bad module can't take the
    // rest down (the same isolation contract the synchronous version had).
    const installDeferredPhase = async () => {
      // Fetch all chunks in parallel; a failed chunk resolves to null and
      // only its own feature is skipped.
      const load = <T,>(p: Promise<T>, name: string): Promise<T | null> =>
        p.catch((err) => {
          console.warn(`[HYPERLOCAL] deferred module ${name} failed to load:`, err);
          return null;
        });
      const [
        waterAnimationMod,
        cityVibeMod,
        billboardsMod,
        incidentCardsMod,
        intelPanelMod,
        factCoherenceMod,
        nearMePanelMod,
        incidentBubblesMod,
        geolocateMod,
        landmarksMod,
        eventsLayerMod,
        simOverlayMod,
        schoolsLayerMod,
        parksEmphasisMod,
        layersPanelMod,
      ] = await Promise.all([
        load(import("./map/water-animation"), "water-animation"),
        load(import("./ui/city-vibe"), "city-vibe"),
        load(import("./map/landmark-billboards"), "landmark-billboards"),
        load(import("./ui/incident-cards"), "incident-cards"),
        load(import("./ui/suburb-intel-panel"), "suburb-intel-panel"),
        load(import("./ui/fact-coherence"), "fact-coherence"),
        load(import("./ui/near-me-panel"), "near-me-panel"),
        load(import("./ui/incident-bubbles"), "incident-bubbles"),
        load(import("./ui/geolocate"), "geolocate"),
        load(import("./ui/landmarks"), "landmarks"),
        load(import("./ui/events-layer"), "events-layer"),
        load(import("./ui/sim-overlay"), "sim-overlay"),
        load(import("./ui/schools-layer"), "schools-layer"),
        load(import("./ui/parks-emphasis"), "parks-emphasis"),
        load(import("./ui/layers-panel"), "layers-panel"),
      ]);

      // SimCity flowing rivers + lake shimmer — attaches AFTER the static
      // refinement stack (phase 1). Self-disables on raster fallback and
      // prefers-reduced-motion.
      if (waterAnimationMod) {
        try {
          waterAnimationMod.attachWaterAnimation(map);
          console.log("✓ water animation attached (SimCity flow + lake shimmer)");
        } catch (err) {
          console.warn("[HYPERLOCAL] water animation attach failed:", err);
        }
      }
      // City vibe — landmark pulse + embers. Needs the building-3d-landmark
      // layer attachLandmarkBuildings created in phase 1.
      if (cityVibeMod) {
        try {
          cityVibeMod.installCityVibe(map);
          console.log("✓ city vibe attached (landmark resting state — map motion stays banned)");
        } catch (err) {
          console.warn("[HYPERLOCAL] city vibe attach failed:", err);
        }
      }
      // (sim-basemap now applies inside the map.load handler — one composed
      //  paint, no mid-read recolour. See simBasemapEarly above.)
      //
      // Building textures (~1.8MB of fill-extrusion patterns) are only
      // visible once the camera tilts into 3D past z14 — fetching them at
      // boot taxed exactly the mobile connections the 30-second promise is
      // for. Load on the first z≥14 crossing instead; zero visible change
      // in the default 2D decision view.
      {
        let texturesRequested = false;
        const maybeLoadTextures = () => {
          if (texturesRequested || map.getZoom() < 14) return;
          texturesRequested = true;
          map.off("zoomend", maybeLoadTextures);
          void import("./map/sim-buildings-textured")
            .then((mod) => mod.attachTexturedBuildings(map))
            .catch((err) => {
              console.warn("[HYPERLOCAL] textured buildings attach failed:", err);
            });
        };
        map.on("zoomend", maybeLoadTextures);
        maybeLoadTextures(); // deep links can land already zoomed in
      }
      // Traffic streaks REMOVED (2026-06-11): 14 frozen layers of
      // headlight-coloured pips read as a live traffic feed, but no traffic
      // API is consulted — a visual claim the data can't back. Road
      // hierarchy is already expressed by sim-basemap's road palette.
      if (billboardsMod) {
        void billboardsMod.attachLandmarkBillboards(map, city.id).catch((err) => {
          console.warn("[HYPERLOCAL] landmark billboards attach failed:", err);
        });
      }
      // Floating category cards — opens on radar marker tap.
      if (incidentCardsMod) {
        try {
          incidentCardsMod.installIncidentCards(map);
        } catch (err) {
          console.warn("[HYPERLOCAL] incident cards install failed:", err);
        }
      }
      // Suburb intelligence panel (Phase 3.5) — listens for
      // hyperlocal:verdict-shown / hyperlocal:incident-tap events.
      if (intelPanelMod) {
        try {
          void intelPanelMod.installSuburbIntelPanel(map, {
            cityId: city.id as "hyd" | "blr" | "mel" | "syd",
            cityLabel: city.label,
          });
        } catch (err) {
          console.warn("[HYPERLOCAL] suburb-intel panel failed:", err);
        }
      }
      // Fact-coherence guardrail — warns (never throws) when a suburb's
      // tags/reasons disagree with themselves.
      if (factCoherenceMod) {
        factCoherenceMod.runFactCoherenceAudit(city.label, city.verdicts);
      }

      // News bubbles + "News near you" panel + GPS. Same wiring contract
      // as before: bubbles resolve first, then geolocate gets the layer
      // handle (nullable on fetch failure — GPS is useful on its own).
      let cachedFix: [number, number] | null = null;
      try {
        const raw = sessionStorage.getItem("hyperlocal.geo.lastFix");
        if (raw) {
          const o = JSON.parse(raw);
          if (o && typeof o.lng === "number" && typeof o.lat === "number") {
            cachedFix = [o.lng, o.lat];
          }
        }
      } catch {
        /* sessionStorage disabled — panel will populate on next GPS event */
      }
      let nearMePanel: ReturnType<typeof installNearMePanel> | null = null;
      if (nearMePanelMod) {
        try {
          nearMePanel = nearMePanelMod.installNearMePanel({
            city: city.id,
            initialUserPosition: cachedFix,
          });
        } catch (err) {
          console.warn("[HYPERLOCAL] near-me panel failed:", err);
          nearMePanel = null;
        }
      }
      const wireGeolocate = (
        layer: Awaited<ReturnType<typeof installIncidentBubbles>> | null
      ) => {
        if (!geolocateMod) return;
        try {
          geolocateMod.installGeolocate({
            city: city.id,
            map,
            incidentLayer: layer,
            nearMePanel,
          });
        } catch (err) {
          console.warn("[HYPERLOCAL] geolocate failed:", err);
        }
      };
      if (incidentBubblesMod) {
        incidentBubblesMod
          .installIncidentBubbles({ city: city.id, map })
          .then((layer) => wireGeolocate(layer))
          .catch((err) => {
            console.warn("[HYPERLOCAL] incident bubbles failed:", err);
            wireGeolocate(null);
          });
      } else {
        wireGeolocate(null);
      }

      // 3D landmark layer — Zenly-style isometric SVG buildings.
      if (landmarksMod) {
        try {
          landmarksMod.installLandmarks({ city: city.id, map });
        } catch (err) {
          console.warn("[HYPERLOCAL] landmarks failed:", err);
        }
      }
      // Upcoming-events overlay — calendar markers + "View all events" pill.
      if (eventsLayerMod) {
        eventsLayerMod.installEventsLayer({ city: city.id, map }).catch((err) => {
          console.warn("[HYPERLOCAL] events layer failed:", err);
        });
      }
      // (Community pins UI retired — the /api/community-pins backend stays
      //  for the account page's "my pins" read; rebuild the layer from the
      //  API if the feature ever earns its map real estate back.)

      // Sim overlay — auto time-of-day filter. Pure ambience.
      if (simOverlayMod) {
        try {
          simOverlayMod.installSimOverlay(map, city.id);
          console.log("✓ sim overlay (auto time-of-day + traffic flow) installed for", city.id);
        } catch (err) {
          console.warn("[HYPERLOCAL] sim overlay failed:", err);
        }
      }
      // (Buyer evaluator deleted 2026-06-12 — its click feature was disabled
      //  2026-04-27 by user request and the module spent fourteen months as
      //  dead weight in the deferred phase.)
      // Optional layer toggles + legend. Schools data is HYD/MEL only.
      // TODO_TRANSIT (unchanged): no transit layer until real line geometry
      // lands in src/data/transit-{city}.geojson — never invent geometry.
      let schoolsLayer: ReturnType<typeof installSchoolsLayer> | null = null;
      if (schoolsLayerMod && (city.id === "hyd" || city.id === "mel")) {
        try {
          schoolsLayer = schoolsLayerMod.installSchoolsLayer({
            city: city.id,
            map,
            verdicts: city.verdicts,
          });
          console.log("✓ schools layer installed (off by default,", schoolsLayer.count, "markers)");
        } catch (err) {
          console.warn("[HYPERLOCAL] schools layer failed:", err);
        }
      } else if (city.id !== "hyd" && city.id !== "mel") {
        console.log("[HYPERLOCAL] schools layer skipped for", city.id, "— no school data yet");
      }
      if (parksEmphasisMod) {
        try {
          parksEmphasisMod.installParksEmphasis({ map });
          console.log("✓ parks emphasis installed (off by default)");
        } catch (err) {
          console.warn("[HYPERLOCAL] parks emphasis failed:", err);
        }
      }
      if (layersPanelMod) {
        try {
          layersPanelMod.installLayersPanel();
          console.log("✓ layers panel (toggles + legend) installed");
        } catch (err) {
          console.warn("[HYPERLOCAL] layers panel failed:", err);
        }
      }
      void schoolsLayer; // dispose handle reserved for a future city-switch hook
      console.log("✓ deferred phase complete");
    };

    // Schedule the deferred phase for browser idle time (with a timeout
    // floor so it still runs promptly under continuous interaction).
    type IdleCapableWindow = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const w = window as IdleCapableWindow;
    const scheduleDeferred = () => {
      void installDeferredPhase();
    };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(scheduleDeferred, { timeout: 2000 });
    } else {
      window.setTimeout(scheduleDeferred, 300);
    }

    console.log("HYPERLOCAL_READY");
    console.groupEnd();
  });

  // Non-fatal: tile 404s / transient CDN blips. Don't spam.
  //
  // MapLibre has a known quirk where its style validator re-fires per
  // setData() for layers that reference `text-field`, complaining that the
  // style has no `glyphs` property even when one is clearly set (verified
  // via `map.getStyle().glyphs`). Text renders correctly — this is purely
  // a validator false-positive. Filter it so the console stays clean.
  map.on("error", (e) => {
    const msg = (e.error && e.error.message) || String(e);
    if (msg.includes("tile") || msg.includes("404")) return;
    if (msg.includes('"text-field" requires a style "glyphs"')) return;
    console.warn("[HYPERLOCAL] map warning:", msg);
  });

  // Expose for manual debugging in devtools.
  (window as any).HYPERLOCAL = {
    map,
    city: city.id,
    cityLabel: city.label,
    cityCenter: city.center,
    verdicts: city.verdicts,
    version: "3.2-citysim-buyer",
    basemap: sourceLabel,
    // Snapshot of which 2026-04-26 feature modules installed cleanly.
    // Read by the user-testing harness to verify the new chrome shipped
    // alongside the existing verdict / landmark / incident layers.
    features: {
      mapControls: !!document.getElementById("hyperlocal-map-controls"),
      simOverlay: !!document.getElementById("hyperlocal-sim-overlay"),
    },
  };
}

// ---- City toggle pill (top-right) -----------------------------------------

function installCityToggle(active: CityId) {
  if (document.getElementById("hyperlocal-city-toggle")) return;

  const wrap = document.createElement("div");
  wrap.id = "hyperlocal-city-toggle";
  wrap.style.cssText = `position:fixed;top:16px;right:16px;display:flex;gap:4px;
    background:rgba(33,33,33,0.75);backdrop-filter:blur(10px);
    padding:4px;border-radius:999px;z-index:1200;
    border:1px solid rgba(255,255,255,0.08);
    font:12px system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;`;

  const make = (id: CityId, label: string) => {
    const b = document.createElement("button");
    b.textContent = label;
    const isActive = id === active;
    b.style.cssText = `
      background: ${isActive ? "#FFD37E" : "transparent"};
      color: ${isActive ? "#1a1a1a" : "#f1f1f1"};
      border: 0;
      padding: 6px 12px;
      border-radius: 999px;
      cursor: ${isActive ? "default" : "pointer"};
      font: inherit;
      font-weight: ${isActive ? "600" : "500"};
      letter-spacing: 0.02em;
      transition: background 140ms ease, color 140ms ease;
      display: inline-flex; align-items: center; gap: 5px;
    `;
    // Sydney is the live truth-engine city — a quiet dot says so. This is
    // a truthful hierarchy cue (only Sydney's feed is engine-verified
    // today), never decoration; extend to other cities as their engine
    // sources light up (Mel → Hyd → Blr, locked 2026-06-07).
    if (id === "syd") {
      const dot = document.createElement("span");
      dot.setAttribute("aria-hidden", "true");
      dot.style.cssText = `width:5px;height:5px;border-radius:999px;flex:0 0 auto;
        background:${isActive ? "#0e7a55" : "#1d9e75"};`;
      b.appendChild(dot);
      b.title = "Sydney — live truth-engine signals";
      b.setAttribute("aria-label", `${label} — live truth-engine signals`);
    }
    if (!isActive) {
      b.addEventListener("click", () => {
        const url = new URL(window.location.href);
        url.searchParams.set("city", id);
        window.location.href = url.toString();
      });
      b.addEventListener("mouseenter", () => {
        b.style.background = "rgba(255,211,126,0.15)";
      });
      b.addEventListener("mouseleave", () => {
        b.style.background = "transparent";
      });
    }
    return b;
  };

  // Order matches the landing-page door order: India cities (HYD, BLR)
  // then Australia cities (MEL, SYD). Mobile uses 2-letter labels because
  // 4 full city names overflow the toggle pill on phone widths.
  const isCompact =
    typeof window !== "undefined" &&
    (window.innerWidth <= 720 || window.innerHeight <= 500);
  if (isCompact) {
    wrap.appendChild(make("hyd", "HYD"));
    wrap.appendChild(make("blr", "BLR"));
    wrap.appendChild(make("mel", "MEL"));
    wrap.appendChild(make("syd", "SYD"));
  } else {
    wrap.appendChild(make("hyd", "Hyderabad"));
    wrap.appendChild(make("blr", "Bengaluru"));
    wrap.appendChild(make("mel", "Melbourne"));
    wrap.appendChild(make("syd", "Sydney"));
  }
  document.body.appendChild(wrap);
}

boot().catch((err) => {
  console.error("[HYPERLOCAL] boot failed:", err);
  status("Boot failed: " + (err?.message || String(err)));
  console.groupEnd();
});
